import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { ProgressSpinner } from "primereact/progressspinner";
import { RadioButton } from "primereact/radiobutton";
import { Tag } from "primereact/tag";
import { Tooltip } from "primereact/tooltip";
import { Toast } from "primereact/toast";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ActionMenuButton from "../../../components/general/ActionMenuButton";
import { getErrorTitle } from "../../../constants/constants";
import { ROLE_TYPE_CAREGIVER, ROLE_TYPE_MHP, ROLE_TYPE_NURSE, ROLE_TYPE_PROVIDER } from "../../../constants/RegisterConstants";
import { RouterAddress } from "../../../constants/RouterAddress";
import { useResponsive } from "../../../responsive/ResponsiveProvider";
import { BinderShare } from "../../../models/binder";
import { ResidentProfile } from "../../../models/residentProfile";
import { renderSurfaceHeader } from "../../../components/general/uiSurfaceStyles";
import Unauthorized from "../../../shared/pages/Unauthorized";
import { openUrlInNewTab } from "../../../utils/securityUtils";
import { useAuthExperience } from "../../auth/hooks/useAuthExperience";
import { BRAND_KEY_ONE_HUB_FOR_CARE } from "../../../constants/brandConstants";
import AddCaregiverModal from "../components/AddCaregiverModal";
import { STAFF_ACCESS_MATRIX_ROWS, StaffAccessMatrixAction } from "../components/StaffAccessMatrix";
import HiringRequestDialog from "../components/HiringRequestDialog";
import StaffDelegationRequirementsForm from "../components/StaffDelegationRequirementsForm";
import StaffCredentialDocumentUpload from "../components/StaffCredentialDocumentUpload";
import { createEmptyStaffDelegationRequirements, getStaffDelegationRequirementsValidationMessage, normalizeStaffDelegationRequirements, StaffDelegationRequirements } from "../../../constants/staffDelegationRequirements";

const STAFF_ROLE_FILTERS = [
  { value: ROLE_TYPE_PROVIDER, label: "Facility Admin" },
  { value: ROLE_TYPE_CAREGIVER, label: "Caregiver" },
  { value: ROLE_TYPE_NURSE, label: "Registered Nurse" },
  { value: ROLE_TYPE_MHP, label: "Mental Health Professional" },
];

const SHIFT_DAY_OPTIONS = [
  { key: "mon", label: "Mon" },
  { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" },
  { key: "sat", label: "Sat" },
  { key: "sun", label: "Sun" },
];

const ALL_TIME_SHIFT_START = "00:00";
const ALL_TIME_SHIFT_END = "23:59";

const DSHS_CAREGIVER_CERTIFICATION_OPTIONS = [
  "Washington State Background Check",
  "Federal Fingerprint Background Check",
  "LTCW Basic Training / 70-Hour Training",
  "Home Care Aide (HCA) Certification",
  "CPR Certification",
  "First Aid Certification",
  "LTCW Orientation and Safety Training",
  "Facility Orientation and Job Skills",
  "Annual Continuing Education",
  "Dementia Specialty Training (if applicable)",
  "Mental Health Specialty Training (if applicable)",
  "Developmental Disabilities Specialty Training (if applicable)",
  "Nurse Delegation Training (if applicable)",
].map((name) => ({ label: name, value: name }));

const PROFESSIONAL_CERTIFICATION_OPTIONS: Record<string, Array<{ label: string; value: string }>> = {
  caregiver: DSHS_CAREGIVER_CERTIFICATION_OPTIONS,
  nurse: [
    "Washington RN/LPN License",
    "Nursing License Verification",
    "CPR Certification",
    "First Aid Certification",
    "Background Check",
    "Clinical Education and Training",
    "Facility Orientation",
    "Nurse Delegation Training (if applicable)",
    "Continuing Nursing Education",
  ].map((name) => ({ label: name, value: name })),
  mhp: [
    "Washington Professional License or Credential",
    "Credential Verification",
    "CPR Certification",
    "First Aid Certification",
    "Background Check",
    "Behavioral Health Education and Training",
    "Facility Orientation",
    "Crisis and De-escalation Training (if applicable)",
    "Continuing Education",
  ].map((name) => ({ label: name, value: name })),
};

const RECOMMENDED_CAREGIVER_QUESTIONS = [
  "What date are you available to start?",
  "What caregiver certifications and training do you currently hold?",
  "Are you authorized to work in the United States?",
  "What days and shifts are you available to work?",
  "Do you have reliable transportation to the assigned facility or resident location?",
  "Have you provided caregiving or direct-support services before? Please describe briefly.",
  "Are you comfortable assisting with activities of daily living, including bathing, dressing, toileting, and transfers?",
  "Are you comfortable documenting care, completing shift notes, and reporting changes in condition?",
  "Is there anything about your availability, experience, or qualifications the provider should know?",
].join("\n");

const RECOMMENDED_ROLE_QUESTIONS: Record<string, string> = {
  caregiver: RECOMMENDED_CAREGIVER_QUESTIONS,
  nurse: [
    "What date are you available to start, and what days or shifts can you cover?",
    "What is your current Washington nursing license type and expiration date?",
    "What residential-care, AFH, ALF, or home-care experience do you have?",
    "Which clinical tasks and resident populations are you most comfortable supporting?",
    "Describe how you assess and escalate a change in a resident’s condition.",
    "What experience do you have with medication administration, MAR review, or nurse delegation?",
    "How do you document clinical findings, orders, and communication with the care team?",
    "Describe a time you handled an urgent or unexpected clinical situation.",
    "Is there anything about your availability or qualifications the provider should know?",
  ].join("\n"),
  mhp: [
    "What date are you available to start, and what days or shifts can you cover?",
    "What is your current professional license or credential and expiration date?",
    "What experience do you have supporting residents with behavioral-health needs?",
    "How do you respond when a resident becomes distressed, escalated, or refuses support?",
    "Describe your experience with crisis prevention and de-escalation.",
    "How do you document behavioral observations, interventions, and outcomes?",
    "How do you collaborate with caregivers, nurses, families, and providers?",
    "How do you protect resident dignity, privacy, and confidentiality?",
    "Is there anything about your availability or qualifications the provider should know?",
  ].join("\n"),
};

const ATTENDANCE_CORRECTION_REASON_OPTIONS = [
  { label: "Forgot to clock out", value: "forgot_clock_out" },
  { label: "Forgot to clock in", value: "forgot_clock_in" },
  { label: "Device issue", value: "device_issue" },
  { label: "Paper log reconciliation", value: "paper_log_reconciliation" },
  { label: "Schedule change", value: "schedule_change" },
  { label: "Manual payroll adjustment", value: "manual_payroll_adjustment" },
  { label: "Other", value: "other" },
];

const LOS_ANGELES_MONTH_VALUE_FORMATTER = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Los_Angeles",
  year: "numeric",
  month: "2-digit",
});

const LOS_ANGELES_MONTH_LABEL_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Los_Angeles",
  month: "long",
  year: "numeric",
});

const buildLosAngelesReportMonthOptions = () => {
  const now = new Date();
  const currentYear = Number(new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
  }).format(now));
  const currentMonthIndex = Number(new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    month: "2-digit",
  }).format(now)) - 1;

  return Array.from({ length: 12 }, (_, index) => {
    const targetDate = new Date(Date.UTC(currentYear, currentMonthIndex - index, 15, 12, 0, 0));
    return {
      value: LOS_ANGELES_MONTH_VALUE_FORMATTER.format(targetDate),
      label: LOS_ANGELES_MONTH_LABEL_FORMATTER.format(targetDate),
    };
  });
};

const getAttendanceReviewStatusLabel = (status?: string) => (
  String(status || "").trim().toLowerCase() === "resolved" ? "Resolved" : "Open"
);

const getAttendanceReviewStatusSeverity = (status?: string) => (
  String(status || "").trim().toLowerCase() === "resolved" ? "success" : "warning"
);

const HALF_HOUR_TIME_OPTIONS = Array.from({ length: 48 }, (_, index) => {
  const hour = Math.floor(index / 2);
  const minutes = index % 2 === 0 ? "00" : "30";
  const value = `${String(hour).padStart(2, "0")}:${minutes}`;
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  const meridiem = hour < 12 ? "AM" : "PM";
  return {
    value,
    label: `${hour12}:${minutes} ${meridiem}`,
  };
});

const getDatePartFromLocalDateTime = (value?: string) => String(value || "").trim().split("T")[0] || "";

const getTimePartFromLocalDateTime = (value?: string) => {
  const timePart = String(value || "").trim().split("T")[1] || "";
  return timePart.slice(0, 5);
};

const combineLocalDateTimeParts = (datePart?: string, timePart?: string) => {
  const normalizedDate = String(datePart || "").trim();
  const normalizedTime = String(timePart || "").trim();
  return normalizedDate && normalizedTime ? `${normalizedDate}T${normalizedTime}` : "";
};

const createDailyShiftSchedule = () => SHIFT_DAY_OPTIONS.map((option) => ({
  day: option.key,
  label: option.label,
  enabled: false,
  startTime: "08:00",
  endTime: "16:00",
}));

const buildShiftWindowsFromSchedule = (schedule: Array<{ day: string; enabled: boolean; startTime: string; endTime: string }>) => (
  schedule
    .filter((item) => item.enabled)
    .map((item) => ({
      day: item.day,
      startTime: item.startTime,
      endTime: item.endTime,
    }))
);

const buildAllTimeShiftWindows = () => (
  SHIFT_DAY_OPTIONS.map((option) => ({
    day: option.key,
    startTime: ALL_TIME_SHIFT_START,
    endTime: ALL_TIME_SHIFT_END,
  }))
);

const isStaffLimitReachedError = (error: any) => (
  error?.code === "SUBSCRIPTION_LIMIT_REACHED"
  || error?.cause?.message === "SUBSCRIPTION_LIMIT_REACHED"
  || String(error?.message || error?.error || "")
    .trim()
    .toLowerCase()
    .includes("maximum number of staffs you can add")
);

const applyShiftPresetToSchedule = (
  schedule: Array<{ day: string; label: string; enabled: boolean; startTime: string; endTime: string }>,
  startTime: string,
  endTime: string,
) => {
  const hasEnabledDays = schedule.some((item) => item.enabled);
  return schedule.map((item) => ({
    ...item,
    enabled: hasEnabledDays ? item.enabled : true,
    startTime: (hasEnabledDays && !item.enabled) ? item.startTime : startTime,
    endTime: (hasEnabledDays && !item.enabled) ? item.endTime : endTime,
  }));
};

const applyShiftPresetForDays = (
  schedule: Array<{ day: string; label: string; enabled: boolean; startTime: string; endTime: string }>,
  dayKeys: string[],
  startTime: string,
  endTime: string,
) => (
  schedule.map((item) => ({
    ...item,
    enabled: dayKeys.includes(item.day),
    startTime,
    endTime,
  }))
);

const clearShiftSchedule = (
  schedule: Array<{ day: string; label: string; enabled: boolean; startTime: string; endTime: string }>,
) => (
  schedule.map((item) => ({
    ...item,
    enabled: false,
  }))
);

const formatRoleLabel = (roleType: string) => {
  if (roleType === ROLE_TYPE_PROVIDER) {
    return "Facility Admin";
  }
  if (roleType === ROLE_TYPE_CAREGIVER) {
    return "Caregiver";
  }
  if (roleType === ROLE_TYPE_NURSE) {
    return "Registered Nurse";
  }
  if (roleType === ROLE_TYPE_MHP) {
    return "Mental Health Professional";
  }
  return String(roleType || "").replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatShiftSummary = (staff: any) => {
  const shiftWindows = Array.isArray(staff?.shiftWindows) ? staff.shiftWindows : [];
  if (shiftWindows.length) {
    const groupedWindows = shiftWindows.reduce((acc: Record<string, string[]>, window: any) => {
      const key = `${String(window?.startTime || "").trim()}-${String(window?.endTime || "").trim()}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(String(window?.day || "").trim().toLowerCase());
      return acc;
    }, {});
    return Object.entries(groupedWindows)
      .map(([timeRange, days]) => {
        const normalizedDays = Array.isArray(days) ? days : [];
        const orderedLabels = SHIFT_DAY_OPTIONS
          .filter((option) => normalizedDays.includes(option.key))
          .map((option) => option.label);
        return `${orderedLabels.join(", ")} · ${String(timeRange || "").replace("-", " - ")}`;
      })
      .join(" | ");
  }
  const shiftEnabled = Boolean(staff?.shiftEnabled);
  const shiftDays = Array.isArray(staff?.shiftDays) ? staff.shiftDays : [];
  const shiftStartTime = String(staff?.shiftStartTime || "").trim();
  const shiftEndTime = String(staff?.shiftEndTime || "").trim();
  if (!shiftEnabled || !shiftDays.length || !shiftStartTime || !shiftEndTime) {
    return "No shift set";
  }
  const orderedLabels = SHIFT_DAY_OPTIONS
    .filter((option) => shiftDays.includes(option.key))
    .map((option) => option.label);
  return `${orderedLabels.join(", ")} · ${shiftStartTime} - ${shiftEndTime}`;
};

const getPermissionSummary = (sharedResidents: any[]) => {
  const residentCount = Array.isArray(sharedResidents) ? sharedResidents.length : 0;
  return `${residentCount} operational resident${residentCount === 1 ? "" : "s"}`;
};

const CLINICAL_SHARE_ROLE_TYPES = [ROLE_TYPE_NURSE, ROLE_TYPE_MHP];

const getClinicalPermissionSummary = (sharedResidents: any[]) => {
  const totals = {
    binder: 0,
    assessment: 0,
    carePlan: 0,
    delegation: 0,
  };
  sharedResidents.forEach((item: any) => {
    if (item?.binderAccessEnabled === true) totals.binder += 1;
    if (item?.assessmentAccessEnabled !== false) totals.assessment += 1;
    if (item?.carePlanAccessEnabled !== false) totals.carePlan += 1;
    if (item?.delegationAccessEnabled !== false) totals.delegation += 1;
  });
  return `${totals.assessment} assessment · ${totals.carePlan} care plan · ${totals.delegation} delegation`;
};

const STAFF_ACCESS_PRESET_CUSTOM = "custom";
const STAFF_ACCESS_PRESET_CAREGIVER = "caregiver";
const STAFF_ACCESS_PRESET_NURSE = "nurse";
const STAFF_ACCESS_PRESET_MHP = "mhp";

const normalizeClinicalAccessValue = (
  value: any,
  roleTypes: string[],
  accessType: "assessment" | "carePlan" | "delegation" | "binder",
) => {
  const normalizedRoleTypes = Array.isArray(roleTypes)
    ? roleTypes.map((roleType) => String(roleType || "").trim().toLowerCase()).filter(Boolean)
    : [];
  const hasNurseRole = normalizedRoleTypes.includes(ROLE_TYPE_NURSE);
  const hasMhpRole = normalizedRoleTypes.includes(ROLE_TYPE_MHP);

  if (accessType === "delegation") {
    if (hasNurseRole) {
      return value !== false;
    }
    return value === true;
  }

  if (accessType === "assessment" || accessType === "carePlan") {
    if (hasNurseRole || hasMhpRole) {
      return value !== false;
    }
    return value === true;
  }

  if (accessType === "binder") {
    return value === true;
  }

  return value === true;
};

const detectStaffAccessPreset = (
  residents: ResidentProfile[],
  roleTypes: string[],
  accessState: {
    residentAssessmentAccessById: Record<string, boolean>;
    residentClinicalCarePlanAccessById: Record<string, boolean>;
    residentDelegationAccessById: Record<string, boolean>;
    residentBinderAccessById: Record<string, boolean>;
  },
) => {
  const residentIds = residents
    .map((resident) => String(resident?.residentId || "").trim())
    .filter(Boolean);

  if (!residentIds.length) {
    return STAFF_ACCESS_PRESET_CUSTOM;
  }

  const matchesAll = (predicate: (residentId: string) => boolean) => residentIds.every((residentId) => predicate(residentId));
  const hasCaregiverRole = roleTypes.includes(ROLE_TYPE_CAREGIVER);
  const hasNurseRole = roleTypes.includes(ROLE_TYPE_NURSE);
  const hasMhpRole = roleTypes.includes(ROLE_TYPE_MHP);
  const matchesDailyAccess = true;

  if (
    hasNurseRole
    && matchesDailyAccess
    && matchesAll((residentId) => (
      accessState.residentBinderAccessById[residentId] === true
      && accessState.residentAssessmentAccessById[residentId] === true
      && accessState.residentClinicalCarePlanAccessById[residentId] === true
      && accessState.residentDelegationAccessById[residentId] === true
    ))
  ) {
    return STAFF_ACCESS_PRESET_NURSE;
  }

  if (
    hasMhpRole
    && matchesDailyAccess
    && matchesAll((residentId) => (
      accessState.residentBinderAccessById[residentId] === true
      && accessState.residentAssessmentAccessById[residentId] === true
      && accessState.residentClinicalCarePlanAccessById[residentId] === true
      && accessState.residentDelegationAccessById[residentId] === false
    ))
  ) {
    return STAFF_ACCESS_PRESET_MHP;
  }

  if (
    hasCaregiverRole
    && matchesDailyAccess
    && matchesAll((residentId) => (
      accessState.residentAssessmentAccessById[residentId] === false
      && accessState.residentClinicalCarePlanAccessById[residentId] === false
      && accessState.residentDelegationAccessById[residentId] === false
      && accessState.residentBinderAccessById[residentId] === false
    ))
  ) {
    return STAFF_ACCESS_PRESET_CAREGIVER;
  }

  return STAFF_ACCESS_PRESET_CUSTOM;
};

const buildResidentBooleanMap = (residents: ResidentProfile[], value: boolean) => (
  residents.reduce((acc: Record<string, boolean>, resident) => {
    const residentId = String(resident?.residentId || "").trim();
    if (residentId) {
      acc[residentId] = value;
    }
    return acc;
  }, {})
);

const getResidentShareOverview = (sharedResidents: any[], clinicalSharedResidents: any[]) => {
  const residentIds = new Set<string>();
  sharedResidents.forEach((item: any) => {
    const residentId = String(item?.residentId || "").trim();
    if (residentId) {
      residentIds.add(residentId);
    }
  });
  clinicalSharedResidents.forEach((item: any) => {
    const residentId = String(item?.residentId || "").trim();
    if (residentId) {
      residentIds.add(residentId);
    }
  });

  const caregiverSummary = sharedResidents.length ? getPermissionSummary(sharedResidents) : "";
  const clinicalSummary = clinicalSharedResidents.length ? getClinicalPermissionSummary(clinicalSharedResidents) : "";
  return {
    sharedCount: residentIds.size,
    permissionSummary: [clinicalSummary, caregiverSummary].filter(Boolean).join(" | "),
  };
};

const BINDER_APPLICATION_STATUS_OPTIONS = [
  { label: "All Statuses", value: "all" },
  { label: "Under Review", value: "under_review" },
  { label: "Questions to Answer", value: "questions_pending" },
  { label: "Accepted", value: "approved" },
  { label: "Need Updates", value: "needs_update" },
  { label: "Rejected", value: "rejected" },
];

const getBinderReviewStatusSeverity = (status?: string) => {
  if (status === "approved") {
    return "success";
  }
  if (status === "rejected") {
    return "danger";
  }
  if (status === "needs_update") {
    return "warning";
  }
  return "info";
};

const getBinderReviewStatusLabel = (status?: string) => {
  if (status === "approved") {
    return "Accepted";
  }
  if (status === "needs_update") {
    return "Need Updates";
  }
  if (status === "rejected") {
    return "Rejected";
  }
  return "Under Review";
};

const getLatestSharedBinderHistoryItem = (binderShare?: BinderShare | null) => {
  const history = Array.isArray(binderShare?.history) ? binderShare?.history || [] : [];
  if (!history.length) {
    return null;
  }
  return [...history].sort((left, right) => {
    const leftTime = new Date(String(left?.createdAt || "")).getTime();
    const rightTime = new Date(String(right?.createdAt || "")).getTime();
    return (Number.isNaN(rightTime) ? 0 : rightTime) - (Number.isNaN(leftTime) ? 0 : leftTime);
  })[0];
};

const formatSharedBinderDate = (value: any) => {
  const normalizedValue = String(value || "").trim();
  if (!normalizedValue) {
    return "Unknown";
  }
  const parsedDate = new Date(normalizedValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return "Unknown";
  }
  return parsedDate.toLocaleDateString("en-US", {
    timeZone: "America/Los_Angeles",
  });
};

const HomeStaffManagementPage = () => {
  const { homeId = "" } = useParams();
  const mobile = useResponsive().isMobile;
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const authExperience = useAuthExperience();
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const [loading, setLoading] = useState(true);
  const [savingAdminAccessByCaregiverId, setSavingAdminAccessByCaregiverId] = useState<Record<string, boolean>>({});
  const [removingStaffByCaregiverId, setRemovingStaffByCaregiverId] = useState<Record<string, boolean>>({});
  const [savingBulkShift, setSavingBulkShift] = useState(false);
  const [savingBulkAccess, setSavingBulkAccess] = useState(false);
  const [homeProfile, setHomeProfile] = useState<any | null>(null);
  const [residents, setResidents] = useState<ResidentProfile[]>([]);
  const [linkedStaff, setLinkedStaff] = useState<any[]>([]);
  const [residentShares, setResidentShares] = useState<any[]>([]);
  const [attendanceRange, setAttendanceRange] = useState<"week" | "month">("week");
  const [selectedAttendanceReportMonth, setSelectedAttendanceReportMonth] = useState(
    LOS_ANGELES_MONTH_VALUE_FORMATTER.format(new Date()),
  );
  const [selectedTimePayCaregiverId, setSelectedTimePayCaregiverId] = useState("");
  const [showTimeAttendanceDialog, setShowTimeAttendanceDialog] = useState(false);
  const [attendanceSummaries, setAttendanceSummaries] = useState<any[]>([]);
  const [attendanceOverview, setAttendanceOverview] = useState<any | null>(null);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [attendanceCorrectionDialogRow, setAttendanceCorrectionDialogRow] = useState<any | null>(null);
  const [attendanceCorrectionAction, setAttendanceCorrectionAction] = useState<"clock_out_now" | "adjust_hours" | "edit_shift_times">("clock_out_now");
  const [attendanceCorrectionReason, setAttendanceCorrectionReason] = useState("forgot_clock_out");
  const [attendanceCorrectionNote, setAttendanceCorrectionNote] = useState("");
  const [attendanceCorrectionHours, setAttendanceCorrectionHours] = useState<number | null>(0);
  const [attendanceCorrectionDateInput, setAttendanceCorrectionDateInput] = useState("");
  const [attendanceCorrectionSelectedAttendanceId, setAttendanceCorrectionSelectedAttendanceId] = useState("");
  const [attendanceCorrectionClockInInput, setAttendanceCorrectionClockInInput] = useState("");
  const [attendanceCorrectionClockOutInput, setAttendanceCorrectionClockOutInput] = useState("");
  const [attendanceCorrectionPreview, setAttendanceCorrectionPreview] = useState<any | null>(null);
  const [loadingAttendanceCorrectionPreview, setLoadingAttendanceCorrectionPreview] = useState(false);
  const [savingAttendanceCorrection, setSavingAttendanceCorrection] = useState(false);
  const [savingPayrollCloseout, setSavingPayrollCloseout] = useState(false);
  const [payrollCloseoutDialogAction, setPayrollCloseoutDialogAction] = useState<"finalize_payroll_period" | "reopen_payroll_period" | null>(null);
  const [payrollCloseoutNoteInput, setPayrollCloseoutNoteInput] = useState("");
  const [resendingAttendanceCorrectionById, setResendingAttendanceCorrectionById] = useState<Record<string, boolean>>({});
  const [updatingReviewItemById, setUpdatingReviewItemById] = useState<Record<string, boolean>>({});
  const [reviewWorkflowDialogRow, setReviewWorkflowDialogRow] = useState<any | null>(null);
  const [reviewWorkflowAction, setReviewWorkflowAction] = useState<"resolve_review_item" | "reopen_review_item">("resolve_review_item");
  const [reviewWorkflowNote, setReviewWorkflowNote] = useState("");
  const [exportingPayrollReport, setExportingPayrollReport] = useState(false);
  const [exportingPayrollPacket, setExportingPayrollPacket] = useState(false);
  const [exportingCorrectionReport, setExportingCorrectionReport] = useState(false);
  const [exportingMonthlySummaryByCaregiverId, setExportingMonthlySummaryByCaregiverId] = useState<Record<string, boolean>>({});
  const [searchText, setSearchText] = useState("");
  const [selectedRoleFilters, setSelectedRoleFilters] = useState<string[]>([]);
  const [selectedStaffIds, setSelectedStaffIds] = useState<string[]>([]);
  const [showArchivedStaff, setShowArchivedStaff] = useState(false);
  const [showQuickAddDialog, setShowQuickAddDialog] = useState(false);
  const [showStaffUnlockDialog, setShowStaffUnlockDialog] = useState(false);
  const [showBulkShiftDialog, setShowBulkShiftDialog] = useState(false);
  const [showBulkAccessDialog, setShowBulkAccessDialog] = useState(false);
  const [selectedBulkAccessPreset, setSelectedBulkAccessPreset] = useState(STAFF_ACCESS_PRESET_CUSTOM);
  const [showStaffAccessDialog, setShowStaffAccessDialog] = useState(false);
  const [selectedStaffAccessRow, setSelectedStaffAccessRow] = useState<any | null>(null);
  const [selectedStaffAccessPreset, setSelectedStaffAccessPreset] = useState(STAFF_ACCESS_PRESET_CUSTOM);
  const [showAccessMatrixDialog, setShowAccessMatrixDialog] = useState(false);
  const [savingStaffAccess, setSavingStaffAccess] = useState(false);
  const [removeStaffDialogRow, setRemoveStaffDialogRow] = useState<any | null>(null);
  const [deleteStaffDialogRow, setDeleteStaffDialogRow] = useState<any | null>(null);
  const [credentialDialogRow, setCredentialDialogRow] = useState<any | null>(null);
  const [credentialDialogValue, setCredentialDialogValue] = useState<StaffDelegationRequirements>(createEmptyStaffDelegationRequirements);
  const [savingStaffCredentials, setSavingStaffCredentials] = useState(false);
  const [showCredentialValidationErrors, setShowCredentialValidationErrors] = useState(false);
  const [showHiringRequestDialog, setShowHiringRequestDialog] = useState(false);
  const [showHiringIntakeDialog, setShowHiringIntakeDialog] = useState(false);
  const [compensationDialogRow, setCompensationDialogRow] = useState<any | null>(null);
  const [compensationHistoryDialogRow, setCompensationHistoryDialogRow] = useState<any | null>(null);
  const [savingCompensation, setSavingCompensation] = useState(false);
  const [hourlyRateInput, setHourlyRateInput] = useState<number | null>(0);
  const [vacationBalanceInput, setVacationBalanceInput] = useState<number | null>(0);
  const [leaveBalanceInput, setLeaveBalanceInput] = useState<number | null>(0);
  const [compensationEffectiveDateInput, setCompensationEffectiveDateInput] = useState(new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date()));
  const [compensationNoteInput, setCompensationNoteInput] = useState("");
  const [dailyShiftSchedule, setDailyShiftSchedule] = useState(createDailyShiftSchedule);
  const [bulkEveryDayStartTime, setBulkEveryDayStartTime] = useState("08:00");
  const [bulkEveryDayEndTime, setBulkEveryDayEndTime] = useState("16:00");
  const [allTimeShiftAccessEnabled, setAllTimeShiftAccessEnabled] = useState(false);
  const [mobileAccessEnabled, setMobileAccessEnabled] = useState(true);
  const [selectedResidentIds, setSelectedResidentIds] = useState<string[]>([]);
  const [bulkResidentAssessmentAccessById, setBulkResidentAssessmentAccessById] = useState<Record<string, boolean>>({});
  const [bulkResidentClinicalCarePlanAccessById, setBulkResidentClinicalCarePlanAccessById] = useState<Record<string, boolean>>({});
  const [bulkResidentDelegationAccessById, setBulkResidentDelegationAccessById] = useState<Record<string, boolean>>({});
  const [bulkResidentBinderAccessById, setBulkResidentBinderAccessById] = useState<Record<string, boolean>>({});
  const [residentClinicalCarePlanAccessById, setResidentClinicalCarePlanAccessById] = useState<Record<string, boolean>>({});
  const [residentAssessmentAccessById, setResidentAssessmentAccessById] = useState<Record<string, boolean>>({});
  const [residentDelegationAccessById, setResidentDelegationAccessById] = useState<Record<string, boolean>>({});
  const [residentBinderAccessById, setResidentBinderAccessById] = useState<Record<string, boolean>>({});
  const [residentRoleLinks, setResidentRoleLinks] = useState<any[]>([]);
  const [sharedBinderRows, setSharedBinderRows] = useState<BinderShare[]>([]);
  const [loadingSharedBinders, setLoadingSharedBinders] = useState(false);
  const [sharedBinderSearchText, setSharedBinderSearchText] = useState("");
  const [selectedSharedBinderStatus, setSelectedSharedBinderStatus] = useState("all");
  const [showSharedBinderHistoryDialog, setShowSharedBinderHistoryDialog] = useState(false);
  const [selectedSharedBinderHistoryRow, setSelectedSharedBinderHistoryRow] = useState<BinderShare | null>(null);
  const [downloadingBinderPacketByShareId, setDownloadingBinderPacketByShareId] = useState<Record<string, boolean>>({});

  const userRoleType = authStore.getUserRoleType();
  const isAdminUser = authStore.isAdmin();
  const staffResidentTooltip = useRef<Tooltip>(null);
  const facilitySingularLabel = authExperience.brandKey === BRAND_KEY_ONE_HUB_FOR_CARE ? "Facility" : "Home";
  const facilityLowerLabel = facilitySingularLabel.toLowerCase();
  const availableStaffRoleFilters = useMemo(() => STAFF_ROLE_FILTERS, []);
  const attendanceReportMonthOptions = useMemo(() => buildLosAngelesReportMonthOptions(), []);

  const loadPageData = async () => {
    if (!homeId) {
      return;
    }
    try {
      setLoading(true);
      await backendAPIStore.init();
      const [homeDetails, homeResidents, homeLinkedStaff, homeResidentShares, approvedResidentRoleLinks] = await Promise.all([
        backendAPIStore.getBusinessHomeDetails(homeId),
        backendAPIStore.getResidentsForHome(homeId),
        backendAPIStore.getLinkedCaregiversForHome(homeId),
        backendAPIStore.getCaregiverResidentSharesForHome(homeId),
        backendAPIStore.getResidentRoleLinks({ status: "approved" }),
      ]);
      setHomeProfile(homeDetails || null);
      setResidents(Array.isArray(homeResidents) ? homeResidents : []);
      setLinkedStaff(Array.isArray(homeLinkedStaff) ? homeLinkedStaff : []);
      setResidentShares(Array.isArray(homeResidentShares) ? homeResidentShares.filter((item: any) => item?.active !== false) : []);
      setResidentRoleLinks(Array.isArray(approvedResidentRoleLinks)
        ? approvedResidentRoleLinks.filter((item: any) =>
          CLINICAL_SHARE_ROLE_TYPES.includes(String(item?.roleType || "").trim().toLowerCase())
          && String(item?.status || "").trim().toLowerCase() === "approved")
        : []);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || `Unable to load staff for this ${facilityLowerLabel}.`,
        life: 3500,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadPageData();
  }, [homeId]);

  const refreshAttendanceSection = async (range: "week" | "month" = attendanceRange, reportMonth: string = selectedAttendanceReportMonth) => {
    if (!homeId) {
      setAttendanceSummaries([]);
      setAttendanceOverview(null);
      return;
    }
    setLoadingAttendance(true);
    try {
      await backendAPIStore.init();
      const response = await backendAPIStore.getCaregiverAttendance({
        homeId,
        range,
        reportMonth: range === "month" ? reportMonth : undefined,
      });
      setAttendanceSummaries(Array.isArray(response?.summaries) ? response.summaries : []);
      setAttendanceOverview(response || null);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || `Unable to load attendance for this ${facilityLowerLabel}.`,
        life: 3500,
      });
    } finally {
      setLoadingAttendance(false);
    }
  };

  useEffect(() => {
    if (!showTimeAttendanceDialog) {
      return;
    }
    void refreshAttendanceSection(attendanceRange, selectedAttendanceReportMonth);
  }, [attendanceRange, backendAPIStore, facilityLowerLabel, homeId, selectedAttendanceReportMonth, showTimeAttendanceDialog]);

  useEffect(() => {
    const row = attendanceCorrectionDialogRow;
    const targetEntry = getResolvedAttendanceCorrectionTarget(
      row,
      attendanceCorrectionDateInput,
      attendanceCorrectionSelectedAttendanceId,
    );
    if (!row?.caregiverId || !targetEntry?.attendanceId || !homeId) {
      setAttendanceCorrectionPreview(null);
      return;
    }
    if (!attendanceCorrectionReason.trim() || !attendanceCorrectionNote.trim()) {
      setAttendanceCorrectionPreview(null);
      return;
    }
    if (attendanceCorrectionAction === "adjust_hours" && (!Number.isFinite(Number(attendanceCorrectionHours || 0)) || Number(attendanceCorrectionHours || 0) <= 0)) {
      setAttendanceCorrectionPreview(null);
      return;
    }
    if (attendanceCorrectionAction === "edit_shift_times" && (!attendanceCorrectionClockInInput || !attendanceCorrectionClockOutInput)) {
      setAttendanceCorrectionPreview(null);
      return;
    }

    let cancelled = false;
    setLoadingAttendanceCorrectionPreview(true);
    const timeoutId = window.setTimeout(() => {
      void (async () => {
        try {
          await backendAPIStore.init();
          const response = await backendAPIStore.correctCaregiverAttendance({
            homeId,
            caregiverId: String(row.caregiverId || "").trim(),
            attendanceId: String(targetEntry.attendanceId || "").trim(),
            action: attendanceCorrectionAction,
            correctionReason: attendanceCorrectionReason,
            correctionNote: attendanceCorrectionNote.trim(),
            correctedWorkedHours: attendanceCorrectionAction === "adjust_hours" ? Number(attendanceCorrectionHours || 0) : undefined,
            correctedClockInAtLocal: attendanceCorrectionAction === "edit_shift_times" ? attendanceCorrectionClockInInput : undefined,
            correctedClockOutAtLocal: attendanceCorrectionAction === "edit_shift_times" ? attendanceCorrectionClockOutInput : undefined,
            previewOnly: true,
          });
          if (!cancelled) {
            setAttendanceCorrectionPreview(response?.impact || null);
          }
        } catch {
          if (!cancelled) {
            setAttendanceCorrectionPreview(null);
          }
        } finally {
          if (!cancelled) {
            setLoadingAttendanceCorrectionPreview(false);
          }
        }
      })();
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [
    attendanceCorrectionAction,
    attendanceCorrectionClockInInput,
    attendanceCorrectionClockOutInput,
    attendanceCorrectionDateInput,
    attendanceCorrectionDialogRow,
    attendanceCorrectionHours,
    attendanceCorrectionNote,
    attendanceCorrectionReason,
    attendanceCorrectionSelectedAttendanceId,
    backendAPIStore,
    homeId,
  ]);

  useEffect(() => {
    if (!attendanceCorrectionDialogRow || !attendanceCorrectionDateInput) {
      return;
    }
    const nextTarget = getResolvedAttendanceCorrectionTarget(
      attendanceCorrectionDialogRow,
      attendanceCorrectionDateInput,
      attendanceCorrectionSelectedAttendanceId,
    );
    if (!nextTarget?.attendanceId) {
      return;
    }
    const isOpenShift = String(nextTarget?.status || attendanceCorrectionDialogRow?.currentStatus || "").trim() === "clocked_in"
      && !String(nextTarget?.clockOutAt || "").trim();
    setAttendanceCorrectionAction(isOpenShift ? "clock_out_now" : "adjust_hours");
    setAttendanceCorrectionReason(isOpenShift ? "forgot_clock_out" : "manual_payroll_adjustment");
    setAttendanceCorrectionHours(Number(nextTarget?.workedHours || 0));
    setAttendanceCorrectionClockInInput(formatDateTimeLocalInput(nextTarget?.clockInAt));
    setAttendanceCorrectionClockOutInput(formatDateTimeLocalInput(nextTarget?.clockOutAt));
    setAttendanceCorrectionPreview(null);
  }, [attendanceCorrectionDateInput, attendanceCorrectionDialogRow, attendanceCorrectionSelectedAttendanceId]);

  useEffect(() => {
    const loadSharedBinders = async () => {
      setLoadingSharedBinders(true);
      try {
        const shares = await backendAPIStore.listBinderSharesSharedWithMe();
        setSharedBinderRows(Array.isArray(shares) ? shares : []);
      } catch {
        setSharedBinderRows([]);
      } finally {
        setLoadingSharedBinders(false);
      }
    };

    void loadSharedBinders();
  }, [backendAPIStore]);

  useEffect(() => {
    setSelectedRoleFilters((current) => {
      const nextValues = current.filter((roleType) => availableStaffRoleFilters.some((option) => option.value === roleType));
      return nextValues.length === current.length ? current : nextValues;
    });
  }, [availableStaffRoleFilters]);

  const activeResidents = useMemo(
    () => residents.filter((resident) => {
      const activeValue: any = resident?.active;
      if (typeof activeValue === "string") {
        return activeValue.trim().toLowerCase() !== "false";
      }
      return activeValue !== false && activeValue !== 0;
    }),
    [residents],
  );

  const staffRows = useMemo(() => (
    linkedStaff.map((staff) => {
      const caregiverId = String(staff?.caregiverId || staff?.userId || "").trim();
      const roleTypes = Array.from(new Set(
        [
          String(staff?.userRoleType || "").trim().toLowerCase(),
          ...(Array.isArray(staff?.roleTypes) ? staff.roleTypes : []),
          ...(Array.isArray(staff?.selectedRoleTypes) ? staff.selectedRoleTypes : []),
        ]
          .map((roleType: any) => String(roleType || "").trim().toLowerCase())
          .filter(Boolean),
      ));
      const primaryRoleType = String(staff?.userRoleType || roleTypes[0] || "").trim().toLowerCase();
      const displayRoleType = String(staff?.userRoleType || primaryRoleType || "").trim().toLowerCase();
      const homeRoleTypes: string[] = Array.from(new Set<string>(
        (Array.isArray(staff?.homeRoleTypes) ? staff.homeRoleTypes : roleTypes)
          .map((roleType: any) => String(roleType || "").trim().toLowerCase())
          .filter(Boolean),
      ));
      const homePrimaryRoleType = String(staff?.homePrimaryRoleType || homeRoleTypes[0] || "").trim().toLowerCase();
      const sharedResidents = residentShares
        .filter((item: any) => String(item?.caregiverId || "").trim() === caregiverId)
        .map((item: any) => {
          const residentProfile = activeResidents.find((resident) => String(resident?.residentId || "").trim() === String(item?.residentId || "").trim());
          return {
            ...item,
            residentName: residentProfile
              ? `${String(residentProfile?.firstName || "").trim()} ${String(residentProfile?.lastName || "").trim()}`.trim() || "Resident"
              : String(item?.residentName || "").trim() || "Resident",
          };
        })
        .filter((item: any) => activeResidents.some((resident) => String(resident?.residentId || "").trim() === String(item?.residentId || "").trim()));
      const clinicalSharedResidents = residentRoleLinks
        .filter((item: any) => String(item?.linkedActorId || "").trim() === caregiverId)
        .map((item: any) => {
          const residentProfile = activeResidents.find((resident) => String(resident?.residentId || "").trim() === String(item?.providerResidentId || "").trim());
          return {
            ...item,
            residentId: String(item?.providerResidentId || "").trim(),
            residentName: residentProfile
              ? `${String(residentProfile?.firstName || "").trim()} ${String(residentProfile?.lastName || "").trim()}`.trim() || "Resident"
              : String(item?.residentName || "").trim() || "Resident",
            assessmentAccessEnabled: normalizeClinicalAccessValue(item?.assessmentAccessEnabled, roleTypes, "assessment"),
            carePlanAccessEnabled: normalizeClinicalAccessValue(item?.carePlanAccessEnabled, roleTypes, "carePlan"),
            delegationAccessEnabled: normalizeClinicalAccessValue(item?.delegationAccessEnabled, roleTypes, "delegation"),
            binderAccessEnabled: normalizeClinicalAccessValue(item?.binderAccessEnabled, roleTypes, "binder"),
          };
        })
        .filter((item: any) => activeResidents.some((resident) => String(resident?.residentId || "").trim() === String(item?.residentId || "").trim()));
      const hasProviderRole = roleTypes.includes(ROLE_TYPE_PROVIDER);
      const supportsClinicalResidentAccess = roleTypes.some((roleType) => CLINICAL_SHARE_ROLE_TYPES.includes(roleType));
      const supportsCaregiverResidentAccess = true;
      const shareOverview = getResidentShareOverview(sharedResidents, clinicalSharedResidents);
      const facilityAdminAccess = staff?.facilityAdminAccess === true;
      const active = staff?.active !== false;
      return {
        caregiverId,
        linkId: String(staff?.linkId || "").trim(),
        name: `${String(staff?.firstName || "").trim()} ${String(staff?.lastName || "").trim()}`.trim() || String(staff?.email || "").trim() || "Staff",
        email: String(staff?.email || "").trim(),
        roleTypes,
        primaryRoleType,
        displayRoleType,
        homeRoleTypes,
        homePrimaryRoleType,
        hasProviderRole,
        facilityAdminAccess,
        active,
        archivedAt: String(staff?.archivedAt || "").trim(),
        shiftSummary: formatShiftSummary(staff),
        sharedResidents,
        clinicalSharedResidents,
        supportsClinicalResidentAccess,
        supportsCaregiverResidentAccess,
        sharedCount: shareOverview.sharedCount,
        permissionSummary: shareOverview.permissionSummary,
        delegationRequirements: normalizeStaffDelegationRequirements(staff?.delegationRequirements),
        delegationCredentialStatus: String(staff?.delegationCredentialStatus || "missing").trim().toLowerCase(),
        staff,
      };
    })
  ), [activeResidents, linkedStaff, residentRoleLinks, residentShares]);

  const activeStaffRows = useMemo(
    () => staffRows.filter((row) => row.active !== false),
    [staffRows],
  );

  const archivedStaffRows = useMemo(
    () => staffRows.filter((row) => row.active === false),
    [staffRows],
  );

  const filteredRows = useMemo(() => {
    const normalizedSearchText = String(searchText || "").trim().toLowerCase();
    const sourceRows = showArchivedStaff ? archivedStaffRows : activeStaffRows;
    return sourceRows.filter((row) => {
      const matchesSearch = !normalizedSearchText
        || row.name.toLowerCase().includes(normalizedSearchText)
        || row.email.toLowerCase().includes(normalizedSearchText);
      const matchesRole = !selectedRoleFilters.length
        || selectedRoleFilters.includes(row.displayRoleType);
      return matchesSearch && matchesRole;
    });
  }, [activeStaffRows, archivedStaffRows, searchText, selectedRoleFilters, showArchivedStaff]);

  useEffect(() => {
    staffResidentTooltip.current?.updateTargetEvents();
  }, [filteredRows]);

  const selectedRows = useMemo(
    () => activeStaffRows.filter((row) => selectedStaffIds.includes(row.caregiverId)),
    [activeStaffRows, selectedStaffIds],
  );

  const sharedBinderSummary = useMemo(() => ({
    total: sharedBinderRows.length,
    underReview: sharedBinderRows.filter((row) => !row.reviewStatus).length,
    questionsPending: sharedBinderRows.filter((row) => row.jobQuestionsStatus === "questions_pending").length,
    accepted: sharedBinderRows.filter((row) => row.reviewStatus === "approved").length,
    needUpdates: sharedBinderRows.filter((row) => row.reviewStatus === "needs_update").length,
  }), [sharedBinderRows]);

  const filteredSharedBinderRows = useMemo(() => {
    const normalizedSearchText = String(sharedBinderSearchText || "").trim().toLowerCase();
    return sharedBinderRows.filter((row) => {
      const ownerDisplayName = String(row.ownerDisplayName || "").trim().toLowerCase();
      const ownerEmail = String(row.ownerEmail || "").trim().toLowerCase();
      const recipientEmail = String(row.recipientEmail || "").trim().toLowerCase();
      const matchesSearch = !normalizedSearchText
        || ownerDisplayName.includes(normalizedSearchText)
        || ownerEmail.includes(normalizedSearchText)
        || recipientEmail.includes(normalizedSearchText);
      const normalizedReviewStatus = String(row.reviewStatus || "").trim().toLowerCase();
      const matchesStatus = selectedSharedBinderStatus === "all"
        ? true
        : selectedSharedBinderStatus === "under_review"
          ? !normalizedReviewStatus
          : selectedSharedBinderStatus === "questions_pending"
            ? String(row.jobQuestionsStatus || "").trim().toLowerCase() === "questions_pending"
            : normalizedReviewStatus === selectedSharedBinderStatus;
      return matchesSearch && matchesStatus;
    });
  }, [selectedSharedBinderStatus, sharedBinderRows, sharedBinderSearchText]);

  useEffect(() => {
    if (!showArchivedStaff) {
      return;
    }
    if (!archivedStaffRows.length) {
      setShowArchivedStaff(false);
    }
  }, [archivedStaffRows.length, showArchivedStaff]);

  useEffect(() => {
    if (showArchivedStaff) {
      if (selectedStaffIds.length) {
        setSelectedStaffIds([]);
      }
      return;
    }
    const activeStaffIds = new Set(activeStaffRows.map((row) => row.caregiverId));
    setSelectedStaffIds((current) => {
      const nextValues = current.filter((staffId) => activeStaffIds.has(staffId));
      return nextValues.length === current.length ? current : nextValues;
    });
  }, [activeStaffRows, selectedStaffIds.length, showArchivedStaff]);

  const formatAttendanceDateTimeDuplicate = (value?: string) => {
    const normalizedValue = String(value || "").trim();
    if (!normalizedValue) {
      return "—";
    }
    const parsedDate = new Date(normalizedValue);
    if (Number.isNaN(parsedDate.getTime())) {
      return normalizedValue;
    }
    return parsedDate.toLocaleString("en-US", {
      timeZone: "America/Los_Angeles",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const openSharedBinderHistoryDialog = (binderShare: BinderShare) => {
    setSelectedSharedBinderHistoryRow(binderShare);
    setShowSharedBinderHistoryDialog(true);
  };

  const onDownloadBinderPacket = async (binderShare: BinderShare) => {
    const shareId = String(binderShare?.shareId || "").trim();
    if (!shareId) {
      return;
    }

    setDownloadingBinderPacketByShareId((current) => ({
      ...current,
      [shareId]: true,
    }));
    try {
      const packetResponse = await backendAPIStore.getBinderSharePacket(shareId);
      const htmlContent = String(packetResponse?.htmlContent || "").trim();
      if (!htmlContent) {
        throw new Error("Packet content is not available.");
      }

      const fileName = String(packetResponse?.fileName || "caregiver-job-application-binder-packet.html").trim();
      const blob = new Blob([htmlContent], { type: "text/html;charset=utf-8" });
      const objectUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to download the caregiver binder packet right now.",
        life: 3500,
      });
    } finally {
      setDownloadingBinderPacketByShareId((current) => ({
        ...current,
        [shareId]: false,
      }));
    }
  };

  const formatAttendanceDateTime = (value?: string) => {
    const normalizedValue = String(value || "").trim();
    if (!normalizedValue) {
      return "—";
    }
    const parsedDate = new Date(normalizedValue);
    if (Number.isNaN(parsedDate.getTime())) {
      return normalizedValue;
    }
    return parsedDate.toLocaleString("en-US", {
      timeZone: "America/Los_Angeles",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const formatCurrency = (value: any) => `$${Number(value || 0).toFixed(2)}`;
  const getAttendanceCorrectionTarget = (row: any) => row?.currentShift || row?.latestEntry || null;
  const getLosAngelesDateKeyForInput = (dateInput: Date | string | number = new Date()) => {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Los_Angeles",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date(dateInput));
    const readPart = (type: string) => parts.find((part) => part.type === type)?.value || "00";
    return `${readPart("year")}-${readPart("month")}-${readPart("day")}`;
  };
  const getAttendanceCorrectionWindowBounds = () => {
    const todayDateKey = getLosAngelesDateKeyForInput();
    const [yearText, monthText] = todayDateKey.split("-");
    const targetYear = Number(yearText || "0");
    const targetMonthIndex = Number(monthText || "1") - 1;
    const earliestAllowedDate = new Date(Date.UTC(targetYear, targetMonthIndex - 2, 1, 12, 0, 0));
    return {
      earliestDateKey: getLosAngelesDateKeyForInput(earliestAllowedDate),
      latestDateKey: todayDateKey,
    };
  };
  const getAttendanceCorrectionEligibleEntries = (row: any) => (
    Array.isArray(row?.correctionEligibleEntries) && row.correctionEligibleEntries.length
      ? row.correctionEligibleEntries
      : (getAttendanceCorrectionTarget(row)?.attendanceId ? [getAttendanceCorrectionTarget(row)] : [])
  );
  const getAttendanceCorrectionEntriesForDate = (row: any, workDate: string) => (
    getAttendanceCorrectionEligibleEntries(row).filter((item: any) => String(item?.workDate || "").trim() === String(workDate || "").trim())
  );
  const getResolvedAttendanceCorrectionTarget = (row: any, workDate: string, attendanceId: string) => {
    const datedEntries = getAttendanceCorrectionEntriesForDate(row, workDate);
    const matchedById = datedEntries.find((item: any) => String(item?.attendanceId || "").trim() === String(attendanceId || "").trim());
    if (matchedById) {
      return matchedById;
    }
    return datedEntries[0] || null;
  };
  const formatDateKeyForDisplay = (value?: string) => {
    const normalizedValue = String(value || "").trim();
    if (!normalizedValue) {
      return "—";
    }
    const parsedDate = new Date(`${normalizedValue}T12:00:00Z`);
    if (Number.isNaN(parsedDate.getTime())) {
      return normalizedValue;
    }
    return parsedDate.toLocaleDateString("en-US", {
      timeZone: "America/Los_Angeles",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };
  const parseDateKeyToCalendarValue = (value?: string) => {
    const normalizedValue = String(value || "").trim();
    if (!normalizedValue) {
      return undefined;
    }
    const parsedDate = new Date(`${normalizedValue}T12:00:00Z`);
    return Number.isNaN(parsedDate.getTime()) ? undefined : parsedDate;
  };
  const formatDateTimeLocalInput = (value?: string) => {
    const normalizedValue = String(value || "").trim();
    if (!normalizedValue) {
      return "";
    }
    const parsedDate = new Date(normalizedValue);
    if (Number.isNaN(parsedDate.getTime())) {
      return "";
    }
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Los_Angeles",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(parsedDate);
    const readPart = (type: string) => parts.find((part) => part.type === type)?.value || "00";
    return `${readPart("year")}-${readPart("month")}-${readPart("day")}T${readPart("hour")}:${readPart("minute")}`;
  };
  const getLosAngelesDateTimeLocalInput = (dateInput: Date | string | number) => {
    const parsedDate = new Date(dateInput);
    if (Number.isNaN(parsedDate.getTime())) {
      return "";
    }
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Los_Angeles",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(parsedDate);
    const readPart = (type: string) => parts.find((part) => part.type === type)?.value || "00";
    return `${readPart("year")}-${readPart("month")}-${readPart("day")}T${readPart("hour")}:${readPart("minute")}`;
  };
  const triggerTextDownload = (content: string, fileName: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType });
    const objectUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(objectUrl);
  };
  const escapeHtml = (value: any) => (
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
  );
  const toCsvValue = (value: any) => {
    const normalizedValue = String(value ?? "").replace(/"/g, "\"\"");
    return `"${normalizedValue}"`;
  };
  const buildCsvContent = (headers: string[], rows: Array<Array<string | number>>) => (
    [
      headers.map((value) => toCsvValue(value)).join(","),
      ...rows.map((row) => row.map((value) => toCsvValue(value)).join(",")),
    ].join("\n")
  );

  const minimumWageDetails = attendanceOverview?.minimumWage || null;
  const minimumWageAmount = Number(minimumWageDetails?.amount || 0);
  const minimumWageEffectiveDate = String(minimumWageDetails?.effectiveDate || "").trim();
  const payrollCloseout = attendanceOverview?.payrollCloseout || null;
  const payrollRangeFinalized = payrollCloseout?.isFinalized === true;
  const payrollRangeLabel = attendanceRange === "month" ? "Monthly" : "Weekly";
  const attendanceCorrectionWindowBounds = useMemo(() => getAttendanceCorrectionWindowBounds(), []);
  const hourlyRateBelowMinimum = minimumWageAmount > 0 && Number(hourlyRateInput || 0) > 0 && Number(hourlyRateInput || 0) < minimumWageAmount;

  const providerAttendanceReport = useMemo(() => {
    const safeSummaries = Array.isArray(attendanceSummaries) ? attendanceSummaries : [];
    const correctionRows = safeSummaries
      .flatMap((item: any) => {
        const correctionEntries = Array.isArray(item?.correctionEntries) ? item.correctionEntries : [];
        return correctionEntries.map((entry: any) => ({
          caregiverId: item?.caregiverId,
          caregiverName: item?.caregiverName || "Caregiver",
          attendanceId: entry?.attendanceId,
          workDate: entry?.workDate,
          workedHours: Number(entry?.workedHours || 0),
          latestCorrectionAt: entry?.latestCorrectionAt,
          latestCorrectionBy: entry?.latestCorrectionBy,
          latestCorrectionNote: entry?.latestCorrectionNote,
          latestCorrectionReasonLabel: entry?.latestCorrectionReasonLabel || "Correction",
          notificationStatus: entry?.notificationStatus,
          notifiedStaffAt: entry?.notifiedStaffAt,
          notificationError: entry?.notificationError,
          latestCorrectionAction: entry?.latestCorrectionAction,
          correctionCount: Number(entry?.correctionCount || 0),
        }));
      })
      .sort((left: any, right: any) => {
        const rightTime = new Date(String(right?.latestCorrectionAt || "")).getTime();
        const leftTime = new Date(String(left?.latestCorrectionAt || "")).getTime();
        return (Number.isNaN(rightTime) ? 0 : rightTime) - (Number.isNaN(leftTime) ? 0 : leftTime);
      });
    const totals = safeSummaries.reduce((acc, item: any) => ({
      totalWorkedHours: acc.totalWorkedHours + Number(item?.totalWorkedHours || 0),
      overtimeHours: acc.overtimeHours + Number(item?.overtimeHours || 0),
      estimatedTotalPay: acc.estimatedTotalPay + Number(item?.estimatedTotalPay || 0),
      sickLeaveAccruedHours: acc.sickLeaveAccruedHours + Number(item?.sickLeaveAccruedHours || 0),
      hourlyRateTotal: acc.hourlyRateTotal + Number(item?.hourlyRate || 0),
      shiftCount: acc.shiftCount + Number(item?.shiftCount || 0),
      openShiftCount: acc.openShiftCount + (String(item?.currentStatus || "").trim() === "clocked_in" ? 1 : 0),
      overtimeStaffCount: acc.overtimeStaffCount + (Number(item?.overtimeHours || 0) > 0 ? 1 : 0),
      missingShiftCount: acc.missingShiftCount + (String(item?.shiftSummary || "").trim() === "No shift set" ? 1 : 0),
      belowMinimumPayCount: acc.belowMinimumPayCount + ((minimumWageAmount > 0 && Number(item?.hourlyRate || 0) > 0 && Number(item?.hourlyRate || 0) < minimumWageAmount) ? 1 : 0),
      correctedShiftCount: acc.correctedShiftCount + Number(item?.correctedShiftCount || 0),
      failedCorrectionNotificationCount: acc.failedCorrectionNotificationCount + Number(item?.failedCorrectionNotificationCount || 0),
      clockOutCorrectionCount: acc.clockOutCorrectionCount + Number(item?.clockOutCorrectionCount || 0),
      adjustedHoursCorrectionCount: acc.adjustedHoursCorrectionCount + Number(item?.adjustedHoursCorrectionCount || 0),
    }), {
      totalWorkedHours: 0,
      overtimeHours: 0,
      estimatedTotalPay: 0,
      sickLeaveAccruedHours: 0,
      hourlyRateTotal: 0,
      shiftCount: 0,
      openShiftCount: 0,
      overtimeStaffCount: 0,
      missingShiftCount: 0,
      belowMinimumPayCount: 0,
      correctedShiftCount: 0,
      failedCorrectionNotificationCount: 0,
      clockOutCorrectionCount: 0,
      adjustedHoursCorrectionCount: 0,
    });
    const averageRate = safeSummaries.length ? totals.hourlyRateTotal / safeSummaries.length : 0;
    const averageShiftHours = totals.shiftCount ? totals.totalWorkedHours / totals.shiftCount : 0;
    const payrollSummaryRows = safeSummaries
      .map((item: any) => ({
        caregiverId: item?.caregiverId,
        caregiverName: item?.caregiverName || "Caregiver",
        hourlyRate: Number(item?.hourlyRate || 0),
        regularHours: Math.max(0, Number(item?.totalWorkedHours || 0) - Number(item?.overtimeHours || 0)),
        overtimeHours: Number(item?.overtimeHours || 0),
        totalWorkedHours: Number(item?.totalWorkedHours || 0),
        estimatedRegularPay: Number(item?.estimatedRegularPay || 0),
        estimatedOvertimePay: Number(item?.estimatedOvertimePay || 0),
        estimatedTotalPay: Number(item?.estimatedTotalPay || 0),
        shiftCount: Number(item?.shiftCount || 0),
        latestActivity: formatAttendanceDateTime(item?.currentShift?.clockInAt || item?.latestEntry?.clockOutAt || item?.latestEntry?.clockInAt),
      }))
      .sort((left: any, right: any) => right.estimatedTotalPay - left.estimatedTotalPay);
    const overtimeRows = payrollSummaryRows
      .filter((item: any) => item.overtimeHours > 0)
      .sort((left: any, right: any) => right.overtimeHours - left.overtimeHours);
    const reviewRows = safeSummaries
      .flatMap((item: any) => {
        const needsReviewItems = Array.isArray(item?.needsReviewItems) ? item.needsReviewItems : [];
        return needsReviewItems.map((reviewItem: any) => ({
          caregiverId: item?.caregiverId,
          caregiverName: item?.caregiverName || "Caregiver",
          attendanceId: reviewItem?.attendanceId,
          reviewItemId: reviewItem?.reviewItemId,
          reviewStatus: reviewItem?.reviewStatus || "open",
          resolvedAt: reviewItem?.resolvedAt,
          resolvedBy: reviewItem?.resolvedBy,
          resolutionNote: reviewItem?.resolutionNote,
          reopenedAt: reviewItem?.reopenedAt,
          reopenedBy: reviewItem?.reopenedBy,
          reopenedNote: reviewItem?.reopenedNote,
          issueType: String(reviewItem?.type || "").trim(),
          issueSummary: String(reviewItem?.label || "Needs review").trim(),
          issueDetail: String(reviewItem?.detail || "").trim(),
          latestActivity: formatAttendanceDateTime(item?.currentShift?.clockInAt || item?.latestEntry?.clockOutAt || item?.latestEntry?.clockInAt),
        }));
      })
      .sort((left: any, right: any) => {
        const leftResolved = String(left?.reviewStatus || "").trim().toLowerCase() === "resolved";
        const rightResolved = String(right?.reviewStatus || "").trim().toLowerCase() === "resolved";
        if (leftResolved !== rightResolved) {
          return leftResolved ? 1 : -1;
        }
        return String(left?.caregiverName || "").localeCompare(String(right?.caregiverName || ""));
      });
    return {
      ...totals,
      averageRate,
      averageShiftHours,
      payrollSummaryRows,
      overtimeRows,
      reviewRows,
      openReviewCount: reviewRows.filter((item: any) => String(item?.reviewStatus || "").trim().toLowerCase() !== "resolved").length,
      resolvedReviewCount: reviewRows.filter((item: any) => String(item?.reviewStatus || "").trim().toLowerCase() === "resolved").length,
      correctionRows,
      recentCorrectionRows: correctionRows.slice(0, 12),
    };
  }, [attendanceSummaries, formatAttendanceDateTime, minimumWageAmount]);

  const timePayStaffOptions = useMemo(
    () => activeStaffRows.map((row) => ({
      label: String(row?.name || "Staff").trim() || "Staff",
      value: String(row?.caregiverId || "").trim(),
    })).filter((option) => option.value),
    [activeStaffRows],
  );

  useEffect(() => {
    const availableIds = activeStaffRows
      .map((row) => String(row?.caregiverId || "").trim())
      .filter(Boolean);
    if (!availableIds.length) {
      if (selectedTimePayCaregiverId) {
        setSelectedTimePayCaregiverId("");
      }
      return;
    }
    if (!selectedTimePayCaregiverId) {
      setSelectedTimePayCaregiverId(availableIds[0]);
      return;
    }
    if (!availableIds.includes(selectedTimePayCaregiverId)) {
      setSelectedTimePayCaregiverId(availableIds[0]);
    }
  }, [activeStaffRows, selectedTimePayCaregiverId]);

  const selectedTimePaySummary = useMemo(
    () => (Array.isArray(attendanceSummaries) ? attendanceSummaries : []).find((item: any) => String(item?.caregiverId || "").trim() === selectedTimePayCaregiverId) || null,
    [attendanceSummaries, selectedTimePayCaregiverId],
  );
  const selectedTimePayStaffRow = useMemo(
    () => staffRows.find((row) => String(row?.caregiverId || "").trim() === selectedTimePayCaregiverId) || null,
    [selectedTimePayCaregiverId, staffRows],
  );
  const selectedTimePayReviewRows = useMemo(
    () => providerAttendanceReport.reviewRows.filter((item: any) => String(item?.caregiverId || "").trim() === selectedTimePayCaregiverId),
    [providerAttendanceReport.reviewRows, selectedTimePayCaregiverId],
  );
  const selectedTimePayCorrectionRows = useMemo(
    () => providerAttendanceReport.correctionRows.filter((item: any) => String(item?.caregiverId || "").trim() === selectedTimePayCaregiverId),
    [providerAttendanceReport.correctionRows, selectedTimePayCaregiverId],
  );
  const selectedAttendanceCorrectionTarget = useMemo(
    () => getResolvedAttendanceCorrectionTarget(
      attendanceCorrectionDialogRow,
      attendanceCorrectionDateInput,
      attendanceCorrectionSelectedAttendanceId,
    ),
    [attendanceCorrectionDateInput, attendanceCorrectionDialogRow, attendanceCorrectionSelectedAttendanceId],
  );
  const attendanceCorrectionDateEntries = useMemo(
    () => getAttendanceCorrectionEntriesForDate(attendanceCorrectionDialogRow, attendanceCorrectionDateInput),
    [attendanceCorrectionDateInput, attendanceCorrectionDialogRow],
  );

  const openTimeAttendanceDialog = (row?: any) => {
    const targetCaregiverId = String(row?.caregiverId || "").trim();
    if (targetCaregiverId) {
      setSelectedTimePayCaregiverId(targetCaregiverId);
    }
    setShowTimeAttendanceDialog(true);
  };

  const closeTimeAttendanceDialog = () => {
    if (savingPayrollCloseout || savingAttendanceCorrection || loadingAttendance) {
      return;
    }
    setShowTimeAttendanceDialog(false);
  };

  const openAttendanceCorrectionDialog = (row: any) => {
    const targetEntry = getAttendanceCorrectionTarget(row);
    if (!targetEntry?.attendanceId) {
      toast.current?.show({ severity: "warn", summary: "No Shift Found", detail: "This staff member does not have a shift available to correct yet.", life: 2500 });
      return;
    }
    const isOpenShift = String(targetEntry?.status || row?.currentStatus || "").trim() === "clocked_in" && !String(targetEntry?.clockOutAt || "").trim();
    const targetWorkDate = String(targetEntry?.workDate || "").trim() || getLosAngelesDateKeyForInput(targetEntry?.clockInAt || new Date());
    setAttendanceCorrectionDialogRow(row);
    setAttendanceCorrectionDateInput(targetWorkDate);
    setAttendanceCorrectionSelectedAttendanceId(String(targetEntry?.attendanceId || "").trim());
    setAttendanceCorrectionAction(isOpenShift ? "clock_out_now" : "adjust_hours");
    setAttendanceCorrectionReason(isOpenShift ? "forgot_clock_out" : "manual_payroll_adjustment");
    setAttendanceCorrectionNote("");
    setAttendanceCorrectionHours(Number(targetEntry?.workedHours || 0));
    setAttendanceCorrectionClockInInput(formatDateTimeLocalInput(targetEntry?.clockInAt));
    setAttendanceCorrectionClockOutInput(formatDateTimeLocalInput(targetEntry?.clockOutAt));
    setAttendanceCorrectionPreview(null);
  };

  const closeAttendanceCorrectionDialog = () => {
    if (savingAttendanceCorrection) {
      return;
    }
    setAttendanceCorrectionDialogRow(null);
    setAttendanceCorrectionDateInput("");
    setAttendanceCorrectionSelectedAttendanceId("");
    setAttendanceCorrectionNote("");
    setAttendanceCorrectionPreview(null);
  };

  const openCompensationDialog = (row: any) => {
    setCompensationDialogRow(row);
    setHourlyRateInput(Number(row?.staff?.hourlyRate || 0));
    setVacationBalanceInput(Number(row?.staff?.vacationBalanceHours || 0));
    setLeaveBalanceInput(Number(row?.staff?.leaveBalanceHours || 0));
    setCompensationEffectiveDateInput(new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/Los_Angeles",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date()));
    setCompensationNoteInput("");
  };

  const onSaveCompensation = async () => {
    const row = compensationDialogRow;
    if (!row?.linkId || !row?.caregiverId || !homeId) {
      return;
    }
    setSavingCompensation(true);
    try {
      await backendAPIStore.init();
      await backendAPIStore.updateHomeCaregiverLinkCompensation({
        homeId,
        caregiverId: String(row.caregiverId || ""),
        linkId: String(row.linkId || ""),
        hourlyRate: Number(hourlyRateInput || 0),
        vacationBalanceHours: Number(vacationBalanceInput || 0),
        leaveBalanceHours: Number(leaveBalanceInput || 0),
        compensationEffectiveDate: compensationEffectiveDateInput,
        compensationNote: compensationNoteInput.trim(),
      });
      toast.current?.show({
        severity: "success",
        summary: "Compensation Updated",
        detail: `${row?.name || "Staff member"} compensation details were saved.`,
        life: 3000,
      });
      setCompensationDialogRow(null);
      await loadPageData();
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to update compensation.", life: 3000 });
    } finally {
      setSavingCompensation(false);
    }
  };

  const onSaveAttendanceCorrection = async () => {
    const row = attendanceCorrectionDialogRow;
    const targetEntry = selectedAttendanceCorrectionTarget;
    if (!row?.caregiverId || !targetEntry?.attendanceId || !homeId) {
      return;
    }
    if (!attendanceCorrectionReason.trim()) {
      toast.current?.show({ severity: "warn", summary: "Select Reason", detail: "Choose a correction reason before saving.", life: 2500 });
      return;
    }
    if (!attendanceCorrectionNote.trim()) {
      toast.current?.show({ severity: "warn", summary: "Add Note", detail: "Explain why this attendance correction is needed.", life: 2500 });
      return;
    }
    if (attendanceCorrectionAction === "adjust_hours" && (!Number.isFinite(Number(attendanceCorrectionHours || 0)) || Number(attendanceCorrectionHours || 0) <= 0)) {
      toast.current?.show({ severity: "warn", summary: "Enter Hours", detail: "Provide the corrected worked hours before saving.", life: 2500 });
      return;
    }
    if (attendanceCorrectionAction === "edit_shift_times" && (!attendanceCorrectionClockInInput || !attendanceCorrectionClockOutInput)) {
      toast.current?.show({ severity: "warn", summary: "Enter Times", detail: "Provide corrected clock-in and clock-out times before saving.", life: 2500 });
      return;
    }

    setSavingAttendanceCorrection(true);
    try {
      await backendAPIStore.init();
      const response = await backendAPIStore.correctCaregiverAttendance({
        homeId,
        caregiverId: String(row.caregiverId || "").trim(),
        attendanceId: String(targetEntry.attendanceId || "").trim(),
        targetWorkDate: attendanceCorrectionDateInput,
        action: attendanceCorrectionAction,
        correctionReason: attendanceCorrectionReason,
        correctionNote: attendanceCorrectionNote.trim(),
        correctedWorkedHours: attendanceCorrectionAction === "adjust_hours" ? Number(attendanceCorrectionHours || 0) : undefined,
        correctedClockInAtLocal: attendanceCorrectionAction === "edit_shift_times" ? attendanceCorrectionClockInInput : undefined,
        correctedClockOutAtLocal: attendanceCorrectionAction === "edit_shift_times" ? attendanceCorrectionClockOutInput : undefined,
      });
      const notificationStatus = String(response?.notificationStatus || "").trim().toLowerCase();
      toast.current?.show({
        severity: notificationStatus === "failed" ? "warn" : "success",
        summary: notificationStatus === "failed" ? "Correction Saved" : "Correction Saved and Emailed",
        detail: notificationStatus === "failed"
          ? response?.notificationError || "Attendance correction saved, but the email notification failed."
          : `${row?.caregiverName || "Staff member"} was notified about the correction.`,
        life: 3500,
      });
      closeAttendanceCorrectionDialog();
      await refreshAttendanceSection(attendanceRange);
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to save attendance correction.", life: 3000 });
    } finally {
      setSavingAttendanceCorrection(false);
    }
  };

  const openPayrollCloseoutDialog = (nextAction: "finalize_payroll_period" | "reopen_payroll_period") => {
    setPayrollCloseoutDialogAction(nextAction);
    setPayrollCloseoutNoteInput("");
  };

  const closePayrollCloseoutDialog = () => {
    if (savingPayrollCloseout) {
      return;
    }
    setPayrollCloseoutDialogAction(null);
    setPayrollCloseoutNoteInput("");
  };

  const onTogglePayrollCloseout = async (nextAction: "finalize_payroll_period" | "reopen_payroll_period") => {
    if (!homeId) {
      return;
    }
    setSavingPayrollCloseout(true);
    try {
      await backendAPIStore.init();
      await backendAPIStore.updateProviderPayrollCloseout({
        homeId,
        range: attendanceRange,
        action: nextAction,
        approvalNote: payrollCloseoutNoteInput.trim() || undefined,
        reportMonth: attendanceRange === "month" ? selectedAttendanceReportMonth : undefined,
      });
      toast.current?.show({
        severity: "success",
        summary: nextAction === "finalize_payroll_period" ? "Payroll Finalized" : "Payroll Reopened",
        detail: nextAction === "finalize_payroll_period"
          ? `The ${attendanceRange} payroll period is now locked for attendance edits.`
          : `The ${attendanceRange} payroll period is open again for attendance edits.`,
        life: 3000,
      });
      setPayrollCloseoutDialogAction(null);
      setPayrollCloseoutNoteInput("");
      await refreshAttendanceSection(attendanceRange);
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to update payroll closeout.", life: 3000 });
    } finally {
      setSavingPayrollCloseout(false);
    }
  };

  const openReviewWorkflowDialog = (row: any, nextAction: "resolve_review_item" | "reopen_review_item") => {
    setReviewWorkflowDialogRow(row);
    setReviewWorkflowAction(nextAction);
    setReviewWorkflowNote("");
  };

  const closeReviewWorkflowDialog = () => {
    const reviewItemId = String(reviewWorkflowDialogRow?.reviewItemId || "").trim();
    if (reviewItemId && updatingReviewItemById[reviewItemId]) {
      return;
    }
    setReviewWorkflowDialogRow(null);
    setReviewWorkflowNote("");
    setReviewWorkflowAction("resolve_review_item");
  };

  const onSaveReviewWorkflow = async () => {
    const reviewItemId = String(reviewWorkflowDialogRow?.reviewItemId || "").trim();
    if (!homeId || !reviewItemId) {
      return;
    }
    if (reviewWorkflowAction === "resolve_review_item" && !reviewWorkflowNote.trim()) {
      toast.current?.show({ severity: "warn", summary: "Add Review Note", detail: "Add a note before resolving this review item.", life: 2500 });
      return;
    }

    setUpdatingReviewItemById((current) => ({
      ...current,
      [reviewItemId]: true,
    }));
    try {
      await backendAPIStore.init();
      await backendAPIStore.updateProviderAttendanceReviewItem({
        homeId,
        reviewItemId,
        action: reviewWorkflowAction,
        resolutionNote: reviewWorkflowNote.trim() || undefined,
      });
      toast.current?.show({
        severity: "success",
        summary: reviewWorkflowAction === "resolve_review_item" ? "Review Item Resolved" : "Review Item Reopened",
        detail: reviewWorkflowAction === "resolve_review_item"
          ? "The review queue was updated and the resolution note was saved."
          : "The review item is open again for follow-up.",
        life: 3000,
      });
      setReviewWorkflowDialogRow(null);
      setReviewWorkflowNote("");
      setReviewWorkflowAction("resolve_review_item");
      await refreshAttendanceSection(attendanceRange);
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to update the review item.", life: 3000 });
    } finally {
      setUpdatingReviewItemById((current) => ({
        ...current,
        [reviewItemId]: false,
      }));
    }
  };

  const onResendAttendanceCorrectionEmail = async (row: any) => {
    const attendanceId = String(row?.attendanceId || "").trim();
    const caregiverId = String(row?.caregiverId || "").trim();
    if (!attendanceId || !caregiverId || !homeId) {
      return;
    }

    setResendingAttendanceCorrectionById((current) => ({
      ...current,
      [attendanceId]: true,
    }));
    try {
      await backendAPIStore.init();
      const response = await backendAPIStore.resendCaregiverAttendanceCorrectionEmail({
        homeId,
        caregiverId,
        attendanceId,
      });
      const notificationStatus = String(response?.notificationStatus || "").trim().toLowerCase();
      toast.current?.show({
        severity: notificationStatus === "failed" ? "warn" : "success",
        summary: notificationStatus === "failed" ? "Resend Failed" : "Email Sent",
        detail: notificationStatus === "failed"
          ? response?.notificationError || "The correction email could not be resent."
          : `${row?.caregiverName || "Staff member"} was notified about the correction.`,
        life: 3500,
      });
      await refreshAttendanceSection(attendanceRange);
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to resend the correction email.", life: 3000 });
    } finally {
      setResendingAttendanceCorrectionById((current) => ({
        ...current,
        [attendanceId]: false,
      }));
    }
  };

  const onExportPayrollReport = () => {
    setExportingPayrollReport(true);
    try {
      const rows = (Array.isArray(attendanceSummaries) ? attendanceSummaries : []).map((item: any) => ([
        String(item?.caregiverName || "Caregiver"),
        String(item?.caregiverEmail || ""),
        attendanceRange === "month" ? "Monthly" : "Weekly",
        String(item?.shiftSummary || "No shift set"),
        Number(item?.shiftCount || 0).toFixed(0),
        Number(item?.totalWorkedHours || 0).toFixed(2),
        Number(item?.overtimeHours || 0).toFixed(2),
        Number(item?.hourlyRate || 0).toFixed(2),
        Number(item?.estimatedRegularPay || 0).toFixed(2),
        Number(item?.estimatedOvertimePay || 0).toFixed(2),
        Number(item?.estimatedTotalPay || 0).toFixed(2),
        Number(item?.sickLeaveAccruedHours || 0).toFixed(2),
        Number(item?.vacationBalanceHours || 0).toFixed(2),
        Number(item?.leaveBalanceHours || 0).toFixed(2),
        String(item?.currentStatus || "").trim() === "clocked_in" ? "Clocked In" : "Off Shift",
        formatAttendanceDateTime(item?.currentShift?.clockInAt || item?.latestEntry?.clockOutAt || item?.latestEntry?.clockInAt),
      ]));
      const csvContent = buildCsvContent(
        [
          "Staff",
          "Email",
          "Report Range",
          "Assigned Shift",
          "Shifts",
          attendanceRange === "month" ? "Month Hours" : "Week Hours",
          "Overtime Hours",
          "Hourly Rate",
          "Estimated Regular Pay",
          "Estimated Overtime Pay",
          "Earned So Far",
          "Sick Accrued Hours",
          "Vacation Balance Hours",
          "Leave Balance Hours",
          "Current Status",
          "Latest Activity",
        ],
        rows,
      );
      triggerTextDownload(
        csvContent,
        `staff-payroll-${attendanceRange}-report-${new Intl.DateTimeFormat("en-CA", { timeZone: "America/Los_Angeles", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date())}.csv`,
        "text/csv;charset=utf-8",
      );
      toast.current?.show({ severity: "success", summary: "Payroll Report Downloaded", detail: "The current attendance range was exported as a payroll-ready CSV.", life: 3000 });
    } finally {
      setExportingPayrollReport(false);
    }
  };

  const onExportPayrollPacket = async () => {
    if (!payrollRangeFinalized) {
      return;
    }
    setExportingPayrollPacket(true);
    try {
      await backendAPIStore.init();
      const response = await backendAPIStore.generateProviderPayrollPacketDocument({
        homeId,
        range: attendanceRange,
        reportMonth: attendanceRange === "month" ? selectedAttendanceReportMonth : undefined,
      });
      if (String(response?.documentUrl || "").trim()) {
        openUrlInNewTab(String(response.documentUrl).trim());
      }
      toast.current?.show({
        severity: "success",
        summary: "Payroll Packet Ready",
        detail: "The finalized payroll packet was exported as a Word document.",
        life: 3000,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to generate the finalized payroll packet.",
        life: 3000,
      });
    } finally {
      setExportingPayrollPacket(false);
    }
  };

  const onExportCorrectionReport = () => {
    setExportingCorrectionReport(true);
    try {
      const rows = providerAttendanceReport.correctionRows.map((item: any) => ([
        String(item?.caregiverName || "Caregiver"),
        String(item?.workDate || ""),
        String(item?.latestCorrectionReasonLabel || "Correction"),
        Number(item?.workedHours || 0).toFixed(2),
        formatAttendanceDateTime(item?.latestCorrectionAt),
        String(item?.latestCorrectionBy || ""),
        String(item?.notificationStatus || ""),
        formatAttendanceDateTime(item?.notifiedStaffAt),
        String(item?.notificationError || ""),
        String(item?.latestCorrectionNote || ""),
        Number(item?.correctionCount || 0).toFixed(0),
      ]));
      const csvContent = buildCsvContent(
        [
          "Staff",
          "Work Date",
          "Correction Reason",
          "Corrected Hours",
          "Corrected At",
          "Corrected By",
          "Notification Status",
          "Notified Staff At",
          "Notification Error",
          "Provider Note",
          "Correction Count",
        ],
        rows,
      );
      triggerTextDownload(
        csvContent,
        `staff-corrections-${attendanceRange}-report-${new Intl.DateTimeFormat("en-CA", { timeZone: "America/Los_Angeles", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date())}.csv`,
        "text/csv;charset=utf-8",
      );
      toast.current?.show({ severity: "success", summary: "Correction Report Downloaded", detail: "Recent attendance corrections were exported as CSV.", life: 3000 });
    } finally {
      setExportingCorrectionReport(false);
    }
  };

  const onExportMonthlyStaffSummary = async (rowData: any) => {
    const caregiverId = String(rowData?.caregiverId || "").trim();
    if (!homeId || !caregiverId) {
      return;
    }

    setExportingMonthlySummaryByCaregiverId((current) => ({
      ...current,
      [caregiverId]: true,
    }));
    try {
      await backendAPIStore.init();
      const response = await backendAPIStore.generateCaregiverMonthlyTimePaySummaryDocument({
        homeId,
        caregiverId,
        reportMonth: selectedAttendanceReportMonth,
      });
      if (String(response?.documentUrl || "").trim()) {
        openUrlInNewTab(String(response.documentUrl).trim());
      }
      toast.current?.show({
        severity: "success",
        summary: "Monthly Summary Ready",
        detail: `Monthly time and pay summary is ready for ${String(rowData?.caregiverName || "this staff member").trim()}.`,
        life: 3000,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to generate the monthly staff summary document.",
        life: 3500,
      });
    } finally {
      setExportingMonthlySummaryByCaregiverId((current) => ({
        ...current,
        [caregiverId]: false,
      }));
    }
  };

  const allVisibleSelected = filteredRows.length > 0 && filteredRows.every((row) => selectedStaffIds.includes(row.caregiverId));
  const hasAnyEnabledShiftDay = dailyShiftSchedule.some((item) => item.enabled);

  const openBulkShiftForRows = (caregiverIds?: string[]) => {
    const targetIds = caregiverIds && caregiverIds.length ? caregiverIds : selectedStaffIds;
    if (!targetIds.length) {
      toast.current?.show({ severity: "warn", summary: "Select Staff", detail: "Choose at least one staff member first.", life: 2500 });
      return;
    }
    setSelectedStaffIds(targetIds);
    setDailyShiftSchedule(createDailyShiftSchedule());
    setBulkEveryDayStartTime("08:00");
    setBulkEveryDayEndTime("16:00");
    setAllTimeShiftAccessEnabled(false);
    setMobileAccessEnabled(true);
    setShowBulkShiftDialog(true);
  };

  const openBulkAccessForRows = (caregiverIds?: string[]) => {
    const targetIds = caregiverIds && caregiverIds.length ? caregiverIds : selectedStaffIds;
    if (!targetIds.length) {
      toast.current?.show({ severity: "warn", summary: "Select Staff", detail: "Choose at least one staff member first.", life: 2500 });
      return;
    }
    setSelectedStaffIds(targetIds);
    setSelectedResidentIds([]);
    setSelectedBulkAccessPreset(STAFF_ACCESS_PRESET_CUSTOM);
    setBulkResidentAssessmentAccessById(buildResidentBooleanMap(activeResidents, false));
    setBulkResidentClinicalCarePlanAccessById(buildResidentBooleanMap(activeResidents, false));
    setBulkResidentDelegationAccessById(buildResidentBooleanMap(activeResidents, false));
    setBulkResidentBinderAccessById(buildResidentBooleanMap(activeResidents, false));
    setShowBulkAccessDialog(true);
  };

  useEffect(() => {
    const dispatchSelection = () => {
      window.dispatchEvent(new CustomEvent("staff-management:selection-changed", { detail: { count: selectedStaffIds.length } }));
      window.dispatchEvent(new CustomEvent("staff-management:view-changed", { detail: { archived: showArchivedStaff } }));
    };
    const handleNewHiringIntake = () => setShowHiringIntakeDialog(true);
    const handleAddStaff = () => setShowQuickAddDialog(true);
    const handleBulkShift = () => openBulkShiftForRows();
    const handleBulkAccess = () => openBulkAccessForRows();
    window.addEventListener("staff-management:new-hiring-intake", handleNewHiringIntake);
    window.addEventListener("staff-management:add-staff", handleAddStaff);
    window.addEventListener("staff-management:bulk-shift", handleBulkShift);
    window.addEventListener("staff-management:bulk-access", handleBulkAccess);
    dispatchSelection();
    return () => {
      window.removeEventListener("staff-management:new-hiring-intake", handleNewHiringIntake);
      window.removeEventListener("staff-management:add-staff", handleAddStaff);
      window.removeEventListener("staff-management:bulk-shift", handleBulkShift);
      window.removeEventListener("staff-management:bulk-access", handleBulkAccess);
    };
  }, [selectedStaffIds.length, showArchivedStaff]);

  const bulkAccessPresetOptions = useMemo(() => {
    const roleTypes = Array.from(new Set(
      selectedRows.flatMap((row) => Array.isArray(row?.roleTypes) ? row.roleTypes : []),
    ));
    const options: Array<{ label: string; value: string; description: string }> = [];
    if (roleTypes.includes(ROLE_TYPE_CAREGIVER)) {
      options.push({
        label: "Caregiver",
        value: STAFF_ACCESS_PRESET_CAREGIVER,
        description: "Daily actions for all selected residents.",
      });
    }
    if (roleTypes.includes(ROLE_TYPE_NURSE)) {
      options.push({
        label: "Nurse",
        value: STAFF_ACCESS_PRESET_NURSE,
        description: "Clinical access plus daily actions, with delegation.",
      });
    }
    if (roleTypes.includes(ROLE_TYPE_MHP)) {
      options.push({
        label: "MHP",
        value: STAFF_ACCESS_PRESET_MHP,
        description: "Clinical access plus daily actions, without delegation.",
      });
    }
    return options;
  }, [selectedRows]);

  const bulkDialogSupportsClinicalAccess = useMemo(
    () => selectedRows.some((row) => row?.supportsClinicalResidentAccess),
    [selectedRows],
  );

  const showBulkClinicalAccess = bulkDialogSupportsClinicalAccess && (
    selectedBulkAccessPreset === STAFF_ACCESS_PRESET_NURSE
    || selectedBulkAccessPreset === STAFF_ACCESS_PRESET_MHP
    || activeResidents.some((resident) => {
      const residentId = String(resident?.residentId || "").trim();
      return bulkResidentBinderAccessById[residentId]
        || bulkResidentAssessmentAccessById[residentId]
        || bulkResidentClinicalCarePlanAccessById[residentId]
        || bulkResidentDelegationAccessById[residentId];
    })
  );

  const applyBulkAccessPreset = (preset: string) => {
    setSelectedBulkAccessPreset(preset);
    if (preset === STAFF_ACCESS_PRESET_CUSTOM) {
      return;
    }

    const enableAllResidents = buildResidentBooleanMap(activeResidents, true);
    const disableAllResidents = buildResidentBooleanMap(activeResidents, false);
    setSelectedResidentIds(activeResidents
      .map((resident) => String(resident?.residentId || "").trim())
      .filter(Boolean));
    if (preset === STAFF_ACCESS_PRESET_CAREGIVER) {
      setBulkResidentBinderAccessById(disableAllResidents);
      setBulkResidentAssessmentAccessById(disableAllResidents);
      setBulkResidentClinicalCarePlanAccessById(disableAllResidents);
      setBulkResidentDelegationAccessById(disableAllResidents);
      return;
    }
    if (preset === STAFF_ACCESS_PRESET_NURSE || preset === STAFF_ACCESS_PRESET_MHP) {
      setBulkResidentBinderAccessById(enableAllResidents);
      setBulkResidentAssessmentAccessById(enableAllResidents);
      setBulkResidentClinicalCarePlanAccessById(enableAllResidents);
      setBulkResidentDelegationAccessById(
        preset === STAFF_ACCESS_PRESET_NURSE ? enableAllResidents : disableAllResidents,
      );
    }
  };

  const openStaffAccessDialog = (row: any) => {
    const caregiverId = String(row?.caregiverId || "").trim();
    if (!caregiverId) {
      toast.current?.show({ severity: "warn", summary: "Staff unavailable", detail: "Choose a valid staff member first.", life: 2500 });
      return;
    }

    const nextAssessmentAccessById: Record<string, boolean> = {};
    const nextClinicalCarePlanAccessById: Record<string, boolean> = {};
    const nextDelegationAccessById: Record<string, boolean> = {};
    const nextBinderAccessById: Record<string, boolean> = {};

    activeResidents.forEach((resident) => {
      const residentId = String(resident?.residentId || "").trim();
      if (!residentId) {
        return;
      }
      const existingShare = Array.isArray(row?.sharedResidents)
        ? row.sharedResidents.find((item: any) => String(item?.residentId || "").trim() === residentId)
        : null;
      const existingClinicalShare = Array.isArray(row?.clinicalSharedResidents)
        ? row.clinicalSharedResidents.find((item: any) => String(item?.residentId || "").trim() === residentId)
        : null;
      nextAssessmentAccessById[residentId] = existingClinicalShare?.assessmentAccessEnabled === true;
      nextClinicalCarePlanAccessById[residentId] = existingClinicalShare?.carePlanAccessEnabled === true;
      nextDelegationAccessById[residentId] = existingClinicalShare?.delegationAccessEnabled === true;
      nextBinderAccessById[residentId] = existingClinicalShare?.binderAccessEnabled === true;
    });

    const nextSelectedResidentIds = activeResidents
      .map((resident) => String(resident?.residentId || "").trim())
      .filter((residentId) => (
        row.sharedResidents?.some((item: any) => String(item?.residentId || "").trim() === residentId)
        || nextAssessmentAccessById[residentId]
        || nextClinicalCarePlanAccessById[residentId]
        || nextDelegationAccessById[residentId]
        || nextBinderAccessById[residentId]
      ));

    const detectedPreset = detectStaffAccessPreset(activeResidents, Array.isArray(row?.roleTypes) ? row.roleTypes : [], {
      residentAssessmentAccessById: nextAssessmentAccessById,
      residentClinicalCarePlanAccessById: nextClinicalCarePlanAccessById,
      residentDelegationAccessById: nextDelegationAccessById,
      residentBinderAccessById: nextBinderAccessById,
    });

    setSelectedStaffAccessRow(row);
    setSelectedResidentIds(nextSelectedResidentIds);
    setSelectedStaffAccessPreset(detectedPreset);
    setResidentAssessmentAccessById(nextAssessmentAccessById);
    setResidentClinicalCarePlanAccessById(nextClinicalCarePlanAccessById);
    setResidentDelegationAccessById(nextDelegationAccessById);
    setResidentBinderAccessById(nextBinderAccessById);
    setShowStaffAccessDialog(true);
  };

  const onToggleAllVisibleRows = (checked: boolean) => {
    if (checked) {
      setSelectedStaffIds(Array.from(new Set([...selectedStaffIds, ...filteredRows.map((row) => row.caregiverId)])));
      return;
    }
    setSelectedStaffIds((current) => current.filter((caregiverId) => !filteredRows.some((row) => row.caregiverId === caregiverId)));
  };

  const staffAccessPresetOptions = useMemo(() => {
    const roleTypes = Array.isArray(selectedStaffAccessRow?.roleTypes) ? selectedStaffAccessRow.roleTypes : [];
    const options: Array<{ label: string; value: string; description: string }> = [];
    if (roleTypes.includes(ROLE_TYPE_CAREGIVER)) {
      options.push({
        label: "Caregiver",
        value: STAFF_ACCESS_PRESET_CAREGIVER,
        description: "Care plan and daily actions.",
      });
    }
    if (roleTypes.includes(ROLE_TYPE_NURSE)) {
      options.push({
        label: "Nurse",
        value: STAFF_ACCESS_PRESET_NURSE,
        description: "Clinical access plus daily actions, with delegation.",
      });
    }
    if (roleTypes.includes(ROLE_TYPE_MHP)) {
      options.push({
        label: "MHP",
        value: STAFF_ACCESS_PRESET_MHP,
        description: "Clinical access plus daily actions, without delegation.",
      });
    }
    return options;
  }, [selectedStaffAccessRow]);

  const applyStaffAccessPreset = (preset: string) => {
    setSelectedStaffAccessPreset(preset);
    if (preset === STAFF_ACCESS_PRESET_CUSTOM) {
      return;
    }

    const enableAllResidents = buildResidentBooleanMap(activeResidents, true);
    const disableAllResidents = buildResidentBooleanMap(activeResidents, false);
    setSelectedResidentIds(activeResidents
      .map((resident) => String(resident?.residentId || "").trim())
      .filter(Boolean));

    if (preset === STAFF_ACCESS_PRESET_CAREGIVER) {
      setResidentBinderAccessById(disableAllResidents);
      setResidentAssessmentAccessById(disableAllResidents);
      setResidentClinicalCarePlanAccessById(disableAllResidents);
      setResidentDelegationAccessById(disableAllResidents);
      return;
    }

    if (preset === STAFF_ACCESS_PRESET_NURSE) {
      setResidentBinderAccessById(enableAllResidents);
      setResidentAssessmentAccessById(enableAllResidents);
      setResidentClinicalCarePlanAccessById(enableAllResidents);
      setResidentDelegationAccessById(enableAllResidents);
      return;
    }

    if (preset === STAFF_ACCESS_PRESET_MHP) {
      setResidentBinderAccessById(enableAllResidents);
      setResidentAssessmentAccessById(enableAllResidents);
      setResidentClinicalCarePlanAccessById(enableAllResidents);
      setResidentDelegationAccessById(disableAllResidents);
    }
  };

  const onSaveBulkShift = async () => {
    if (!selectedStaffIds.length) {
      return;
    }
    const shiftWindows = allTimeShiftAccessEnabled ? buildAllTimeShiftWindows() : buildShiftWindowsFromSchedule(dailyShiftSchedule);

    try {
      setSavingBulkShift(true);
      await backendAPIStore.init();
      await backendAPIStore.bulkUpdateHomeCaregiverLinkShift({
        homeId,
        caregiverIds: selectedStaffIds,
        shiftEnabled: shiftWindows.length > 0,
        shiftWindows,
        shiftTimezone: "America/Los_Angeles",
        mobileAccessEnabled,
      });
      toast.current?.show({
        severity: "success",
        summary: "Shift Updated",
        detail: `Saved shift settings for ${selectedStaffIds.length} staff member${selectedStaffIds.length === 1 ? "" : "s"}.`,
        life: 2500,
      });
      setShowBulkShiftDialog(false);
      await loadPageData();
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to save shift settings.", life: 3000 });
    } finally {
      setSavingBulkShift(false);
    }
  };

  const onSaveBulkAccess = async () => {
    if (!selectedStaffIds.length) {
      return;
    }
    try {
      setSavingBulkAccess(true);
      await backendAPIStore.init();
      const selectedRowsById = new Map(selectedRows.map((row) => [String(row?.caregiverId || "").trim(), row]));
      for (const caregiverId of selectedStaffIds) {
        const row = selectedRowsById.get(String(caregiverId || "").trim());
        if (!row) {
          continue;
        }
        const activeResidentIds = activeResidents
          .map((item) => String(item?.residentId || "").trim())
          .filter(Boolean);
        for (const residentId of activeResidentIds) {
          const resident = activeResidents.find((item) => String(item?.residentId || "").trim() === residentId);
          const assessmentEnabled = bulkResidentAssessmentAccessById[residentId] === true;
          const clinicalCarePlanEnabled = bulkResidentClinicalCarePlanAccessById[residentId] === true;
          const delegationEnabled = bulkResidentDelegationAccessById[residentId] === true;
          const binderEnabled = bulkResidentBinderAccessById[residentId] === true;
          const hasAnyClinicalAccess = binderEnabled || assessmentEnabled || clinicalCarePlanEnabled || delegationEnabled;

          if (row?.supportsCaregiverResidentAccess) {
            const existingShare = Array.isArray(row?.sharedResidents)
              ? row.sharedResidents.find((item: any) => String(item?.residentId || "").trim() === residentId)
              : null;
            if (selectedResidentIds.includes(residentId)) {
              await backendAPIStore.bulkAssignCaregiverResidentShares({
                homeId,
                caregiverIds: [caregiverId],
                residentIds: [residentId],
              });
            } else if (existingShare) {
              await backendAPIStore.deleteCaregiverResidentShare(homeId, caregiverId, residentId);
            }
          }

          if (row?.supportsClinicalResidentAccess) {
            const roleType = Array.isArray(row?.roleTypes)
              ? row.roleTypes.find((value: string) => CLINICAL_SHARE_ROLE_TYPES.includes(String(value || "").trim().toLowerCase()))
              : "";
            const includesBinderAccessByRole = roleType === ROLE_TYPE_NURSE || roleType === ROLE_TYPE_MHP;
            const existingClinicalShare = Array.isArray(row?.clinicalSharedResidents)
              ? row.clinicalSharedResidents.find((item: any) => String(item?.residentId || "").trim() === residentId)
              : null;
            const resolvedBinderEnabled = includesBinderAccessByRole ? true : binderEnabled;
            const resolvedHasAnyClinicalAccess = resolvedBinderEnabled || assessmentEnabled || clinicalCarePlanEnabled || delegationEnabled;
            if (roleType && resolvedHasAnyClinicalAccess) {
              if (existingClinicalShare?.linkId) {
                await backendAPIStore.updateResidentRoleLinkStatus({
                  linkId: String(existingClinicalShare.linkId).trim(),
                  status: "approved",
                  assessmentAccessEnabled: assessmentEnabled,
                  carePlanAccessEnabled: clinicalCarePlanEnabled,
                  delegationAccessEnabled: delegationEnabled,
                  binderAccessEnabled: resolvedBinderEnabled,
                  metadata: {
                    providerHomeId: String((resident as any)?.homeId || homeId || "").trim(),
                  },
                });
              } else {
                await backendAPIStore.createResidentRoleLink({
                  providerResidentId: residentId,
                  linkedActorId: caregiverId,
                  roleType,
                  assessmentAccessEnabled: assessmentEnabled,
                  carePlanAccessEnabled: clinicalCarePlanEnabled,
                  delegationAccessEnabled: delegationEnabled,
                  binderAccessEnabled: resolvedBinderEnabled,
                  metadata: {
                    providerHomeId: String((resident as any)?.homeId || homeId || "").trim(),
                  },
                });
              }
            } else if (existingClinicalShare?.linkId) {
              await backendAPIStore.updateResidentRoleLinkStatus({
                linkId: String(existingClinicalShare.linkId).trim(),
                status: "deleted",
              });
            }
          }
        }
      }
      toast.current?.show({
        severity: "success",
        summary: "Access Updated",
        detail: `Applied resident access to ${selectedStaffIds.length} staff member${selectedStaffIds.length === 1 ? "" : "s"}.`,
        life: 2500,
      });
      setShowBulkAccessDialog(false);
      await loadPageData();
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to update resident access.", life: 3000 });
    } finally {
      setSavingBulkAccess(false);
    }
  };

  const onSaveStaffAccess = async () => {
    const caregiverId = String(selectedStaffAccessRow?.caregiverId || "").trim();
    if (!homeId || !caregiverId) {
      return;
    }
    try {
      setSavingStaffAccess(true);
      await backendAPIStore.init();

      for (const resident of activeResidents) {
        const residentId = String(resident?.residentId || "").trim();
        if (!residentId) {
          continue;
        }
        const residentSelected = selectedResidentIds.includes(residentId);

        if (selectedStaffAccessRow?.supportsClinicalResidentAccess) {
          const roleType = Array.isArray(selectedStaffAccessRow?.roleTypes)
            ? selectedStaffAccessRow.roleTypes.find((value: string) => CLINICAL_SHARE_ROLE_TYPES.includes(String(value || "").trim().toLowerCase()))
            : "";
          if (!roleType) {
            throw new Error("This staff member does not have a nurse or MHP role.");
          }
          const includesBinderAccessByRole = roleType === ROLE_TYPE_NURSE || roleType === ROLE_TYPE_MHP;

          const assessmentEnabled = residentSelected && residentAssessmentAccessById[residentId] === true;
          const clinicalCarePlanEnabled = residentSelected && residentClinicalCarePlanAccessById[residentId] === true;
          const delegationEnabled = residentSelected && residentDelegationAccessById[residentId] === true;
          const binderEnabled = residentSelected && residentBinderAccessById[residentId] === true;
          const resolvedBinderEnabled = residentSelected && (includesBinderAccessByRole || binderEnabled);
          const hasAnyClinicalAccess = resolvedBinderEnabled || assessmentEnabled || clinicalCarePlanEnabled || delegationEnabled;
          const existingClinicalShare = Array.isArray(selectedStaffAccessRow?.clinicalSharedResidents)
            ? selectedStaffAccessRow.clinicalSharedResidents.find((item: any) => String(item?.residentId || "").trim() === residentId)
            : null;

          if (hasAnyClinicalAccess) {
            if (existingClinicalShare?.linkId) {
              await backendAPIStore.updateResidentRoleLinkStatus({
                linkId: String(existingClinicalShare.linkId).trim(),
                status: "approved",
                assessmentAccessEnabled: assessmentEnabled,
                carePlanAccessEnabled: clinicalCarePlanEnabled,
                delegationAccessEnabled: delegationEnabled,
                binderAccessEnabled: resolvedBinderEnabled,
                metadata: {
                  providerHomeId: String((resident as any)?.homeId || homeId || "").trim(),
                },
              });
            } else {
              await backendAPIStore.createResidentRoleLink({
                providerResidentId: residentId,
                linkedActorId: caregiverId,
                roleType,
                assessmentAccessEnabled: assessmentEnabled,
                carePlanAccessEnabled: clinicalCarePlanEnabled,
                delegationAccessEnabled: delegationEnabled,
                binderAccessEnabled: resolvedBinderEnabled,
                metadata: {
                  providerHomeId: String((resident as any)?.homeId || homeId || "").trim(),
                },
              });
            }
          } else if (existingClinicalShare?.linkId) {
            await backendAPIStore.updateResidentRoleLinkStatus({
              linkId: String(existingClinicalShare.linkId).trim(),
              status: "deleted",
            });
          }
        }

        if (selectedStaffAccessRow?.supportsCaregiverResidentAccess) {
          const existingShare = Array.isArray(selectedStaffAccessRow?.sharedResidents)
            ? selectedStaffAccessRow.sharedResidents.find((item: any) => String(item?.residentId || "").trim() === residentId)
            : null;

          if (residentSelected) {
            await backendAPIStore.bulkAssignCaregiverResidentShares({
              homeId,
              caregiverIds: [caregiverId],
              residentIds: [residentId],
            });
          } else if (existingShare) {
            await backendAPIStore.deleteCaregiverResidentShare(homeId, caregiverId, residentId);
          }
        }
      }

      toast.current?.show({
        severity: "success",
        summary: "Access Updated",
        detail: `Saved resident access for ${selectedStaffAccessRow?.name || "this staff member"}.`,
        life: 2500,
      });
      setShowStaffAccessDialog(false);
      await loadPageData();
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to update resident access.", life: 3000 });
    } finally {
      setSavingStaffAccess(false);
    }
  };

  const onToggleFacilityAdminAccess = async (row: any) => {
    const caregiverId = String(row?.caregiverId || "").trim();
    const linkId = String(row?.linkId || "").trim();
    if (!homeId || !caregiverId || !linkId) {
      return;
    }

    try {
      setSavingAdminAccessByCaregiverId((current) => ({ ...current, [caregiverId]: true }));
      await backendAPIStore.init();
      await backendAPIStore.updateHomeCaregiverLinkAdminAccess({
        homeId,
        caregiverId,
        linkId,
        facilityAdminAccess: !(row?.facilityAdminAccess === true),
      });
      toast.current?.show({
        severity: "success",
        summary: row?.facilityAdminAccess === true ? "Admin Access Removed" : "Admin Access Granted",
        detail: `${row?.name || "Staff member"} ${row?.facilityAdminAccess === true ? "no longer has" : "now has"} facility-level admin access.`,
        life: 2500,
      });
      await loadPageData();
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to update admin access.", life: 3000 });
    } finally {
      setSavingAdminAccessByCaregiverId((current) => ({ ...current, [caregiverId]: false }));
    }
  };

  const onArchiveStaff = async (row: any, nextActive: boolean) => {
    const caregiverId = String(row?.caregiverId || "").trim();
    const linkId = String(row?.linkId || "").trim();
    const staffName = String(row?.name || "this staff member").trim();
    if (!homeId || !caregiverId || !linkId) {
      return;
    }

    try {
      setRemovingStaffByCaregiverId((current) => ({ ...current, [caregiverId]: true }));
      await backendAPIStore.init();
      await backendAPIStore.updateHomeCaregiverLinkStatus({
        homeId,
        caregiverId,
        linkId,
        active: nextActive,
      });
      toast.current?.show({
        severity: "success",
        summary: nextActive ? "Staff Restored" : "Staff Archived",
        detail: nextActive
          ? `${staffName} was restored to active staff for this ${facilityLowerLabel}.`
          : `${staffName} was moved to archived staff for this ${facilityLowerLabel}, and their shift schedule was cleared.`,
        life: 2500,
      });
      setSelectedStaffIds((current) => current.filter((value) => value !== caregiverId));
      setRemoveStaffDialogRow(null);
      await loadPageData();
    } catch (error: any) {
      if (isStaffLimitReachedError(error)) {
        setShowStaffUnlockDialog(true);
        return;
      }
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || `Unable to ${nextActive ? "restore" : "archive"} staff.`, life: 3000 });
    } finally {
      setRemovingStaffByCaregiverId((current) => ({ ...current, [caregiverId]: false }));
    }
  };

  const onDeleteStaff = async (row: any) => {
    const caregiverId = String(row?.caregiverId || "").trim();
    const linkId = String(row?.linkId || "").trim();
    const staffName = String(row?.name || "this staff member").trim();
    if (!homeId || !caregiverId || !linkId) {
      return;
    }

    try {
      setRemovingStaffByCaregiverId((current) => ({ ...current, [caregiverId]: true }));
      await backendAPIStore.init();
      await backendAPIStore.deleteHomeCaregiverLink(linkId, homeId);
      toast.current?.show({
        severity: "success",
        summary: "Staff Deleted",
        detail: `${staffName} and all linked resident access were permanently removed from this ${facilityLowerLabel}.`,
        life: 3000,
      });
      setSelectedStaffIds((current) => current.filter((value) => value !== caregiverId));
      setDeleteStaffDialogRow(null);
      await loadPageData();
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to delete staff.",
        life: 3000,
      });
    } finally {
      setRemovingStaffByCaregiverId((current) => ({ ...current, [caregiverId]: false }));
    }
  };

  const openStaffCredentials = (row: any) => {
    setCredentialDialogRow(row);
    setShowCredentialValidationErrors(false);
    setCredentialDialogValue(normalizeStaffDelegationRequirements(row?.delegationRequirements || row?.staff?.delegationRequirements));
  };

  const saveStaffCredentials = async () => {
    const row = credentialDialogRow;
    const staffId = String(row?.caregiverId || "").trim();
    if (!staffId) return;
    setShowCredentialValidationErrors(true);
    const validationMessage = getStaffDelegationRequirementsValidationMessage(credentialDialogValue);
    if (validationMessage) {
      toast.current?.show({ severity: "error", summary: "Validation required", detail: validationMessage, life: 4500 });
      return;
    }
    try {
      setSavingStaffCredentials(true);
      await backendAPIStore.init();
      await backendAPIStore.saveStaffAccount({
        roleType: String(row?.roleTypes?.[0] || ROLE_TYPE_CAREGIVER),
        staffId,
        name: String(row?.name || "Staff").trim(),
        email: String(row?.email || "").trim().toLowerCase(),
        staffDetails: credentialDialogValue,
      });
      toast.current?.show({ severity: "success", summary: "Credentials saved", detail: "Staff credential details were updated.", life: 2500 });
      setCredentialDialogRow(null);
      await loadPageData();
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to save staff credentials.", life: 3500 });
    } finally {
      setSavingStaffCredentials(false);
    }
  };

  if (userRoleType && userRoleType !== ROLE_TYPE_PROVIDER && !isAdminUser) {
    return <Unauthorized />;
  }

  return (
    <div style={{ padding: mobile ? "1rem" : "1.25rem" }}>
      <Toast ref={toast} />
      <Tooltip ref={staffResidentTooltip} target=".staff-resident-hover-target, .facility-worker-credentials-disabled-tooltip" position="right" />
      <AddCaregiverModal
        homeId={homeId}
        residents={activeResidents}
        isOpen={showQuickAddDialog}
        toggleModal={setShowQuickAddDialog}
        onAdded={loadPageData}
      />
      <Dialog
        visible={Boolean(credentialDialogRow)}
        modal
        header={renderSurfaceHeader("Staff Credentials", "Update delegation credentials and training verification.", "pi pi-id-card")}
        style={mobile ? { width: "96vw" } : { width: "54rem", maxWidth: "95vw" }}
        onHide={() => !savingStaffCredentials && setCredentialDialogRow(null)}
        footer={<div className="flex justify-content-between w-full"><Button type="button" label="Cancel" severity="secondary" outlined rounded disabled={savingStaffCredentials} onClick={() => setCredentialDialogRow(null)} /><Button type="button" label="Save Credentials" icon="pi pi-save" severity="warning" rounded loading={savingStaffCredentials} onClick={() => void saveStaffCredentials()} /></div>}
      >
        {credentialDialogRow ? (
          <>
            {credentialDialogRow.delegationCredentialStatus === "missing" ? (
              <div style={{ marginBottom: "1rem", padding: "0.8rem 1rem", border: "1px solid #fecaca", borderRadius: "10px", background: "#fef2f2", color: "#b91c1c", fontWeight: 650 }}>
                Credentials are missing. Fill all the credential and training details below.
              </div>
            ) : credentialDialogRow.delegationCredentialStatus === "expired" ? (
              <div style={{ marginBottom: "1rem", padding: "0.8rem 1rem", border: "1px solid #fecaca", borderRadius: "10px", background: "#fef2f2", color: "#b91c1c", fontWeight: 650 }}>
                A credential has expired. The expired field is highlighted in red below.
              </div>
            ) : null}
            <StaffDelegationRequirementsForm value={credentialDialogValue} onChange={setCredentialDialogValue} showValidationErrors={showCredentialValidationErrors} name={credentialDialogRow.name} email={credentialDialogRow.email} documentUpload={<StaffCredentialDocumentUpload
              residentIds={residents.map((resident) => String(resident?.residentId || "").trim()).filter(Boolean)}
              staffName={credentialDialogRow.name}
              staffId={String(credentialDialogRow.staffId || credentialDialogRow.userId || "")}
              email={credentialDialogRow.email}
              onExtracted={(details) => setCredentialDialogValue((current) => ({ ...current, ...details }))}
            />} />
          </>
        ) : null}
      </Dialog>
      <HiringRequestDialog
        visible={showHiringIntakeDialog}
        onHide={() => setShowHiringIntakeDialog(false)}
        initialFacilityType={String(homeProfile?.facilityType || "afh")}
      />

      {/* Legacy inline hiring form retained in source for reference; the shared dialog above is the active entry point.
      <Card className="mb-3" style={{ display: "none" }}>
        <div className="flex justify-content-between align-items-center gap-3 flex-wrap">
          {renderSurfaceHeader("Hiring Requirements", "Send a role-specific Binder checklist.", "pi pi-send")}
          <Button type="button" label="Create Hiring Request" icon="pi pi-plus" severity="warning" rounded onClick={() => { setHiringDialogStep(1); setShowHiringRequestDialog(true); }} />
        </div>
      </Card>

      <Dialog
        visible={showHiringRequestDialog}
        modal
        header={renderSurfaceHeader("Create Hiring Request", "Select certifications and a reusable questionnaire.", "pi pi-send")}
        style={mobile ? { width: "95vw" } : { width: "48rem", maxWidth: "95vw" }}
        onHide={() => {
          if (!sendingHiringRequest) {
      setShowHiringRequestDialog(false);
      setHiringDialogStep(1);
            setHiringDialogStep(1);
          }
        }}
      >
        {hiringDialogStep === 1 ? <div className="flex flex-column gap-3">
          <div className="surface-50 border-1 surface-border border-round p-3">
            <div className="flex align-items-center gap-2 mb-1"><i className="pi pi-send" style={{ color: "#d97706" }} /><strong>Send hiring requirements</strong></div>
            <small className="text-color-secondary">Choose the role, select the documents or certifications to request, and include questions for the applicant.</small>
          </div>
          <div className="surface-card border-1 surface-border border-round p-3">
            <div className="flex align-items-center gap-2 mb-3"><i className="pi pi-user" style={{ color: "#d97706" }} /><strong>Applicant details</strong></div>
            <div className="grid">
            <div className="col-12">
              <label className="block mb-2">Recipient email</label>
              <InputText value={hiringRecipientEmail} onChange={(event) => setHiringRecipientEmail(event.target.value)} className="w-full" placeholder="name@example.com" />
            </div>
            <div className="col-12">
              <label className="block mb-2">Recipient name</label>
              <InputText value={hiringRecipientName} onChange={(event) => setHiringRecipientName(event.target.value)} className="w-full" />
            </div>
            <div className="col-12 md:col-6">
              <label className="block mb-2">Role</label>
              <Dropdown value={hiringRole} options={[{ label: "Caregiver", value: "caregiver" }, { label: "Nurse", value: "nurse" }, { label: "MHP", value: "mhp" }]} onChange={(event) => onHiringRoleChange(event.value)} className="w-full" />
            </div>
            <div className="col-12 md:col-6">
              <label className="block mb-2">Facility type</label>
              <Dropdown value={hiringFacilityType} options={hiringFacilityTypeOptions} onChange={(event) => setHiringFacilityType(event.value)} className="w-full" placeholder="Select facility type" />
              <small className="block mt-1 text-color-secondary">This helps apply the appropriate role and facility requirements.</small>
            </div>
            </div>
          </div>
          <div className="surface-card border-1 surface-border border-round p-3">
            <div className="flex justify-content-between align-items-center gap-2 mb-2">
              <div><div className="flex align-items-center gap-2"><i className="pi pi-verified" style={{ color: "#d97706" }} /><strong>Recommended certifications</strong></div><small className="text-color-secondary">Select the items the applicant should provide.</small></div>
              <Tag value={`${hiringCertifications.length} selected`} severity={hiringCertifications.length ? "warning" : "danger"} rounded />
            </div>
            <div className="col-12">
              <div className="flex justify-content-between align-items-center mb-2">
                <label>Recommended {hiringRole === "mhp" ? "MHP" : hiringRole.charAt(0).toUpperCase() + hiringRole.slice(1)} certifications</label>
                <div className="flex gap-2">
                  <Button type="button" label="All" className="p-button-text p-button-sm" onClick={() => setHiringCertifications(hiringCertificationOptions.map((option) => option.value))} />
                  <Button type="button" label="Clear" className="p-button-text p-button-sm" onClick={() => setHiringCertifications([])} />
                </div>
              </div>
              <MultiSelect value={hiringCertifications} options={hiringCertificationOptions} onChange={(event) => setHiringCertifications(event.value || [])} className="w-full" display="chip" placeholder="Select certifications" />
            </div>
          </div>
          <div className="surface-card border-1 surface-border border-round p-3">
            <div className="flex align-items-center gap-2 mb-1"><i className="pi pi-question-circle" style={{ color: "#d97706" }} /><strong>Questionnaire</strong></div>
            <small className="text-color-secondary">Use the recommended questions or choose a saved questionnaire, then edit it as needed.</small>
            <div className="col-12">
              {savedHiringQuestionnaireTemplates.length > 0 && (
                <div className="flex gap-2 mb-2">
                  <Dropdown value={selectedHiringQuestionnaireTemplateId} options={savedHiringQuestionnaireTemplates} optionLabel="name" optionValue="templateId" onChange={(event) => setSelectedHiringQuestionnaireTemplateId(event.value)} className="flex-1" placeholder="Choose saved questionnaire" />
                  <Button type="button" label="Use" icon="pi pi-check" className="p-button-outlined" onClick={applyHiringQuestionnaireTemplate} disabled={!selectedHiringQuestionnaireTemplate} />
                  <Button type="button" icon="pi pi-trash" className="p-button-outlined p-button-danger" tooltip="Remove saved questionnaire" onClick={() => void removeHiringQuestionnaireTemplate()} disabled={!selectedHiringQuestionnaireTemplate} />
                </div>
              )}
              <InputTextarea value={hiringQuestions} onChange={(event) => setHiringQuestions(event.target.value)} rows={5} autoResize className="w-full" placeholder="One question per line" />
              <div className="flex gap-2 mt-2 flex-wrap">
                <Button type="button" label="Use Recommended Questions" icon="pi pi-list" className="p-button-outlined" onClick={() => { setHiringQuestions(RECOMMENDED_ROLE_QUESTIONS[hiringRole] || RECOMMENDED_CAREGIVER_QUESTIONS); setHiringQuestionnaireTemplateName(`Recommended ${hiringRole === "mhp" ? "MHP" : hiringRole.charAt(0).toUpperCase() + hiringRole.slice(1)} Screening`); setSelectedHiringQuestionnaireTemplateId(""); }} />
                <InputText value={hiringQuestionnaireTemplateName} onChange={(event) => setHiringQuestionnaireTemplateName(event.target.value)} className="flex-1" placeholder="Questionnaire name to save" />
                <Button type="button" label={selectedHiringQuestionnaireTemplate ? "Update" : "Save"} icon="pi pi-save" tooltip="Save the questionnaire for future use" tooltipOptions={{ position: "top" }} className="p-button-outlined" onClick={() => void saveHiringQuestionnaireTemplate()} />
              </div>
            </div>
          </div>
          <div className="flex justify-content-end">
            <Button type="button" label="Preview Email" icon="pi pi-eye" severity="warning" onClick={previewHiringRequest} />
          </div>
        </div> : <div className="flex flex-column gap-3">
          <div className="surface-100 border-round p-3">
            <div className="font-semibold mb-2">AFH Hub hiring requirements</div>
            <div className="text-color-secondary">To: {hiringRecipientName.trim() || hiringRecipientEmail.trim()} &lt;{hiringRecipientEmail.trim()}&gt;</div>
            <div className="text-color-secondary">Role: {hiringRole === "mhp" ? "MHP" : hiringRole.charAt(0).toUpperCase() + hiringRole.slice(1)}</div>
          </div>
          <div><div className="font-semibold mb-2">Requested certifications</div><ul className="mt-0">{hiringCertifications.map((certification) => <li key={certification}>{certification}</li>)}</ul></div>
          <div><div className="font-semibold mb-2">Questionnaire</div>{hiringQuestions.trim() ? <ol className="mt-0">{hiringQuestions.split("\n").map((question) => question.trim()).filter(Boolean).map((question, index) => <li key={`${question}-${index}`}>{question}</li>)}</ol> : <span className="text-color-secondary">No questions added.</span>}</div>
          <div className="surface-50 border-1 surface-border border-round p-3 text-sm"><i className="pi pi-info-circle mr-2" style={{ color: "#d97706" }} />Once the applicant shares the Binder, you will receive an email and can review it from the <a href={`${window.location.origin}${RouterAddress.SHARED_CONTENT_PAGE}`} target="_blank" rel="noreferrer">Shared Content page</a>.</div>
          <div className="surface-100 border-round p-3 text-sm"><div className="font-semibold mb-2">What the recipient will do</div>1. Create or sign in to an AFH Hub account as the selected role.<br />2. Create a Binder, add the requested documents, and complete the questionnaire.<br />3. Share the Binder back with you for review.</div>
          <div className="flex justify-content-between">
            <Button type="button" label="Back" icon="pi pi-arrow-left" className="p-button-text" onClick={() => setHiringDialogStep(1)} disabled={sendingHiringRequest} />
            <Button type="button" label={sendingHiringRequest ? "Sending..." : "Send Request"} icon="pi pi-send" severity="warning" onClick={() => void onSendHiringRequest()} disabled={sendingHiringRequest} />
          </div>
        </div>}
      </Dialog> */}

      <Dialog
        visible={showStaffUnlockDialog}
        modal
        header={renderSurfaceHeader("More Staff Access Needed", "Choose how you want to continue adding staff.", "pi pi-users")}
        style={mobile ? { width: "92vw" } : { width: "32rem", maxWidth: "90vw" }}
        onHide={() => setShowStaffUnlockDialog(false)}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <div style={{ color: "#596F80", lineHeight: 1.7 }}>
            You have reached the maximum number of staff members included right now. Archive a staff member or update Billing to add more active staff.
          </div>
          <div className="flex justify-content-end gap-2">
            <Button
              type="button"
              label="Close"
              severity="secondary"
              text
              rounded
              onClick={() => setShowStaffUnlockDialog(false)}
            />
            <Button
              type="button"
              label="Billing"
              severity="secondary"
              outlined
              rounded
              onClick={() => {
                setShowStaffUnlockDialog(false);
                navigate(`${RouterAddress.BILLING}?tab=subscription`);
              }}
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={!!removeStaffDialogRow}
        header={removeStaffDialogRow?.active === false ? "Restore Staff" : "Archive Staff"}
        style={mobile ? { width: "95vw" } : { width: "32rem", maxWidth: "90vw" }}
        modal
        onHide={() => {
          if (!removeStaffDialogRow || !removingStaffByCaregiverId[String(removeStaffDialogRow?.caregiverId || "").trim()]) {
            setRemoveStaffDialogRow(null);
          }
        }}
      >
        <div className="flex flex-column gap-3">
          <div style={{ color: "#475569", lineHeight: 1.65 }}>
            {removeStaffDialogRow?.active === false
              ? `Restore ${String(removeStaffDialogRow?.name || "this staff member").trim()} to the active staff list for this ${facilityLowerLabel}?`
              : `Archive ${String(removeStaffDialogRow?.name || "this staff member").trim()} from the active staff list for this ${facilityLowerLabel}? Their linked resident access will be turned off and their saved shift times will be cleared until the staff member is restored.`}
          </div>
          <div className="flex justify-content-end gap-2">
            <Button
              type="button"
              label="Cancel"
              severity="secondary"
              rounded
              outlined
              disabled={Boolean(removingStaffByCaregiverId[String(removeStaffDialogRow?.caregiverId || "").trim()])}
              onClick={() => setRemoveStaffDialogRow(null)}
            />
            <Button
              type="button"
              label={removeStaffDialogRow?.active === false ? "Restore Staff" : "Archive Staff"}
              severity={removeStaffDialogRow?.active === false ? "success" : "danger"}
              rounded
              loading={Boolean(removingStaffByCaregiverId[String(removeStaffDialogRow?.caregiverId || "").trim()])}
              onClick={() => {
                if (removeStaffDialogRow) {
                  void onArchiveStaff(removeStaffDialogRow, removeStaffDialogRow?.active === false);
                }
              }}
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={!!deleteStaffDialogRow}
        header="Delete Staff"
        style={mobile ? { width: "95vw" } : { width: "32rem", maxWidth: "90vw" }}
        modal
        onHide={() => {
          if (!deleteStaffDialogRow || !removingStaffByCaregiverId[String(deleteStaffDialogRow?.caregiverId || "").trim()]) {
            setDeleteStaffDialogRow(null);
          }
        }}
      >
        <div className="flex flex-column gap-3">
          <div style={{ color: "#475569", lineHeight: 1.65 }}>
            {`Permanently delete ${String(deleteStaffDialogRow?.name || "this staff member").trim()} from this ${facilityLowerLabel}? This will remove the staff link, shared resident access, and related shared resident links for this home.`}
          </div>
          <div style={{ color: "#b45309", fontSize: "0.92rem", lineHeight: 1.6 }}>
            This action cannot be undone.
          </div>
          <div className="flex justify-content-end gap-2">
            <Button
              type="button"
              label="Cancel"
              severity="secondary"
              rounded
              outlined
              disabled={Boolean(removingStaffByCaregiverId[String(deleteStaffDialogRow?.caregiverId || "").trim()])}
              onClick={() => setDeleteStaffDialogRow(null)}
            />
            <Button
              type="button"
              label="Delete Staff"
              severity="danger"
              rounded
              loading={Boolean(removingStaffByCaregiverId[String(deleteStaffDialogRow?.caregiverId || "").trim()])}
              onClick={() => {
                if (deleteStaffDialogRow) {
                  void onDeleteStaff(deleteStaffDialogRow);
                }
              }}
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={!!payrollCloseoutDialogAction}
        header={payrollCloseoutDialogAction === "finalize_payroll_period" ? `Finalize ${payrollRangeLabel} Payroll` : `Reopen ${payrollRangeLabel} Payroll`}
        style={mobile ? { width: "95vw" } : { width: "34rem", maxWidth: "90vw" }}
        modal
        onHide={closePayrollCloseoutDialog}
      >
        <div className="flex flex-column gap-3">
          <div style={{ color: "#475569", lineHeight: 1.65 }}>
            {payrollCloseoutDialogAction === "finalize_payroll_period"
              ? `Finalize this ${attendanceRange} payroll period to lock attendance edits.`
              : `Reopen this ${attendanceRange} payroll period so staff time can be corrected again.`}
          </div>
          <div>
            <div style={{ color: "#64748b", fontSize: "0.8rem", fontWeight: 700, marginBottom: "0.35rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              {payrollCloseoutDialogAction === "finalize_payroll_period" ? "Approval Note" : "Reopen Note"}
            </div>
            <InputTextarea
              value={payrollCloseoutNoteInput}
              rows={4}
              className="w-full"
              onChange={(event) => setPayrollCloseoutNoteInput(String(event.target.value || ""))}
              placeholder={payrollCloseoutDialogAction === "finalize_payroll_period" ? "Optional payroll approval note." : "Optional reason for reopening payroll."}
            />
          </div>
          <div className="flex justify-content-end gap-2">
            <Button type="button" label="Cancel" severity="secondary" rounded outlined disabled={savingPayrollCloseout} onClick={closePayrollCloseoutDialog} />
            <Button
              type="button"
              label={payrollCloseoutDialogAction === "finalize_payroll_period" ? `Finalize ${payrollRangeLabel} Payroll` : `Reopen ${payrollRangeLabel} Payroll`}
              severity="warning"
              rounded
              loading={savingPayrollCloseout}
              onClick={() => {
                if (payrollCloseoutDialogAction) {
                  void onTogglePayrollCloseout(payrollCloseoutDialogAction);
                }
              }}
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={!!reviewWorkflowDialogRow}
        header={reviewWorkflowAction === "resolve_review_item" ? "Resolve Review Item" : "Reopen Review Item"}
        style={mobile ? { width: "95vw" } : { width: "34rem", maxWidth: "90vw" }}
        modal
        onHide={closeReviewWorkflowDialog}
      >
        <div className="flex flex-column gap-3">
          <div style={{ color: "#475569", lineHeight: 1.65 }}>
            {reviewWorkflowDialogRow
              ? `${reviewWorkflowDialogRow?.caregiverName || "This staff member"}: ${reviewWorkflowDialogRow?.issueSummary || "Needs review"}`
              : ""}
          </div>
          {reviewWorkflowDialogRow?.issueDetail ? (
            <div style={{ padding: "0.85rem 0.95rem", borderRadius: "12px", border: "1px solid #e2e8f0", background: "#f8fafc", color: "#475569" }}>
              {String(reviewWorkflowDialogRow?.issueDetail || "").trim()}
            </div>
          ) : null}
          <div>
            <div style={{ color: "#64748b", fontSize: "0.8rem", fontWeight: 700, marginBottom: "0.35rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              {reviewWorkflowAction === "resolve_review_item" ? "Resolution Note" : "Reopen Note"}
            </div>
            <InputTextarea
              value={reviewWorkflowNote}
              rows={4}
              className="w-full"
              onChange={(event) => setReviewWorkflowNote(String(event.target.value || ""))}
              placeholder={reviewWorkflowAction === "resolve_review_item" ? "Required note explaining how this issue was handled." : "Optional reason for reopening this item."}
            />
          </div>
          <div className="flex justify-content-end gap-2">
            <Button type="button" label="Cancel" severity="secondary" rounded outlined disabled={Boolean(updatingReviewItemById[String(reviewWorkflowDialogRow?.reviewItemId || "").trim()])} onClick={closeReviewWorkflowDialog} />
            <Button
              type="button"
              label={reviewWorkflowAction === "resolve_review_item" ? "Resolve Item" : "Reopen Item"}
              severity="warning"
              rounded
              loading={Boolean(updatingReviewItemById[String(reviewWorkflowDialogRow?.reviewItemId || "").trim()])}
              onClick={() => { void onSaveReviewWorkflow(); }}
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={!!compensationDialogRow}
        header="Pay & Leave"
        style={mobile ? { width: "95vw" } : { width: "34rem", maxWidth: "90vw" }}
        modal
        onHide={() => !savingCompensation && setCompensationDialogRow(null)}
      >
        <div className="flex flex-column gap-3">
          <div style={{ color: "#475569", lineHeight: 1.65 }}>
            {`Update provider-managed pay and leave balances for ${String(compensationDialogRow?.name || "this staff member").trim()}.`}
          </div>
          {minimumWageAmount > 0 ? (
            <div
              style={{
                border: "1px solid #fde68a",
                background: "#fffbeb",
                borderRadius: "14px",
                padding: "0.85rem 0.95rem",
                color: "#92400e",
                lineHeight: 1.55,
              }}
            >
              <div style={{ fontSize: "0.76rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase" }}>Minimum Wage</div>
              <div style={{ marginTop: "0.2rem", fontWeight: 800, color: "#78350f" }}>
                {formatCurrency(minimumWageAmount)}/hr
              </div>
              <div style={{ marginTop: "0.2rem", fontSize: "0.9rem" }}>
                Effective {minimumWageEffectiveDate || "current rule"}.
                Keep staff pay at or above this floor unless the rate is intentionally left unconfigured.
              </div>
            </div>
          ) : null}
          <div className="grid formgrid">
            <div className="col-12">
              <div style={{ color: "#64748b", fontSize: "0.8rem", fontWeight: 700, marginBottom: "0.35rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Hourly Rate</div>
              <InputNumber value={hourlyRateInput} mode="currency" currency="USD" locale="en-US" min={0} className="w-full" inputClassName="w-full" onValueChange={(event) => setHourlyRateInput(event.value ?? 0)} />
              {hourlyRateBelowMinimum ? (
                <div style={{ marginTop: "0.45rem", color: "#b45309", fontWeight: 700 }}>
                  This rate is below the current wage floor of {formatCurrency(minimumWageAmount)}/hr.
                </div>
              ) : null}
            </div>
            <div className="col-12">
              <div style={{ color: "#64748b", fontSize: "0.8rem", fontWeight: 700, marginBottom: "0.35rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Vacation Hours</div>
              <InputNumber value={vacationBalanceInput} min={0} minFractionDigits={0} maxFractionDigits={2} onValueChange={(event) => setVacationBalanceInput(event.value ?? 0)} />
            </div>
            <div className="col-12">
              <div style={{ color: "#64748b", fontSize: "0.8rem", fontWeight: 700, marginBottom: "0.35rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Leave Hours</div>
              <InputNumber value={leaveBalanceInput} min={0} minFractionDigits={0} maxFractionDigits={2} onValueChange={(event) => setLeaveBalanceInput(event.value ?? 0)} />
            </div>
            <div className="col-12 md:col-6">
              <div style={{ color: "#64748b", fontSize: "0.8rem", fontWeight: 700, marginBottom: "0.35rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Effective Date</div>
              <InputText type="date" value={compensationEffectiveDateInput} className="w-full" onChange={(event) => setCompensationEffectiveDateInput(String(event.target.value || ""))} />
            </div>
            <div className="col-12">
              <div style={{ color: "#64748b", fontSize: "0.8rem", fontWeight: 700, marginBottom: "0.35rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Provider Note</div>
              <InputTextarea value={compensationNoteInput} rows={3} className="w-full" onChange={(event) => setCompensationNoteInput(String(event.target.value || ""))} placeholder="Optional note for this pay or leave update." />
            </div>
          </div>
          <div className="flex justify-content-between gap-2 flex-wrap">
            <Button type="button" label="View Pay History" severity="warning" outlined rounded onClick={() => setCompensationHistoryDialogRow(compensationDialogRow)} />
            <div className="flex justify-content-end gap-2">
              <Button type="button" label="Cancel" severity="secondary" rounded outlined disabled={savingCompensation} onClick={() => setCompensationDialogRow(null)} />
              <Button type="button" label="Save" rounded loading={savingCompensation} disabled={hourlyRateBelowMinimum} onClick={() => { void onSaveCompensation(); }} />
            </div>
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={!!compensationHistoryDialogRow}
        header="Pay History"
        style={mobile ? { width: "95vw" } : { width: "46rem", maxWidth: "92vw" }}
        modal
        onHide={() => setCompensationHistoryDialogRow(null)}
      >
        <div className="flex flex-column gap-3">
          <div style={{ color: "#475569", lineHeight: 1.6 }}>
            {`Review provider pay and leave updates for ${String(compensationHistoryDialogRow?.name || "this staff member").trim()}.`}
          </div>
          {Array.isArray(compensationHistoryDialogRow?.staff?.compensationHistory) && compensationHistoryDialogRow.staff.compensationHistory.length ? (
            <DataTable
              value={[...compensationHistoryDialogRow.staff.compensationHistory].sort((left: any, right: any) => new Date(String(right?.changedAt || "")).getTime() - new Date(String(left?.changedAt || "")).getTime())}
              rows={6}
              paginator={compensationHistoryDialogRow.staff.compensationHistory.length > 6}
              emptyMessage="No pay history yet."
            >
              <Column field="effectiveDate" header="Effective Date" />
              <Column field="changedAt" header="Changed At" body={(rowData: any) => formatAttendanceDateTime(rowData?.changedAt)} />
              <Column field="nextHourlyRate" header="Rate" body={(rowData: any) => `${formatCurrency(rowData?.previousHourlyRate)}/hr -> ${formatCurrency(rowData?.nextHourlyRate)}/hr`} />
              <Column field="vacation" header="Vacation" body={(rowData: any) => `${Number(rowData?.previousVacationBalanceHours || 0).toFixed(2)} -> ${Number(rowData?.nextVacationBalanceHours || 0).toFixed(2)} hrs`} />
              <Column field="leave" header="Leave" body={(rowData: any) => `${Number(rowData?.previousLeaveBalanceHours || 0).toFixed(2)} -> ${Number(rowData?.nextLeaveBalanceHours || 0).toFixed(2)} hrs`} />
              <Column field="changedBy" header="Changed By" />
              <Column field="note" header="Note" body={(rowData: any) => <div style={{ maxWidth: "14rem", color: "#475569", lineHeight: 1.45 }}>{String(rowData?.note || "No note recorded.").trim()}</div>} />
            </DataTable>
          ) : (
            <div style={{ color: "#64748b" }}>No pay history yet for this staff member.</div>
          )}
        </div>
      </Dialog>

      <Dialog
        visible={!!attendanceCorrectionDialogRow}
        header="Attendance Correction"
        style={mobile ? { width: "95vw" } : { width: "48rem", maxWidth: "92vw" }}
        modal
        onHide={closeAttendanceCorrectionDialog}
      >
        {attendanceCorrectionDialogRow ? (
          <div className="flex flex-column gap-3">
            <div style={{ color: "#475569", lineHeight: 1.65 }}>
              {`Update a shift for ${String(attendanceCorrectionDialogRow?.caregiverName || attendanceCorrectionDialogRow?.name || "this staff member").trim()}. Pick a date within the last 2 payroll months. A note is required, and the staff member will be emailed automatically.`}
            </div>
            <div style={{ border: "1px solid #fde68a", borderRadius: "14px", background: "#fffbeb", padding: "0.9rem 1rem", color: "#78350f" }}>
              <div style={{ fontSize: "0.76rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase" }}>Target Shift</div>
              <div style={{ marginTop: "0.4rem", lineHeight: 1.6 }}>
                <div><strong>Date:</strong> {formatDateKeyForDisplay(attendanceCorrectionDateInput)}</div>
                <div><strong>Clock In:</strong> {formatAttendanceDateTime(selectedAttendanceCorrectionTarget?.clockInAt)}</div>
                <div><strong>Clock Out:</strong> {formatAttendanceDateTime(selectedAttendanceCorrectionTarget?.clockOutAt)}</div>
                <div><strong>Worked Hours:</strong> {Number(selectedAttendanceCorrectionTarget?.workedHours || 0).toFixed(2)} hrs</div>
              </div>
            </div>
            <div className="grid" style={{ rowGap: "0.4rem" }}>
              <div className="col-12 md:col-12">
                <div style={{ color: "#64748b", fontSize: "0.8rem", fontWeight: 700, marginBottom: "0.35rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Correction Date</div>
                <Calendar
                  value={parseDateKeyToCalendarValue(attendanceCorrectionDateInput)}
                  minDate={parseDateKeyToCalendarValue(attendanceCorrectionWindowBounds.earliestDateKey)}
                  maxDate={parseDateKeyToCalendarValue(attendanceCorrectionWindowBounds.latestDateKey)}
                  dateFormat="yy-mm-dd"
                  showIcon
                  appendTo="self"
                  touchUI={mobile}
                  className="w-full"
                  onChange={(event) => {
                    const nextDate = event.value ? getLosAngelesDateKeyForInput(event.value) : "";
                    setAttendanceCorrectionDateInput(nextDate);
                    const datedEntries = getAttendanceCorrectionEntriesForDate(attendanceCorrectionDialogRow, nextDate);
                    setAttendanceCorrectionSelectedAttendanceId(String(datedEntries[0]?.attendanceId || ""));
                  }}
                />
                <div style={{ color: "#94a3b8", fontSize: "0.75rem", marginTop: "0.35rem" }}>
                  {`Allowed range: ${formatDateKeyForDisplay(attendanceCorrectionWindowBounds.earliestDateKey)} to ${formatDateKeyForDisplay(attendanceCorrectionWindowBounds.latestDateKey)}`}
                </div>
              </div>
              <div className="col-12 md:col-12">
                <div style={{ color: "#64748b", fontSize: "0.8rem", fontWeight: 700, marginBottom: "0.35rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Shift Entry</div>
                <Dropdown
                  value={attendanceCorrectionSelectedAttendanceId}
                  options={attendanceCorrectionDateEntries.map((item: any) => ({
                    label: `${formatAttendanceDateTime(item?.clockInAt)}${item?.clockOutAt ? ` -> ${formatAttendanceDateTime(item?.clockOutAt)}` : " -> Open shift"}`,
                    value: String(item?.attendanceId || "").trim(),
                  }))}
                  optionLabel="label"
                  optionValue="value"
                  onChange={(event) => setAttendanceCorrectionSelectedAttendanceId(String(event.value || ""))}
                  className="w-full"
                  placeholder={attendanceCorrectionDateInput ? "Select shift entry" : "Select a date first"}
                  disabled={!attendanceCorrectionDateEntries.length}
                  appendTo="self"
                />
              </div>
              {selectedAttendanceCorrectionTarget ? (
                <>
                  <div className="col-12">
                    <div style={{ color: "#64748b", fontSize: "0.8rem", fontWeight: 700, marginBottom: "0.35rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Correction Action</div>
                    <Dropdown
                      value={attendanceCorrectionAction}
                      options={[
                        ...(String(selectedAttendanceCorrectionTarget?.status || attendanceCorrectionDialogRow?.currentStatus || "").trim() === "clocked_in"
                          && !String(selectedAttendanceCorrectionTarget?.clockOutAt || "").trim()
                          ? [{ label: "Clock Out Now", value: "clock_out_now" }]
                          : []),
                        { label: "Adjust Worked Hours", value: "adjust_hours" },
                        { label: "Edit Clock In/Out", value: "edit_shift_times" },
                      ]}
                      optionLabel="label"
                      optionValue="value"
                      onChange={(event) => setAttendanceCorrectionAction(event.value)}
                      className="w-full"
                      appendTo="self"
                    />
                  </div>
                  <div className="col-12">
                    <div style={{ color: "#64748b", fontSize: "0.8rem", fontWeight: 700, marginBottom: "0.35rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Reason</div>
                    <Dropdown
                      value={attendanceCorrectionReason}
                      options={ATTENDANCE_CORRECTION_REASON_OPTIONS}
                      optionLabel="label"
                      optionValue="value"
                      onChange={(event) => setAttendanceCorrectionReason(String(event.value || ""))}
                      className="w-full"
                      placeholder="Select a reason"
                      appendTo="self"
                    />
                  </div>
                  {attendanceCorrectionAction === "adjust_hours" ? (
                    <div className="col-12">
                      <div style={{ color: "#64748b", fontSize: "0.8rem", fontWeight: 700, marginBottom: "0.35rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Corrected Worked Hours</div>
                      <InputNumber
                        value={attendanceCorrectionHours}
                        min={0}
                        minFractionDigits={0}
                        maxFractionDigits={2}
                        className="w-full"
                        onValueChange={(event) => setAttendanceCorrectionHours(event.value ?? 0)}
                      />
                    </div>
                  ) : null}
                  {attendanceCorrectionAction === "edit_shift_times" ? (
                    <>
                      <div className="col-12">
                        <div style={{ color: "#64748b", fontSize: "0.8rem", fontWeight: 700, marginBottom: "0.35rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Corrected Clock In</div>
                        <div className="grid">
                          <div className="col-12 md:col-6">
                            <label className="block mb-2 font-medium" htmlFor="attendance-correction-clock-in-date">Date</label>
                            <Calendar
                              inputId="attendance-correction-clock-in-date"
                              value={parseDateKeyToCalendarValue(getDatePartFromLocalDateTime(attendanceCorrectionClockInInput))}
                              minDate={parseDateKeyToCalendarValue(attendanceCorrectionWindowBounds.earliestDateKey)}
                              maxDate={parseDateKeyToCalendarValue(attendanceCorrectionWindowBounds.latestDateKey)}
                              onChange={(event) => setAttendanceCorrectionClockInInput(combineLocalDateTimeParts(
                                event.value ? getLosAngelesDateTimeLocalInput(event.value).slice(0, 10) : "",
                                getTimePartFromLocalDateTime(attendanceCorrectionClockInInput),
                              ))}
                              dateFormat="yy-mm-dd"
                              showIcon
                              touchUI={mobile}
                              className="w-full"
                              appendTo="self"
                            />
                          </div>
                          <div className="col-12 md:col-6">
                            <label className="block mb-2 font-medium" htmlFor="attendance-correction-clock-in-time">Time</label>
                            <Dropdown
                              inputId="attendance-correction-clock-in-time"
                              value={getTimePartFromLocalDateTime(attendanceCorrectionClockInInput)}
                              options={HALF_HOUR_TIME_OPTIONS}
                              optionLabel="label"
                              optionValue="value"
                              onChange={(event) => setAttendanceCorrectionClockInInput(combineLocalDateTimeParts(
                                getDatePartFromLocalDateTime(attendanceCorrectionClockInInput) || attendanceCorrectionDateInput,
                                String(event.value || ""),
                              ))}
                              placeholder="Select time"
                              className="w-full"
                              appendTo="self"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-12">
                        <div style={{ color: "#64748b", fontSize: "0.8rem", fontWeight: 700, marginBottom: "0.35rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Corrected Clock Out</div>
                        <div className="grid">
                          <div className="col-12 md:col-6">
                            <label className="block mb-2 font-medium" htmlFor="attendance-correction-clock-out-date">Date</label>
                            <Calendar
                              inputId="attendance-correction-clock-out-date"
                              value={parseDateKeyToCalendarValue(getDatePartFromLocalDateTime(attendanceCorrectionClockOutInput))}
                              minDate={parseDateKeyToCalendarValue(attendanceCorrectionWindowBounds.earliestDateKey)}
                              maxDate={parseDateKeyToCalendarValue(attendanceCorrectionWindowBounds.latestDateKey)}
                              onChange={(event) => setAttendanceCorrectionClockOutInput(combineLocalDateTimeParts(
                                event.value ? getLosAngelesDateTimeLocalInput(event.value).slice(0, 10) : "",
                                getTimePartFromLocalDateTime(attendanceCorrectionClockOutInput),
                              ))}
                              dateFormat="yy-mm-dd"
                              showIcon
                              touchUI={mobile}
                              className="w-full"
                              appendTo="self"
                            />
                          </div>
                          <div className="col-12 md:col-6">
                            <label className="block mb-2 font-medium" htmlFor="attendance-correction-clock-out-time">Time</label>
                            <Dropdown
                              inputId="attendance-correction-clock-out-time"
                              value={getTimePartFromLocalDateTime(attendanceCorrectionClockOutInput)}
                              options={HALF_HOUR_TIME_OPTIONS}
                              optionLabel="label"
                              optionValue="value"
                              onChange={(event) => setAttendanceCorrectionClockOutInput(combineLocalDateTimeParts(
                                getDatePartFromLocalDateTime(attendanceCorrectionClockOutInput) || attendanceCorrectionDateInput,
                                String(event.value || ""),
                              ))}
                              placeholder="Select time"
                              className="w-full"
                              appendTo="self"
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  ) : null}
                  <div className="col-12">
                    <div style={{ color: "#64748b", fontSize: "0.8rem", fontWeight: 700, marginBottom: "0.35rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Required Note</div>
                    <InputTextarea
                      value={attendanceCorrectionNote}
                      onChange={(event) => setAttendanceCorrectionNote(event.target.value)}
                      rows={4}
                      autoResize
                      className="w-full"
                      placeholder="Explain what changed and why. This note is included in the staff email."
                    />
                  </div>
                  <div className="col-12">
                    <div style={{ border: "1px solid #fde68a", borderRadius: "14px", background: "#fffdf5", padding: "0.9rem 1rem" }}>
                      <div style={{ color: "#92400e", fontSize: "0.76rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase" }}>Correction Impact Preview</div>
                      {loadingAttendanceCorrectionPreview ? (
                        <div style={{ marginTop: "0.6rem", color: "#64748b" }}>Refreshing payroll impact...</div>
                      ) : attendanceCorrectionPreview ? (
                        <div className="grid" style={{ marginTop: "0.3rem" }}>
                          <div className="col-12 md:col-4">
                            <div style={{ color: "#64748b", fontSize: "0.8rem" }}>Worked Hours</div>
                            <div style={{ marginTop: "0.2rem", fontWeight: 700, color: "#0f172a" }}>
                              {Number(attendanceCorrectionPreview?.originalWorkedHours || 0).toFixed(2)} {"->"} {Number(attendanceCorrectionPreview?.correctedWorkedHours || 0).toFixed(2)} hrs
                            </div>
                          </div>
                          <div className="col-12 md:col-4">
                            <div style={{ color: "#64748b", fontSize: "0.8rem" }}>Overtime</div>
                            <div style={{ marginTop: "0.2rem", fontWeight: 700, color: "#0f172a" }}>
                              {Number(attendanceCorrectionPreview?.originalOvertimeHours || 0).toFixed(2)} {"->"} {Number(attendanceCorrectionPreview?.correctedOvertimeHours || 0).toFixed(2)} hrs
                            </div>
                          </div>
                          <div className="col-12 md:col-4">
                            <div style={{ color: "#64748b", fontSize: "0.8rem" }}>Estimated Pay</div>
                            <div style={{ marginTop: "0.2rem", fontWeight: 700, color: "#0f172a" }}>
                              {formatCurrency(attendanceCorrectionPreview?.originalEstimatedPay)} {"->"} {formatCurrency(attendanceCorrectionPreview?.correctedEstimatedPay)}
                            </div>
                          </div>
                          <div className="col-12">
                            <div className="flex gap-2 flex-wrap">
                              <Tag value={`Hours Delta ${Number(attendanceCorrectionPreview?.workedHoursDelta || 0).toFixed(2)}`} severity="info" />
                              <Tag value={`OT Delta ${Number(attendanceCorrectionPreview?.overtimeHoursDelta || 0).toFixed(2)}`} severity="warning" />
                              <Tag value={`Pay Delta ${formatCurrency(attendanceCorrectionPreview?.estimatedPayDelta)}`} severity="success" />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div style={{ marginTop: "0.6rem", color: "#64748b" }}>
                          Add the correction details and note to preview the hours, overtime, and payroll impact before saving.
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="col-12">
                  <div style={{ border: "1px solid #dbeafe", borderRadius: "14px", background: "#f8fbff", padding: "0.9rem 1rem", color: "#475569" }}>
                    No shift found for the selected date. Pick another date within the allowed window.
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-content-end gap-2">
              <Button type="button" label="Cancel" severity="secondary" rounded outlined disabled={savingAttendanceCorrection} onClick={closeAttendanceCorrectionDialog} />
              <Button type="button" label="Save Correction" severity="warning" rounded disabled={!selectedAttendanceCorrectionTarget} loading={savingAttendanceCorrection} onClick={() => { void onSaveAttendanceCorrection(); }} />
            </div>
          </div>
        ) : null}
      </Dialog>

      <Dialog
        visible={showBulkShiftDialog}
        header={`Bulk Shift Settings (${selectedRows.length})`}
        style={mobile ? { width: "95vw" } : { width: "64rem", maxWidth: "95vw" }}
        modal
        onHide={() => !savingBulkShift && setShowBulkShiftDialog(false)}
      >
        <div className="flex flex-column gap-3">
          {false ? (
            <>
              <div style={{ color: "#64748b", lineHeight: 1.6 }}>
                Set each day’s access window for the selected staff members. If no day is enabled, scheduled access will be cleared.
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: "0.75rem", cursor: "pointer" }}>
                <Checkbox checked={mobileAccessEnabled} onChange={(event) => setMobileAccessEnabled(Boolean(event.checked))} />
                <span style={{ fontWeight: 700, color: "#0f172a" }}>Allow mobile access</span>
              </label>
              <div style={{ color: "#64748b", fontSize: "0.9rem", lineHeight: 1.55, marginTop: "-0.35rem" }}>
                When this is on, staff can use use their mobile phones. Turn it off if they should only access shared resident data from desktop or laptop.
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button type="button" label="Weekday Day Shift" severity="secondary" rounded outlined onClick={() => setDailyShiftSchedule((current) => applyShiftPresetForDays(current, ["mon", "tue", "wed", "thu", "fri"], "07:00", "15:00"))} />
                <Button type="button" label="Weekday Night Shift" severity="secondary" rounded outlined onClick={() => setDailyShiftSchedule((current) => applyShiftPresetForDays(current, ["mon", "tue", "wed", "thu", "fri"], "23:00", "07:00"))} />
                <Button type="button" label="Every Day Day Shift" severity="secondary" rounded outlined onClick={() => setDailyShiftSchedule((current) => applyShiftPresetForDays(current, SHIFT_DAY_OPTIONS.map((option) => option.key), "07:00", "15:00"))} />
                <Button type="button" label="Every Day Night Shift" severity="secondary" rounded outlined onClick={() => setDailyShiftSchedule((current) => applyShiftPresetForDays(current, SHIFT_DAY_OPTIONS.map((option) => option.key), "23:00", "07:00"))} />
                <Button type="button" label="Clear All" severity="contrast" rounded outlined onClick={() => setDailyShiftSchedule((current) => clearShiftSchedule(current))} />
              </div>
              <div
                style={{
                  border: "1px solid #e2e8f0",
                  borderRadius: "16px",
                  padding: "0.9rem 1rem",
                  background: "#f8fafc",
                }}
              >
                <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.7rem" }}>Apply Same Time to Every Day</div>
                <div className="flex gap-2 flex-wrap align-items-end">
                  <div style={{ minWidth: mobile ? "100%" : "11rem" }}>
                    <div style={{ color: "#64748b", fontSize: "0.8rem", fontWeight: 700, marginBottom: "0.35rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Start</div>
                    <select
                      value={bulkEveryDayStartTime}
                      onChange={(event) => setBulkEveryDayStartTime(event.target.value)}
                      style={{ width: "100%", border: "1px solid #cbd5e1", borderRadius: 12, padding: "0.72rem 0.8rem", minHeight: 46, background: "#ffffff" }}
                    >
                      {HALF_HOUR_TIME_OPTIONS.map((option) => (
                        <option key={`bulk-start-${option.value}`} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ minWidth: mobile ? "100%" : "11rem" }}>
                    <div style={{ color: "#64748b", fontSize: "0.8rem", fontWeight: 700, marginBottom: "0.35rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>End</div>
                    <select
                      value={bulkEveryDayEndTime}
                      onChange={(event) => setBulkEveryDayEndTime(event.target.value)}
                      style={{ width: "100%", border: "1px solid #cbd5e1", borderRadius: 12, padding: "0.72rem 0.8rem", minHeight: 46, background: "#ffffff" }}
                    >
                      {HALF_HOUR_TIME_OPTIONS.map((option) => (
                        <option key={`bulk-end-${option.value}`} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <Button
                    type="button"
                    label="Apply to Every Day"
                    severity="warning"
                    rounded
                    outlined
                    onClick={() => setDailyShiftSchedule((current) => applyShiftPresetForDays(current, SHIFT_DAY_OPTIONS.map((option) => option.key), bulkEveryDayStartTime, bulkEveryDayEndTime))}
                  />
                </div>
              </div>
              <div style={{ border: "1px solid #e2e8f0", borderRadius: "16px", overflow: "hidden", background: "#ffffff" }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: mobile ? "1fr" : "minmax(90px, 0.7fr) minmax(110px, 0.6fr) minmax(150px, 1fr) minmax(150px, 1fr)",
                    gap: "0.75rem",
                    padding: "0.8rem 1rem",
                    background: "#f8fafc",
                    borderBottom: "1px solid #e2e8f0",
                    color: "#64748b",
                    fontWeight: 700,
                    fontSize: "0.78rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  <div>Day</div>
                  {!mobile ? <div>Enabled</div> : null}
                  {!mobile ? <div>Start</div> : null}
                  {!mobile ? <div>End</div> : null}
                </div>
                {dailyShiftSchedule.map((daySchedule) => (
                  <div
                    key={daySchedule.day}
                    style={{
                      display: "grid",
                      gridTemplateColumns: mobile ? "1fr" : "minmax(90px, 0.7fr) minmax(110px, 0.6fr) minmax(150px, 1fr) minmax(150px, 1fr)",
                      gap: "0.75rem",
                      padding: "0.85rem 1rem",
                      borderBottom: "1px solid #eef2f7",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>{daySchedule.label}</div>
                    <div>
                      <Checkbox
                        checked={daySchedule.enabled}
                        onChange={(event) => {
                          setDailyShiftSchedule((current) => current.map((item) =>
                            item.day === daySchedule.day ? { ...item, enabled: Boolean(event.checked) } : item));
                        }}
                      />
                    </div>
                    <div>
                      <select
                        value={daySchedule.startTime}
                        disabled={!daySchedule.enabled}
                        onChange={(event) => {
                          setDailyShiftSchedule((current) => current.map((item) =>
                            item.day === daySchedule.day ? { ...item, startTime: event.target.value } : item));
                        }}
                        style={{ width: "100%", border: "1px solid #cbd5e1", borderRadius: 12, padding: "0.72rem 0.8rem", minHeight: 46 }}
                      >
                        {HALF_HOUR_TIME_OPTIONS.map((option) => (
                          <option key={`${daySchedule.day}-start-${option.value}`} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <select
                        value={daySchedule.endTime}
                        disabled={!daySchedule.enabled}
                        onChange={(event) => {
                          setDailyShiftSchedule((current) => current.map((item) =>
                            item.day === daySchedule.day ? { ...item, endTime: event.target.value } : item));
                        }}
                        style={{ width: "100%", border: "1px solid #cbd5e1", borderRadius: 12, padding: "0.72rem 0.8rem", minHeight: 46 }}
                      >
                        {HALF_HOUR_TIME_OPTIONS.map((option) => (
                          <option key={`${daySchedule.day}-end-${option.value}`} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ color: "#64748b", fontSize: "0.88rem" }}>
                {hasAnyEnabledShiftDay ? "Enabled days will be saved as per-day access windows." : "No day is enabled yet. Saving now will clear scheduled access for the selected staff."}
              </div>
              <div className="flex justify-content-end gap-2">
                <Button type="button" label="Cancel" severity="secondary" rounded outlined disabled={savingBulkShift} onClick={() => setShowBulkShiftDialog(false)} />
                <Button type="button" label="Save Shift" severity="warning" rounded loading={savingBulkShift} onClick={() => void onSaveBulkShift()} />
              </div>
            </>
          ) : (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: "0.9rem",
                  alignItems: "start",
                }}
              >
                <div
                  style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: "16px",
                    background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)",
                    padding: "0.85rem 0.95rem",
                    width: "100%",
                  }}
                >
                  <div style={{ color: "#64748b", fontSize: "0.85rem", lineHeight: 1.5, marginBottom: "0.65rem" }}>
                    When this is on, staff can use shared resident tools from a phone or tablet. Turn it off if they should only access shared resident data from desktop or laptop.
                  </div>
                  <label style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem", cursor: "pointer" }}>
                    <Checkbox checked={mobileAccessEnabled} onChange={(event) => setMobileAccessEnabled(Boolean(event.checked))} style={{ marginTop: "0.1rem" }} />
                    <div style={{ display: "grid", gap: "0.35rem" }}>
                      <span style={{ fontWeight: 700, color: "#0f172a" }}>Allow mobile access</span>
                    </div>
                  </label>
                  <label style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem", cursor: "pointer", marginTop: "0.7rem" }}>
                    <Checkbox checked={allTimeShiftAccessEnabled} onChange={(event) => setAllTimeShiftAccessEnabled(Boolean(event.checked))} style={{ marginTop: "0.1rem" }} />
                    <div style={{ display: "grid", gap: "0.2rem" }}>
                      <span style={{ fontWeight: 700, color: "#0f172a" }}>All time access (24/7)</span>
                      <span style={{ color: "#64748b", fontSize: "0.82rem", lineHeight: 1.45 }}>
                        Save full-time scheduled access for every day of the week.
                      </span>
                    </div>
                  </label>
                </div>
                <div style={{ display: "grid", gap: "0.85rem" }}>
                  <div style={{ color: "#64748b", lineHeight: 1.6 }}>
                    {allTimeShiftAccessEnabled
                      ? "24/7 access will be saved for every day. Turn this off to customize the weekly schedule."
                      : "Set each day's access window for the selected staff members. If no day is enabled, scheduled access will be cleared."}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button type="button" label="Weekday Day Shift" severity="secondary" rounded outlined disabled={allTimeShiftAccessEnabled} onClick={() => setDailyShiftSchedule((current) => applyShiftPresetForDays(current, ["mon", "tue", "wed", "thu", "fri"], "07:00", "19:00"))} />
                    <Button type="button" label="Weekday Night Shift" severity="secondary" rounded outlined disabled={allTimeShiftAccessEnabled} onClick={() => setDailyShiftSchedule((current) => applyShiftPresetForDays(current, ["mon", "tue", "wed", "thu", "fri"], "19:00", "07:00"))} />
                    <Button type="button" label="Every Day Day Shift" severity="secondary" rounded outlined disabled={allTimeShiftAccessEnabled} onClick={() => setDailyShiftSchedule((current) => applyShiftPresetForDays(current, SHIFT_DAY_OPTIONS.map((option) => option.key), "07:00", "19:00"))} />
                    <Button type="button" label="Every Day Night Shift" severity="secondary" rounded outlined disabled={allTimeShiftAccessEnabled} onClick={() => setDailyShiftSchedule((current) => applyShiftPresetForDays(current, SHIFT_DAY_OPTIONS.map((option) => option.key), "19:00", "07:00"))} />
                    <Button type="button" label="Clear All" severity="contrast" rounded outlined disabled={allTimeShiftAccessEnabled} onClick={() => setDailyShiftSchedule((current) => clearShiftSchedule(current))} />
                  </div>
                </div>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: mobile ? "1fr" : "minmax(0, 20rem) minmax(0, 33.5rem)",
                  gap: "1rem",
                  justifyContent: "center",
                  alignItems: "start",
                }}
              >
                <div
                  style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: "16px",
                    padding: "0.8rem 0.85rem",
                    background: "#f8fafc",
                    width: "100%",
                  }}
                >
                  <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.65rem", fontSize: "0.95rem" }}>Apply Same Time to Every Day</div>
                  <div style={{ display: "grid", gap: "0.65rem" }}>
                    <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: "0.55rem" }}>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.69rem", fontWeight: 700, marginBottom: "0.3rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Start Time</div>
                        <select
                          value={bulkEveryDayStartTime}
                          disabled={allTimeShiftAccessEnabled}
                          onChange={(event) => setBulkEveryDayStartTime(event.target.value)}
                          style={{ width: "100%", border: "1px solid #cbd5e1", borderRadius: 10, padding: "0.4rem 0.55rem", minHeight: 34, background: "#ffffff", fontSize: "0.86rem", opacity: allTimeShiftAccessEnabled ? 0.6 : 1 }}
                        >
                          {HALF_HOUR_TIME_OPTIONS.map((option) => (
                            <option key={`bulk-start-${option.value}`} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <div style={{ color: "#64748b", fontSize: "0.69rem", fontWeight: 700, marginBottom: "0.3rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>End Time</div>
                        <select
                          value={bulkEveryDayEndTime}
                          disabled={allTimeShiftAccessEnabled}
                          onChange={(event) => setBulkEveryDayEndTime(event.target.value)}
                          style={{ width: "100%", border: "1px solid #cbd5e1", borderRadius: 10, padding: "0.4rem 0.55rem", minHeight: 34, background: "#ffffff", fontSize: "0.86rem", opacity: allTimeShiftAccessEnabled ? 0.6 : 1 }}
                        >
                          {HALF_HOUR_TIME_OPTIONS.map((option) => (
                            <option key={`bulk-end-${option.value}`} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <Button
                        type="button"
                        label="Apply to Every Day"
                        severity="warning"
                        rounded
                        outlined
                        disabled={allTimeShiftAccessEnabled}
                        className={mobile ? "w-full" : undefined}
                        style={mobile ? undefined : { minHeight: 34, padding: "0.45rem 0.85rem" }}
                        onClick={() => setDailyShiftSchedule((current) => applyShiftPresetForDays(current, SHIFT_DAY_OPTIONS.map((option) => option.key), bulkEveryDayStartTime, bulkEveryDayEndTime))}
                      />
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <div style={{ border: "1px solid #e2e8f0", borderRadius: "16px", overflow: "hidden", background: "#ffffff", width: "100%", maxWidth: mobile ? "100%" : "33.5rem" }}>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: mobile ? "1fr" : "minmax(74px, 0.7fr) minmax(68px, 0.42fr) minmax(112px, 0.85fr) minmax(112px, 0.85fr)",
                        gap: "0.45rem",
                        padding: "0.58rem 0.72rem",
                        background: "#f8fafc",
                        borderBottom: "1px solid #e2e8f0",
                        color: "#64748b",
                        fontWeight: 700,
                        fontSize: "0.69rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      <div>Day</div>
                      {!mobile ? <div>On</div> : null}
                      {!mobile ? <div>Start</div> : null}
                      {!mobile ? <div>End</div> : null}
                    </div>
                    {dailyShiftSchedule.map((daySchedule) => (
                      <div
                        key={daySchedule.day}
                        style={{
                          display: "grid",
                          gridTemplateColumns: mobile ? "1fr" : "minmax(74px, 0.7fr) minmax(68px, 0.42fr) minmax(112px, 0.85fr) minmax(112px, 0.85fr)",
                          gap: "0.45rem",
                          padding: "0.5rem 0.72rem",
                          borderBottom: "1px solid #eef2f7",
                          alignItems: "center",
                        }}
                      >
                        <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.86rem" }}>{daySchedule.label}</div>
                        <div style={{ display: "flex", justifyContent: mobile ? "flex-start" : "center" }}>
                          <Checkbox
                            checked={daySchedule.enabled}
                            disabled={allTimeShiftAccessEnabled}
                            onChange={(event) => {
                              setDailyShiftSchedule((current) => current.map((item) =>
                                item.day === daySchedule.day ? { ...item, enabled: Boolean(event.checked) } : item));
                            }}
                          />
                        </div>
                        <div>
                          <select
                            value={daySchedule.startTime}
                            disabled={!daySchedule.enabled || allTimeShiftAccessEnabled}
                            onChange={(event) => {
                              setDailyShiftSchedule((current) => current.map((item) =>
                                item.day === daySchedule.day ? { ...item, startTime: event.target.value } : item));
                            }}
                            style={{ width: "100%", border: "1px solid #cbd5e1", borderRadius: 10, padding: "0.4rem 0.55rem", minHeight: 34, background: "#ffffff", fontSize: "0.86rem" }}
                          >
                            {HALF_HOUR_TIME_OPTIONS.map((option) => (
                              <option key={`${daySchedule.day}-start-${option.value}`} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <select
                            value={daySchedule.endTime}
                            disabled={!daySchedule.enabled || allTimeShiftAccessEnabled}
                            onChange={(event) => {
                              setDailyShiftSchedule((current) => current.map((item) =>
                                item.day === daySchedule.day ? { ...item, endTime: event.target.value } : item));
                            }}
                            style={{ width: "100%", border: "1px solid #cbd5e1", borderRadius: 10, padding: "0.4rem 0.55rem", minHeight: 34, background: "#ffffff", fontSize: "0.86rem" }}
                          >
                            {HALF_HOUR_TIME_OPTIONS.map((option) => (
                              <option key={`${daySchedule.day}-end-${option.value}`} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div style={{ color: "#64748b", fontSize: "0.88rem" }}>
                {allTimeShiftAccessEnabled
                  ? "24/7 access will be saved as full-day windows for all seven days."
                  : hasAnyEnabledShiftDay
                    ? "Enabled days will be saved as per-day access windows."
                    : "No day is enabled yet. Saving now will clear scheduled access for the selected staff."}
              </div>
              <div
                style={{
                  position: "sticky",
                  bottom: 0,
                  zIndex: 5,
                  paddingTop: "0.85rem",
                  borderTop: "1px solid #e2e8f0",
                  background: "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, #ffffff 24%)",
                  backdropFilter: "blur(6px)",
                }}
              >
                <div className="flex justify-content-end gap-2 flex-wrap">
                  <Button type="button" label="Cancel" severity="secondary" rounded outlined disabled={savingBulkShift} onClick={() => setShowBulkShiftDialog(false)} />
                  <Button type="button" label="Save Shift" severity="warning" rounded loading={savingBulkShift} onClick={() => void onSaveBulkShift()} />
                </div>
              </div>
            </>
          )}
        </div>
      </Dialog>

      <Dialog
        visible={showBulkAccessDialog}
        header={`Bulk Resident Access (${selectedRows.length})`}
        style={mobile ? { width: "95vw" } : { width: "64rem", maxWidth: "95vw" }}
        modal
        onHide={() => !savingBulkAccess && setShowBulkAccessDialog(false)}
      >
        <div className="flex flex-column gap-3">
          <div style={{ color: "#64748b", lineHeight: 1.6 }}>
            Role setup and resident selection are optional here. Pick either one now if you want, or save and refine each staff member later.
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: mobile ? "1fr" : "minmax(0, 1fr) minmax(0, 1.1fr)",
              gap: "0.9rem",
              alignItems: "start",
            }}
          >
            <div style={{ padding: "0.9rem 1rem", borderRadius: "14px", border: "1px solid #e2e8f0", background: "#f8fafc" }}>
              <div className="flex justify-content-between align-items-center gap-2 flex-wrap" style={{ marginBottom: "0.75rem" }}>
                <div style={{ fontWeight: 700, color: "#0f172a" }}>Select Role</div>
                <div className="flex gap-2 flex-wrap align-items-center">
                  <Tag value="Optional" severity="info" rounded />
                  {bulkAccessPresetOptions.length ? (
                    <Tag
                      value="View Permissions"
                      severity="warning"
                      rounded
                      onClick={() => setShowAccessMatrixDialog(true)}
                      style={{ cursor: "pointer" }}
                    />
                  ) : null}
                </div>
              </div>
              <div style={{ color: "#64748b", fontSize: "0.88rem", lineHeight: 1.6, marginBottom: "0.85rem" }}>
                Click a role card if you want to apply one shared access setup to all selected staff now.
              </div>
              {bulkAccessPresetOptions.length ? (
                <div className="flex flex-column gap-3">
                  {bulkAccessPresetOptions.map((option) => (
                    <label
                      key={option.value}
                      htmlFor={`bulk-access-preset-${option.value}`}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "0.7rem",
                        border: selectedBulkAccessPreset === option.value ? "1px solid #fdba74" : "1px solid #dbe7f3",
                        background: selectedBulkAccessPreset === option.value ? "#fff7ed" : "#ffffff",
                        borderRadius: "14px",
                        padding: "0.8rem 0.9rem",
                        cursor: "pointer",
                      }}
                    >
                      <RadioButton
                        inputId={`bulk-access-preset-${option.value}`}
                        name="bulkAccessPreset"
                        value={option.value}
                        checked={selectedBulkAccessPreset === option.value}
                        onChange={(event) => applyBulkAccessPreset(String(event.value || STAFF_ACCESS_PRESET_CUSTOM))}
                        style={{ marginTop: "0.1rem" }}
                      />
                      <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}>
                        <span style={{ fontWeight: 700, color: "#0f172a" }}>{option.label}</span>
                        <span style={{ color: "#64748b", fontSize: "0.88rem", lineHeight: 1.5 }}>{option.description}</span>
                      </div>
                    </label>
                  ))}
                </div>
              ) : (
                <div style={{ color: "#64748b", fontStyle: "italic" }}>
                  No role templates are available for this staff selection.
                </div>
              )}
            </div>

            <div style={{ padding: "0.9rem 1rem", borderRadius: "14px", border: "1px solid #e2e8f0", background: "#f8fafc" }}>
              <div className="flex justify-content-between align-items-center gap-2 flex-wrap" style={{ marginBottom: "0.75rem" }}>
                <div style={{ fontWeight: 700, color: "#0f172a" }}>Select Residents</div>
                <div className="flex gap-2 flex-wrap align-items-center">
                  <Tag value="Optional" severity="info" rounded />
                  {activeResidents.length ? (
                    <>
                      <Button
                        type="button"
                        label="Select All"
                        severity="secondary"
                        rounded
                        outlined
                        onClick={() => setSelectedResidentIds(activeResidents.map((resident) => String(resident?.residentId || "").trim()).filter(Boolean))}
                      />
                      <Button
                        type="button"
                        label="Clear"
                        severity="secondary"
                        rounded
                        outlined
                        onClick={() => setSelectedResidentIds([])}
                      />
                    </>
                  ) : null}
                </div>
              </div>
              <div style={{ color: "#64748b", fontSize: "0.88rem", lineHeight: 1.6, marginBottom: "0.85rem" }}>
                Click any resident row to give all selected staff shared resident access now.
              </div>
              {activeResidents.length ? (
                <div style={{ display: "grid", gap: "0.75rem" }}>
                  {activeResidents.map((resident) => {
                    const residentId = String(resident?.residentId || "").trim();
                    const residentName = `${String(resident?.firstName || "").trim()} ${String(resident?.lastName || "").trim()}`.trim() || "Resident";
                    const checked = selectedResidentIds.includes(residentId);
                    return (
                      <div
                        key={`bulk-access-resident-${residentId}`}
                        style={{
                          border: checked ? "1px solid #fdba74" : "1px solid #dbe7f3",
                          background: checked ? "#fff7ed" : "#ffffff",
                          borderRadius: "14px",
                          padding: "0.85rem 0.95rem",
                        }}
                      >
                        <label
                          htmlFor={`bulk-access-resident-${residentId}`}
                          style={{ display: "flex", alignItems: "center", gap: "0.85rem", cursor: "pointer" }}
                        >
                          <Checkbox
                            inputId={`bulk-access-resident-${residentId}`}
                            checked={checked}
                            onChange={(event) => {
                              setSelectedResidentIds((current) => (
                                event.checked
                                  ? (current.includes(residentId) ? current : [...current, residentId])
                                  : current.filter((item) => item !== residentId)
                              ));
                            }}
                          />
                          <div style={{ fontWeight: 700, color: "#0f172a" }}>{residentName}</div>
                        </label>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ color: "#64748b", fontStyle: "italic" }}>
                  No active residents available right now.
                </div>
              )}
            </div>
          </div>
          <div
            style={{
              position: "sticky",
              bottom: 0,
              zIndex: 5,
              paddingTop: "0.85rem",
              borderTop: "1px solid #e2e8f0",
              background: "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, #ffffff 24%)",
              backdropFilter: "blur(6px)",
            }}
          >
            <div className="flex justify-content-end gap-2 flex-wrap">
              <Button type="button" label="Cancel" severity="secondary" rounded outlined disabled={savingBulkAccess} onClick={() => setShowBulkAccessDialog(false)} />
              <Button type="button" label="Apply Access" severity="warning" rounded loading={savingBulkAccess} onClick={() => void onSaveBulkAccess()} />
            </div>
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={showStaffAccessDialog}
        header={`Resident Access${selectedStaffAccessRow?.name ? ` - ${selectedStaffAccessRow.name}` : ""}`}
        style={mobile ? { width: "95vw" } : { width: "64rem", maxWidth: "95vw" }}
        modal
        onHide={() => !savingStaffAccess && setShowStaffAccessDialog(false)}
      >
        <div className="flex flex-column gap-3">
          <div style={{ color: "#64748b", lineHeight: 1.6 }}>
            Choose one role template for this staff member and save when you are ready.
          </div>
          {staffAccessPresetOptions.length ? (
            <div style={{ padding: "0.9rem 1rem", borderRadius: "14px", border: "1px solid #e2e8f0", background: "#f8fafc" }}>
              <div className="flex justify-content-between align-items-center gap-2 flex-wrap" style={{ marginBottom: "0.7rem" }}>
                <div style={{ fontWeight: 700, color: "#0f172a" }}>Quick Setup</div>
                <Tag
                  value="View Permissions"
                  severity="warning"
                  rounded
                  onClick={() => setShowAccessMatrixDialog(true)}
                  style={{ cursor: "pointer" }}
                />
              </div>
              <div className="flex flex-column md:flex-row md:flex-wrap gap-3">
                {staffAccessPresetOptions.map((option) => (
                  <label
                    key={option.value}
                    htmlFor={`staff-access-preset-${option.value}`}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "0.7rem",
                      border: selectedStaffAccessPreset === option.value ? "1px solid #fdba74" : "1px solid #dbe7f3",
                      background: selectedStaffAccessPreset === option.value ? "#fff7ed" : "#ffffff",
                      borderRadius: "14px",
                      padding: "0.8rem 0.9rem",
                      cursor: "pointer",
                      minWidth: mobile ? undefined : "15rem",
                      flex: mobile ? undefined : "1 1 15rem",
                    }}
                  >
                    <RadioButton
                      inputId={`staff-access-preset-${option.value}`}
                      name="staffAccessPreset"
                      value={option.value}
                      checked={selectedStaffAccessPreset === option.value}
                      onChange={(event) => applyStaffAccessPreset(String(event.value || STAFF_ACCESS_PRESET_CUSTOM))}
                      style={{ marginTop: "0.1rem" }}
                    />
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}>
                      <span style={{ fontWeight: 700, color: "#0f172a" }}>{option.label}</span>
                      <span style={{ color: "#64748b", fontSize: "0.88rem", lineHeight: 1.5 }}>{option.description}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          ) : null}
          <div style={{ border: "1px solid #e2e8f0", borderRadius: "16px", background: "#ffffff", padding: "0.95rem 1rem" }}>
            <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.35rem" }}>Active Residents</div>
            <div style={{ color: "#64748b", lineHeight: 1.6, marginBottom: "0.85rem" }}>
              Select the active residents this staff member can access.
            </div>
            {activeResidents.length ? (
              <div style={{ display: "grid", gap: "0.75rem" }}>
                {activeResidents.map((resident) => {
                  const residentId = String(resident?.residentId || "").trim();
                  const residentName = `${String(resident?.firstName || "").trim()} ${String(resident?.lastName || "").trim()}`.trim() || "Resident";
                  const checked = selectedResidentIds.includes(residentId);
                  return (
                    <div
                      key={`staff-access-resident-${residentId}`}
                      style={{
                        border: checked ? "1px solid #fdba74" : "1px solid #dbe7f3",
                        background: checked ? "#fff7ed" : "#ffffff",
                        borderRadius: "14px",
                        padding: "0.85rem 0.95rem",
                      }}
                    >
                      <label
                        htmlFor={`staff-access-resident-${residentId}`}
                        style={{ display: "flex", alignItems: "center", gap: "0.85rem", cursor: "pointer" }}
                      >
                        <Checkbox
                          inputId={`staff-access-resident-${residentId}`}
                          checked={checked}
                          onChange={(event) => {
                            setSelectedResidentIds((current) => (
                              event.checked
                                ? (current.includes(residentId) ? current : [...current, residentId])
                                : current.filter((item) => item !== residentId)
                            ));
                          }}
                        />
                        <div style={{ fontWeight: 700, color: "#0f172a" }}>{residentName}</div>
                      </label>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ color: "#64748b", fontStyle: "italic" }}>
                No active residents available right now.
              </div>
            )}
          </div>
          <div className="flex justify-content-end gap-2">
            <Button type="button" label="Cancel" severity="secondary" rounded outlined disabled={savingStaffAccess} onClick={() => setShowStaffAccessDialog(false)} />
            <Button type="button" label="Save Access" severity="warning" rounded loading={savingStaffAccess} onClick={() => void onSaveStaffAccess()} />
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={showAccessMatrixDialog}
        header="Access Matrix"
        style={mobile ? { width: "95vw" } : { width: "52rem", maxWidth: "95vw" }}
        modal
        onHide={() => setShowAccessMatrixDialog(false)}
      >
        <div className="flex flex-column gap-3">
          <div style={{ color: "#64748b", lineHeight: 1.6 }}>
            This shows which access is included when you choose each role template.
          </div>
          <DataTable value={STAFF_ACCESS_MATRIX_ROWS} size="small" responsiveLayout="scroll">
            <Column field="access" header="Access" body={(rowData: any) => <StaffAccessMatrixAction row={rowData} />} />
            <Column
              header="Caregiver"
              body={(rowData: any) => <Tag value={rowData.caregiver ? "Included" : "Not included"} severity={rowData.caregiver ? "success" : "secondary"} rounded />}
            />
            <Column
              header="Nurse"
              body={(rowData: any) => <Tag value={rowData.nurse ? "Included" : "Not included"} severity={rowData.nurse ? "success" : "secondary"} rounded />}
            />
            <Column
              header="MHP"
              body={(rowData: any) => <Tag value={rowData.mhp ? "Included" : "Not included"} severity={rowData.mhp ? "success" : "secondary"} rounded />}
            />
          </DataTable>
        </div>
      </Dialog>

      <Dialog
        visible={showSharedBinderHistoryDialog}
        modal
        style={{ width: mobile ? "95vw" : "40rem", maxWidth: "95vw" }}
        header="Application History"
        onHide={() => setShowSharedBinderHistoryDialog(false)}
      >
        {selectedSharedBinderHistoryRow?.history?.length ? (
          <div className="flex flex-column gap-3">
            {selectedSharedBinderHistoryRow.history.map((historyItem, index) => (
              <div
                key={`${historyItem.eventType || "event"}-${historyItem.createdAt || index}`}
                style={{
                  borderLeft: "3px solid #f59e0b",
                  paddingLeft: "0.85rem",
                }}
              >
                <div style={{ fontWeight: 700, color: "#0f172a" }}>{historyItem.title || "Update"}</div>
                <div style={{ color: "#64748b", marginTop: "0.2rem", lineHeight: 1.6 }}>{historyItem.description || ""}</div>
                <div style={{ color: "#94a3b8", fontSize: "0.82rem", marginTop: "0.25rem" }}>
                  {historyItem.actorLabel || "System"}{historyItem.createdAt ? ` • ${formatSharedBinderDate(historyItem.createdAt)}` : ""}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: "#64748b" }}>No history yet for this application.</div>
        )}
      </Dialog>

      <div
        style={{
          borderRadius: "24px",
          border: "1px solid #e2e8f0",
          background: "#ffffff",
          boxShadow: "0 18px 40px rgba(15, 23, 42, 0.08)",
          padding: mobile ? "1rem" : "1.2rem",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
        }}
      >
        {/* <div
          style={{
            border: "1px solid #e2e8f0",
            borderRadius: "16px",
            background: "#f8fafc",
            padding: mobile ? "0.95rem" : "1rem",
          }}
        >
            <div className="flex flex-column gap-3">
              <div className="flex justify-content-between align-items-center gap-2 flex-wrap">
                <div>
                  <div style={{ fontSize: "1rem", fontWeight: 800, color: "#0f172a" }}>Caregiver Applications</div>
                  <div style={{ color: "#64748b", lineHeight: 1.6 }}>
                    Review caregiver job binders that were shared with your account.
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Tag value={`${sharedBinderSummary.total} total`} severity="info" />
                  <Tag value={`${sharedBinderSummary.underReview} under review`} severity="info" />
                  <Tag value={`${sharedBinderSummary.questionsPending} questions pending`} severity="warning" />
                  <Tag value={`${sharedBinderSummary.accepted} accepted`} severity="success" />
                  <Tag value={`${sharedBinderSummary.needUpdates} need updates`} severity="warning" />
                </div>
              </div>
              <div className="grid">
                <div className="col-12 md:col-7">
                  <InputText
                    value={sharedBinderSearchText}
                    onChange={(event) => setSharedBinderSearchText(event.target.value)}
                    placeholder="Search caregiver name or email"
                    className="w-full"
                  />
                </div>
                <div className="col-12 md:col-5">
                  <Dropdown
                    value={selectedSharedBinderStatus}
                    options={BINDER_APPLICATION_STATUS_OPTIONS}
                    onChange={(event) => setSelectedSharedBinderStatus(String(event.value || "all"))}
                    optionLabel="label"
                    optionValue="value"
                    className="w-full"
                    placeholder="Filter by status"
                  />
                </div>
              </div>
              <div style={{ overflowX: "auto" }}>
                <div style={{ border: "1px solid #e2e8f0", borderRadius: "14px", overflow: "hidden", background: "#ffffff" }}>
                  <DataTable
                    value={filteredSharedBinderRows}
                    loading={loadingSharedBinders}
                    paginator
                    rows={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    emptyMessage="No caregiver applications match this view yet."
                    sortField="createdAt"
                    sortOrder={-1}
                    removableSort
                  >
                    <Column
                      header="Caregiver"
                      sortable
                      sortField="ownerDisplayName"
                      body={(rowData: BinderShare) => (
                        <div>
                          <div style={{ fontWeight: 800, color: "#0f172a" }}>{rowData.ownerDisplayName || "Caregiver"}</div>
                          <div style={{ color: "#64748b", marginTop: "0.2rem" }}>{rowData.ownerEmail || "Not available"}</div>
                        </div>
                      )}
                    />
                    <Column
                      header="Status"
                      sortable
                      sortField="reviewStatus"
                      body={(rowData: BinderShare) => (
                        <Tag value={getBinderReviewStatusLabel(rowData.reviewStatus)} severity={getBinderReviewStatusSeverity(rowData.reviewStatus)} />
                      )}
                    />
                    <Column
                      header="Questions"
                      body={(rowData: BinderShare) => (
                        <Tag
                          value={String(rowData.jobQuestionsStatus || "").trim().toLowerCase() === "questions_pending" ? "Questions to Answer" : String(rowData.jobQuestionsStatus || "").trim().toLowerCase() === "answered" ? "Answered" : "Not Sent"}
                          severity={String(rowData.jobQuestionsStatus || "").trim().toLowerCase() === "questions_pending" ? "warning" : String(rowData.jobQuestionsStatus || "").trim().toLowerCase() === "answered" ? "success" : "info"}
                        />
                      )}
                    />
                    <Column
                      header="Shared"
                      sortable
                      sortField="createdAt"
                      body={(rowData: BinderShare) => formatSharedBinderDate(rowData.createdAt)}
                    />
                    <Column
                      header="Last Activity"
                      body={(rowData: BinderShare) => {
                        const latestHistoryItem = getLatestSharedBinderHistoryItem(rowData);
                        if (!latestHistoryItem) {
                          return <span style={{ color: "#64748b" }}>No activity yet</span>;
                        }
                        return (
                          <div>
                            <div style={{ fontWeight: 700, color: "#0f172a" }}>{latestHistoryItem.title || "Update"}</div>
                            <div style={{ color: "#64748b", marginTop: "0.2rem" }}>
                              {latestHistoryItem.createdAt ? formatSharedBinderDate(latestHistoryItem.createdAt) : "Unknown"}
                            </div>
                          </div>
                        );
                      }}
                    />
                    <Column
                      header="Action"
                      body={(rowData: BinderShare) => (
                        <div className="flex flex-wrap gap-2">
                          <Button
                            type="button"
                            label={downloadingBinderPacketByShareId[String(rowData.shareId || "").trim()] ? "Downloading..." : "Download Packet"}
                            severity="secondary"
                            rounded
                            outlined
                            size="small"
                            loading={Boolean(downloadingBinderPacketByShareId[String(rowData.shareId || "").trim()])}
                            onClick={() => void onDownloadBinderPacket(rowData)}
                          />
                          <Button
                            type="button"
                            label="View History"
                            severity="secondary"
                            rounded
                            outlined
                            size="small"
                            onClick={() => openSharedBinderHistoryDialog(rowData)}
                          />
                          <Button
                            type="button"
                            label="Open Binder"
                            severity="warning"
                            rounded
                            outlined
                            size="small"
                            onClick={() => navigate(RouterAddress.getSharedBinderPagePath(String(rowData.shareId || "")))}
                          />
                        </div>
                      )}
                    />
                  </DataTable>
                </div>
              </div>
            </div>
        </div> */}

        <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
          {renderSurfaceHeader(
            showArchivedStaff ? "Archived Staff" : "Staff Management",
            `Manage staff links, bulk shift settings, and resident access for ${String(homeProfile?.businessName || homeProfile?.name || `this ${facilityLowerLabel}`).trim()}`,
            "pi pi-users",
          )}
          {staffRows.length ? (
            <div className="staff-management-header-controls flex flex-column align-items-end gap-2">
              <div className="staff-management-filters flex align-items-center gap-2 flex-wrap justify-content-end">
              <InputText
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder="Search staff by name or email"
                style={{ width: "13rem" }}
              />
              <MultiSelect
                value={selectedRoleFilters}
                options={availableStaffRoleFilters}
                onChange={(event) => setSelectedRoleFilters(Array.isArray(event.value) ? event.value : [])}
                optionLabel="label"
                optionValue="value"
                display="chip"
                placeholder="Filter by Role"
                style={{ width: "15rem" }}
                maxSelectedLabels={2}
              />
              {archivedStaffRows.length ? (
                <div className="staff-management-active-filter flex align-items-center gap-2">
                  <Checkbox
                    inputId="staff-active-filter"
                    checked={!showArchivedStaff}
                    onChange={(event) => setShowArchivedStaff(!event.checked)}
                  />
                  <label htmlFor="staff-active-filter">Active Staff</label>
                </div>
              ) : null}
              </div>
            </div>
          ) : null}
        </div>

        {loading ? (
          <div className="flex justify-content-center p-5">
            <ProgressSpinner />
          </div>
        ) : (
          <>
            {staffRows.length ? (
              <div style={{ color: "#64748b", fontSize: "0.92rem" }}>
                {showArchivedStaff
                  ? "Archived staff stay out of the active roster until you restore them."
                  : selectedStaffIds.length
                    ? `${selectedStaffIds.length} staff member${selectedStaffIds.length === 1 ? "" : "s"} selected.`
                    : "Select staff rows to apply bulk shift or resident access changes."}
              </div>
            ) : null}

            {filteredRows.length ? (
              <div style={{ border: "1px solid #e2e8f0", borderRadius: "16px", overflow: "hidden", background: "#ffffff" }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: mobile
                      ? "1fr"
                      : showArchivedStaff
                        ? "minmax(240px, 1.2fr) minmax(240px, 1fr) minmax(320px, auto)"
                        : "56px minmax(240px, 1.2fr) minmax(240px, 1fr) minmax(320px, auto)",
                    gap: "0.75rem",
                    padding: "0.8rem 1rem",
                    background: "#f8fafc",
                    borderBottom: "1px solid #e2e8f0",
                    color: "#64748b",
                    fontWeight: 700,
                    fontSize: "0.78rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    alignItems: "center",
                  }}
                >
                  {!mobile && !showArchivedStaff ? (
                    <div>
                      <Checkbox checked={allVisibleSelected} onChange={(event) => onToggleAllVisibleRows(Boolean(event.checked))} />
                    </div>
                  ) : null}
                  <div>Staff</div>
                  {!mobile ? <div>Shift & Residents</div> : null}
                  {!mobile ? <div>Actions</div> : null}
                </div>

                {filteredRows.map((row) => {
                  const checked = selectedStaffIds.includes(row.caregiverId);
                  const sharedResidentNames = Array.from(new Set([
                    ...(Array.isArray(row?.sharedResidents) ? row.sharedResidents : []),
                    ...(Array.isArray(row?.clinicalSharedResidents) ? row.clinicalSharedResidents : []),
                  ].map((resident: any) => String(resident?.residentName || "").trim()).filter(Boolean)));
                  const sharedResidentTooltip = sharedResidentNames.join("\n");
                  const shiftAccessActionItems = row.active === false
                    ? [
                      {
                        label: "Restore Staff",
                        icon: "pi pi-replay",
                        command: () => setRemoveStaffDialogRow(row),
                      },
                      {
                        label: "Delete Staff",
                        icon: "pi pi-trash",
                        command: () => setDeleteStaffDialogRow(row),
                      },
                    ]
                    : [
                      {
                        label: "Shift",
                        icon: "pi pi-clock",
                        command: () => openBulkShiftForRows([row.caregiverId]),
                      },
                      {
                        label: "Access",
                        icon: "pi pi-users",
                        command: () => openStaffAccessDialog(row),
                      },
                      {
                        label: "Archive Staff",
                        icon: "pi pi-archive",
                        command: () => setRemoveStaffDialogRow(row),
                      },
                      {
                        label: "Delete Staff",
                        icon: "pi pi-trash",
                        command: () => setDeleteStaffDialogRow(row),
                      },
                    ];
                  const payAttendanceActionItems = row.active === false
                    ? [
                      {
                        label: "Time & Attendance",
                        icon: "pi pi-clock",
                        command: () => openTimeAttendanceDialog(row),
                      },
                      {
                        label: "Pay History",
                        icon: "pi pi-history",
                        command: () => setCompensationHistoryDialogRow(row),
                      },
                    ]
                    : [
                      {
                        label: "Pay & Leave",
                        icon: "pi pi-wallet",
                        command: () => openCompensationDialog(row),
                      },
                      {
                        label: "Pay History",
                        icon: "pi pi-history",
                        command: () => setCompensationHistoryDialogRow(row),
                      },
                      {
                        label: "Time & Attendance",
                        icon: "pi pi-clock",
                        command: () => openTimeAttendanceDialog(row),
                      },
                    ];
                  return (
                    <div
                      key={row.caregiverId}
                      style={{
                        display: "grid",
                        gridTemplateColumns: mobile
                          ? "1fr"
                          : showArchivedStaff
                            ? "minmax(240px, 1.2fr) minmax(240px, 1fr) minmax(320px, auto)"
                            : "56px minmax(240px, 1.2fr) minmax(240px, 1fr) minmax(320px, auto)",
                        gap: "0.9rem",
                        padding: "0.9rem 1rem",
                        borderBottom: "1px solid #eef2f7",
                        alignItems: "start",
                      }}
                    >
                      {!mobile && !showArchivedStaff ? (
                        <div style={{ paddingTop: "0.2rem" }}>
                          <Checkbox
                            checked={checked}
                            onChange={(event) => {
                              setSelectedStaffIds((current) => event.checked
                                ? [...current, row.caregiverId]
                                : current.filter((value) => value !== row.caregiverId));
                            }}
                          />
                        </div>
                      ) : null}
                      <div style={{ minWidth: 0 }}>
                        {mobile && !showArchivedStaff ? (
                          <div style={{ marginBottom: "0.55rem" }}>
                            <Checkbox
                              checked={checked}
                              onChange={(event) => {
                                setSelectedStaffIds((current) => event.checked
                                  ? [...current, row.caregiverId]
                                  : current.filter((value) => value !== row.caregiverId));
                              }}
                            />
                          </div>
                        ) : null}
                        <div style={{ fontWeight: 800, color: "#0f172a" }}>{row.name}</div>
                        {row.email ? (
                          <div style={{ color: "#64748b", fontSize: "0.84rem", marginTop: "0.18rem" }}>{row.email}</div>
                        ) : null}
                        {row.displayRoleType ? (
                          <div className="flex flex-wrap gap-2" style={{ marginTop: "0.35rem" }}>
                            <Tag value={formatRoleLabel(row.displayRoleType)} severity="warning" />
                            {row.facilityAdminAccess ? (
                              <Tag value="Facility Admin Access" severity="info" />
                            ) : null}
                            {row.delegationCredentialStatus !== "verified" ? (
                              <Tag
                                value={row.delegationCredentialStatus === "expired" ? "Credentials expired" : "Credentials missing"}
                                severity="danger"
                              />
                            ) : null}
                          </div>
                        ) : null}
                        {mobile ? (
                          <div style={{ marginTop: "0.65rem", color: "#475569", lineHeight: 1.6 }}>
                            <div><strong>Shift:</strong> {row.shiftSummary}</div>
                            <div>
                              <strong>Residents:</strong>{" "}
                              <span
                                className={sharedResidentTooltip ? "staff-resident-hover-target" : undefined}
                                data-pr-tooltip={sharedResidentTooltip || undefined}
                                data-pr-position="right"
                                style={{ color: "#0f172a", fontWeight: 700, cursor: sharedResidentTooltip ? "help" : "default" }}
                              >
                                {row.sharedCount}
                              </span>
                            </div>
                          </div>
                        ) : null}
                      </div>
                      {!mobile ? (
                        <div style={{ color: "#475569", lineHeight: 1.6 }}>
                          <div><strong>Shift:</strong> {row.shiftSummary}</div>
                          <div>
                            <strong>Residents:</strong>{" "}
                            <span
                              className={sharedResidentTooltip ? "staff-resident-hover-target" : undefined}
                              data-pr-tooltip={sharedResidentTooltip || undefined}
                              data-pr-position="right"
                              style={{ color: "#0f172a", fontWeight: 700, cursor: sharedResidentTooltip ? "help" : "default" }}
                            >
                              {row.sharedCount}
                            </span>
                          </div>
                        </div>
                      ) : null}
                      <div
                        className={mobile ? "flex gap-2 flex-wrap" : "flex justify-content-end gap-2 flex-wrap"}
                        style={mobile ? undefined : { minWidth: "320px" }}
                      >
                        {!showArchivedStaff ? (
                          <span style={{ display: "inline-flex" }}>
                            <Button
                              type="button"
                              label="Edit Credentials"
                              icon="pi pi-id-card"
                              severity="warning"
                              outlined
                              rounded
                              size="small"
                              onClick={() => openStaffCredentials(row)}
                              disabled={Boolean(savingAdminAccessByCaregiverId[row.caregiverId]) || Boolean(removingStaffByCaregiverId[row.caregiverId])}
                            />
                          </span>
                        ) : null}
                        <ActionMenuButton
                          label="Shift & Access"
                          severity="warning"
                          outlined
                          rounded
                          size="small"
                          model={shiftAccessActionItems}
                          disabled={Boolean(savingAdminAccessByCaregiverId[row.caregiverId]) || Boolean(removingStaffByCaregiverId[row.caregiverId])}
                        />
                        <ActionMenuButton
                          label="Pay & Attendance"
                          severity="warning"
                          outlined
                          rounded
                          size="small"
                          model={payAttendanceActionItems}
                          disabled={Boolean(savingAdminAccessByCaregiverId[row.caregiverId]) || Boolean(removingStaffByCaregiverId[row.caregiverId])}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : staffRows.length ? (
              <div
                style={{
                  borderRadius: "16px",
                  border: "1px dashed #cbd5e1",
                  background: "#f8fafc",
                  padding: "1.25rem",
                  color: "#64748b",
                }}
              >
                {showArchivedStaff ? "No archived staff matched the current filters." : "No staff matched the current filters."}
              </div>
            ) : null}

          </>
        )}
      </div>

      <Dialog
        visible={showTimeAttendanceDialog}
        header="Time & Attendance"
        style={mobile ? { width: "96vw" } : { width: "92vw", maxWidth: "1100px" }}
        modal
        onHide={closeTimeAttendanceDialog}
      >
        {loadingAttendance ? (
          <div className="flex justify-content-center p-5">
            <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
          </div>
        ) : (
          <div style={{ border: "1px solid #e2e8f0", borderRadius: "16px", background: "#ffffff", overflow: "hidden" }}>
            <div style={{ padding: "1rem", borderBottom: "1px solid #e2e8f0", background: "#f8fafc" }}>
              <div className="flex gap-2 flex-wrap align-items-center justify-content-between">
                <div style={{ fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#64748b" }}>
                  Time & Attendance
                </div>
                <div className="flex gap-2 flex-wrap align-items-center">
                  <Dropdown
                    value={selectedTimePayCaregiverId}
                    options={timePayStaffOptions}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select caregiver"
                    className={mobile ? "w-full" : "w-14rem"}
                    appendTo="self"
                    onChange={(event) => setSelectedTimePayCaregiverId(String(event.value || "").trim())}
                  />
                  <Button
                    type="button"
                    label="Week"
                    severity={attendanceRange === "week" ? "warning" : "secondary"}
                    outlined={attendanceRange !== "week"}
                    onClick={() => setAttendanceRange("week")}
                  />
                  <Button
                    type="button"
                    label="Month"
                    severity={attendanceRange === "month" ? "warning" : "secondary"}
                    outlined={attendanceRange !== "month"}
                    onClick={() => setAttendanceRange("month")}
                  />
                  {attendanceRange === "month" ? (
                    <Dropdown
                      value={selectedAttendanceReportMonth}
                      options={attendanceReportMonthOptions}
                      optionLabel="label"
                      optionValue="value"
                      className={mobile ? "w-full" : "w-13rem"}
                      appendTo="self"
                      onChange={(event) => setSelectedAttendanceReportMonth(String(event.value || "").trim())}
                    />
                  ) : null}
                </div>
              </div>
            </div>
            <div style={{ padding: "1rem", borderBottom: "1px solid #e2e8f0", background: "#fffdf5" }}>
              <div className="grid" style={{ marginTop: 0 }}>
                <div className="col-12 lg:col-6">
                  <div style={{ border: "1px solid #fde68a", borderRadius: "16px", padding: "1rem", background: "#ffffff", height: "100%" }}>
                    <div style={{ fontSize: "0.76rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#92400e" }}>
                      Payroll Status
                    </div>
                    <div style={{ marginTop: "0.45rem", display: "flex", flexDirection: "column", gap: "0.4rem", color: "#475569", lineHeight: 1.5 }}>
                      <div style={{ fontWeight: 700, color: "#0f172a" }}>{payrollRangeFinalized ? `${payrollRangeLabel} Payroll Finalized` : `${payrollRangeLabel} Payroll Draft`}</div>
                      {payrollCloseout?.finalizedAt ? <div>Finalized {formatAttendanceDateTime(payrollCloseout?.finalizedAt)}</div> : null}
                      {payrollCloseout?.finalizedBy ? <div>By {String(payrollCloseout?.finalizedBy || "").trim()}</div> : null}
                      {String(payrollCloseout?.approvalNote || "").trim() ? <div><strong>Approval Note:</strong> {String(payrollCloseout?.approvalNote || "").trim()}</div> : null}
                      {minimumWageAmount > 0 ? (
                        <div style={{ color: "#92400e", fontWeight: 700 }}>
                          Current wage floor: {formatCurrency(minimumWageAmount)}/hr
                          {minimumWageEffectiveDate ? ` | Effective ${minimumWageEffectiveDate}` : ""}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
                <div className="col-12 lg:col-6">
                  <div style={{ border: "1px solid #fde68a", borderRadius: "16px", padding: "1rem", background: "#ffffff", height: "100%" }}>
                    <div className="flex align-items-start justify-content-between gap-3 flex-wrap">
                      <div>
                        <div style={{ fontSize: "0.76rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#92400e" }}>
                          Time & Pay Overview
                        </div>
                        <div style={{ marginTop: "0.35rem", color: "#475569", lineHeight: 1.5 }}>
                          Quick totals for the selected {payrollRangeLabel.toLowerCase()} payroll view.
                        </div>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          type="button"
                          label={`Download ${payrollRangeLabel} Packet`}
                          severity="warning"
                          outlined
                          rounded
                          disabled={!payrollRangeFinalized}
                          loading={exportingPayrollPacket}
                          onClick={() => { void onExportPayrollPacket(); }}
                        />
                        <Button
                          type="button"
                          label={payrollRangeFinalized ? `Reopen ${payrollRangeLabel} Payroll` : `Finalize ${payrollRangeLabel} Payroll`}
                          severity="warning"
                          rounded
                          loading={savingPayrollCloseout}
                          onClick={() => openPayrollCloseoutDialog(payrollRangeFinalized ? "reopen_payroll_period" : "finalize_payroll_period")}
                        />
                      </div>
                    </div>
                    <div className="grid" style={{ marginTop: "0.35rem" }}>
                      <div className="col-6 xl:col-3">
                        <div style={{ fontSize: "0.74rem", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: "#94a3b8" }}>Earned</div>
                        <div style={{ marginTop: "0.2rem", fontWeight: 800, color: "#0f172a" }}>{formatCurrency(providerAttendanceReport.estimatedTotalPay)}</div>
                      </div>
                      <div className="col-6 xl:col-3">
                        <div style={{ fontSize: "0.74rem", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: "#94a3b8" }}>Worked</div>
                        <div style={{ marginTop: "0.2rem", fontWeight: 800, color: "#0f172a" }}>{providerAttendanceReport.totalWorkedHours.toFixed(2)} hrs</div>
                      </div>
                      <div className="col-6 xl:col-3">
                        <div style={{ fontSize: "0.74rem", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: "#94a3b8" }}>OT Watch</div>
                        <div style={{ marginTop: "0.2rem", fontWeight: 800, color: "#0f172a" }}>{providerAttendanceReport.overtimeStaffCount} staff</div>
                        <div style={{ color: "#64748b" }}>{providerAttendanceReport.overtimeHours.toFixed(2)} OT hrs</div>
                      </div>
                      <div className="col-6 xl:col-3">
                        <div style={{ fontSize: "0.74rem", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: "#94a3b8" }}>Open Shifts</div>
                        <div style={{ marginTop: "0.2rem", fontWeight: 800, color: "#0f172a" }}>{providerAttendanceReport.openShiftCount}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {selectedTimePaySummary ? (
                <div style={{ marginTop: "1rem", border: "1px solid #fde68a", borderRadius: "16px", background: "#ffffff", padding: "1rem" }}>
                  <div className="flex align-items-start justify-content-between gap-3 flex-wrap">
                    <div>
                      <div style={{ fontSize: "0.76rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#92400e" }}>
                        Selected Staff Time & Pay
                      </div>
                      <div style={{ marginTop: "0.35rem", fontWeight: 800, color: "#0f172a", fontSize: "1.1rem" }}>
                        {selectedTimePaySummary?.caregiverName || selectedTimePayStaffRow?.name || "Staff"}
                      </div>
                      <div style={{ marginTop: "0.2rem", color: "#475569", lineHeight: 1.5 }}>
                        {selectedTimePaySummary?.shiftSummary || selectedTimePayStaffRow?.shiftSummary || "No shift set"}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        type="button"
                        label="Download Summary"
                        severity="warning"
                        outlined
                        rounded
                        loading={Boolean(exportingMonthlySummaryByCaregiverId[String(selectedTimePaySummary?.caregiverId || "").trim()])}
                        onClick={() => { void onExportMonthlyStaffSummary(selectedTimePaySummary); }}
                      />
                      <Button
                        type="button"
                        label="Correct Shift"
                        severity="warning"
                        rounded
                        disabled={payrollRangeFinalized}
                        onClick={() => openAttendanceCorrectionDialog(selectedTimePaySummary)}
                      />
                    </div>
                  </div>
                  <div className="grid" style={{ marginTop: "0.35rem" }}>
                    <div className="col-6 md:col-3 xl:col-2">
                      <div style={{ fontSize: "0.74rem", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: "#94a3b8" }}>Earned So Far</div>
                      <div style={{ marginTop: "0.2rem", fontWeight: 800, color: "#0f172a" }}>{formatCurrency(selectedTimePaySummary?.estimatedTotalPay)}</div>
                    </div>
                    <div className="col-6 md:col-3 xl:col-2">
                      <div style={{ fontSize: "0.74rem", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: "#94a3b8" }}>Worked Hours</div>
                      <div style={{ marginTop: "0.2rem", fontWeight: 800, color: "#0f172a" }}>{Number(selectedTimePaySummary?.totalWorkedHours || 0).toFixed(2)} hrs</div>
                    </div>
                    <div className="col-6 md:col-3 xl:col-2">
                      <div style={{ fontSize: "0.74rem", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: "#94a3b8" }}>OT Hours</div>
                      <div style={{ marginTop: "0.2rem", fontWeight: 800, color: "#0f172a" }}>{Number(selectedTimePaySummary?.overtimeHours || 0).toFixed(2)} hrs</div>
                    </div>
                    <div className="col-6 md:col-3 xl:col-2">
                      <div style={{ fontSize: "0.74rem", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: "#94a3b8" }}>Rate</div>
                      <div style={{ marginTop: "0.2rem", fontWeight: 800, color: "#0f172a" }}>{formatCurrency(selectedTimePaySummary?.hourlyRate)}/hr</div>
                    </div>
                    <div className="col-6 md:col-3 xl:col-2">
                      <div style={{ fontSize: "0.74rem", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: "#94a3b8" }}>Sick</div>
                      <div style={{ marginTop: "0.2rem", fontWeight: 800, color: "#0f172a" }}>{Number(selectedTimePaySummary?.sickLeaveAccruedHours || 0).toFixed(2)} hrs</div>
                    </div>
                    <div className="col-6 md:col-3 xl:col-2">
                      <div style={{ fontSize: "0.74rem", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: "#94a3b8" }}>Vacation</div>
                      <div style={{ marginTop: "0.2rem", fontWeight: 800, color: "#0f172a" }}>{Number(selectedTimePaySummary?.vacationBalanceHours || 0).toFixed(2)} hrs</div>
                    </div>
                  </div>
                  <div className="grid" style={{ marginTop: "0.25rem" }}>
                    <div className="col-12 lg:col-6">
                      <div style={{ fontSize: "0.76rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#92400e", marginBottom: "0.65rem" }}>
                        Pay Breakdown
                      </div>
                      <DataTable value={[selectedTimePaySummary]} emptyMessage="No pay details available yet.">
                        <Column field="shiftCount" header="Shifts" />
                        <Column field="totalWorkedHours" header="Worked Hours" body={(rowData: any) => `${Number(rowData?.totalWorkedHours || 0).toFixed(2)} hrs`} />
                        <Column field="estimatedRegularPay" header="Regular Pay" body={(rowData: any) => formatCurrency(rowData?.estimatedRegularPay)} />
                        <Column field="estimatedOvertimePay" header="OT Pay" body={(rowData: any) => formatCurrency(rowData?.estimatedOvertimePay)} />
                        <Column field="estimatedTotalPay" header="Earned So Far" body={(rowData: any) => formatCurrency(rowData?.estimatedTotalPay)} />
                      </DataTable>
                    </div>
                    <div className="col-12 lg:col-6">
                      <div style={{ fontSize: "0.76rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#92400e", marginBottom: "0.65rem" }}>
                        What Needs Attention
                      </div>
                      {selectedTimePayReviewRows.length ? (
                        <DataTable value={selectedTimePayReviewRows} rows={4} paginator={selectedTimePayReviewRows.length > 4} emptyMessage="No review items right now.">
                          <Column field="issueSummary" header="Issue" />
                          <Column field="issueDetail" header="Detail" />
                          <Column field="reviewStatus" header="Status" body={(rowData: any) => <Tag value={getAttendanceReviewStatusLabel(rowData?.reviewStatus)} severity={getAttendanceReviewStatusSeverity(rowData?.reviewStatus)} />} />
                        </DataTable>
                      ) : selectedTimePayCorrectionRows.length ? (
                        <DataTable value={selectedTimePayCorrectionRows.slice(0, 4)} emptyMessage="No corrections right now.">
                          <Column field="workDate" header="Work Date" />
                          <Column field="latestCorrectionReasonLabel" header="Correction" body={(rowData: any) => rowData?.latestCorrectionReasonLabel || "Correction"} />
                          <Column field="notificationStatus" header="Email" body={(rowData: any) => <Tag value={String(rowData?.notificationStatus || "").trim().toLowerCase() === "failed" ? "Failed" : "Sent"} severity={String(rowData?.notificationStatus || "").trim().toLowerCase() === "failed" ? "danger" : "success"} />} />
                        </DataTable>
                      ) : (
                        <div style={{ color: "#64748b", lineHeight: 1.6 }}>No open review items or recent corrections for this staff member in the selected range.</div>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}
              <div style={{ marginTop: "1rem", border: "1px solid #fde68a", borderRadius: "16px", background: "#ffffff", padding: "1rem" }}>
                <div className="flex align-items-start justify-content-between gap-3 flex-wrap" style={{ marginBottom: "0.75rem" }}>
                  <div>
                    <div style={{ fontSize: "0.76rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#92400e" }}>
                      All Staff Summary
                    </div>
                    <div style={{ marginTop: "0.35rem", color: "#475569", lineHeight: 1.5 }}>
                      Quick payroll totals for each caregiver in the selected {attendanceRange === "month" ? "month" : "week"}.
                    </div>
                  </div>
                  <Button
                    type="button"
                    label="Download Quick Report"
                    severity="warning"
                    outlined
                    rounded
                    loading={exportingPayrollReport}
                    onClick={onExportPayrollReport}
                  />
                </div>
                <DataTable value={providerAttendanceReport.payrollSummaryRows} rows={6} paginator={providerAttendanceReport.payrollSummaryRows.length > 6} emptyMessage="No payroll summary available yet.">
                  <Column field="caregiverName" header="Staff" />
                  <Column field="shiftCount" header="Shifts" />
                  <Column field="regularHours" header="Regular Hours" body={(rowData: any) => `${Number(rowData?.regularHours || 0).toFixed(2)} hrs`} />
                  <Column field="overtimeHours" header="OT Hours" body={(rowData: any) => `${Number(rowData?.overtimeHours || 0).toFixed(2)} hrs`} />
                  <Column field="estimatedRegularPay" header="Regular Pay" body={(rowData: any) => formatCurrency(rowData?.estimatedRegularPay)} />
                  <Column field="estimatedOvertimePay" header="OT Pay" body={(rowData: any) => formatCurrency(rowData?.estimatedOvertimePay)} />
                  <Column field="estimatedTotalPay" header="Earned So Far" body={(rowData: any) => formatCurrency(rowData?.estimatedTotalPay)} />
                </DataTable>
              </div>
              {providerAttendanceReport.overtimeRows.length ? (
                <div style={{ marginTop: "1rem" }}>
                  <div style={{ fontSize: "0.76rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#92400e", marginBottom: "0.75rem" }}>
                    Overtime
                  </div>
                  <DataTable value={providerAttendanceReport.overtimeRows} rows={5} paginator={providerAttendanceReport.overtimeRows.length > 5} emptyMessage="No overtime entries in this range.">
                    <Column field="caregiverName" header="Staff" />
                    <Column field="totalWorkedHours" header="Total Hours" body={(rowData: any) => `${Number(rowData?.totalWorkedHours || 0).toFixed(2)} hrs`} />
                    <Column field="overtimeHours" header="OT Hours" body={(rowData: any) => `${Number(rowData?.overtimeHours || 0).toFixed(2)} hrs`} />
                    <Column field="estimatedOvertimePay" header="Estimated OT Pay" body={(rowData: any) => formatCurrency(rowData?.estimatedOvertimePay)} />
                    <Column field="latestActivity" header="Latest Activity" />
                  </DataTable>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default HomeStaffManagementPage;
